import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { useSnapchatAds, validateSnapchatForm } from '../hooks/useSnapchatAds';
import { SnapchatAccountSection, SnapchatGhostIcon } from '../components/adglobe/SnapchatAccountSection';
import { SnapchatCampaignsPanel } from '../components/adglobe/SnapchatCampaignsPanel';
import { SnapchatSuccessModal } from '../components/adglobe/SnapchatSuccessModal';
import { SnapchatTargetingPanel } from '../components/adglobe/SnapchatTargetingPanel';
import { SnapchatDeliveryPanel } from '../components/adglobe/SnapchatDeliveryPanel';
import { SnapchatAudienceEstimate } from '../components/adglobe/SnapchatAudienceEstimate';
import { SnapchatObjectiveSetup } from '../components/adglobe/SnapchatObjectiveSetup';
import { SnapchatAdPreview } from '../components/adglobe/SnapchatAdPreview';
import { MetaAdPreview } from '../components/adglobe/MetaAdPreview';
import { PostEngagementDetailModal } from '../components/adglobe/PostEngagementDetailModal';
import { CampaignCreatedModal } from '../components/adglobe/CampaignCreatedModal';
import { SNAPCHAT_OBJECTIVES, SNAPCHAT_OPTIMIZATION_GOALS, SNAPCHAT_CTA_TYPES, SNAPCHAT_APP_CTA_TYPES, SNAPCHAT_LIMITS } from '../components/adglobe/snapchatConfig';
import { 
  ArrowLeft, 
  Globe, 
  Target, 
  DollarSign, 
  Image as ImageIcon, 
  Link as LinkIcon,
  MapPin,
  Upload,
  CheckCircle,
  Loader2,
  X,
  Send,
  Info,
  PanelRightClose,
  PanelRight,
  Users,
  Share2,
  Facebook,
  Building2,
  BarChart3,
  Play,
  Pause,
  TrendingUp,
  MousePointerClick,
  Eye,
  Settings,
  ChevronDown,
  ChevronUp,
  Unlink,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Plug,
  Sparkles,
  Heart,
  MessageCircle,
  Edit,
  Save,
  Timer,
  Clock,
  FileText,
  Video,
  Download,
  Activity,
  Calendar,
  Plus,
  AlertCircle,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ad Platforms Configuration
const AD_PLATFORMS = [
  { 
    id: 'facebook', 
    name: 'Facebook', 
    icon: 'facebook',
    color: '#1877F2',
    bgColor: 'from-blue-600/20 to-blue-700/20',
    borderColor: 'border-blue-500/30',
    description: 'Facebook Ads',
    connected: false
  },
  { 
    id: 'instagram', 
    name: 'Instagram', 
    icon: 'instagram',
    color: '#E4405F',
    bgColor: 'from-pink-500/20 via-purple-500/20 to-orange-500/20',
    borderColor: 'border-pink-500/30',
    description: 'Instagram Ads',
    connected: false
  },
  { 
    id: 'tiktok', 
    name: 'TikTok', 
    icon: '/platforms/tiktok.svg',
    color: '#000000',
    bgColor: 'from-gray-800/30 to-gray-900/30',
    borderColor: 'border-gray-500/30',
    description: 'TikTok Ads Manager',
    connected: false
  },
  { 
    id: 'google', 
    name: 'Google', 
    icon: '/platforms/google.svg',
    color: '#4285F4',
    bgColor: 'from-red-500/10 via-yellow-500/10 to-green-500/10',
    borderColor: 'border-yellow-500/30',
    description: 'Google Ads & YouTube',
    connected: false
  },
  { 
    id: 'snapchat', 
    name: 'Snapchat', 
    icon: '/platforms/snapchat.svg',
    color: '#FFFC00',
    bgColor: 'from-yellow-400/20 to-yellow-500/20',
    borderColor: 'border-yellow-400/30',
    description: 'Snapchat Ads',
    connected: false
  },
  { 
    id: 'youtube', 
    name: 'YouTube', 
    icon: '/platforms/youtube.svg',
    color: '#FF0000',
    bgColor: 'from-red-600/20 to-red-700/20',
    borderColor: 'border-red-500/30',
    description: 'YouTube Ads',
    connected: false
  },
  { 
    id: 'twitter', 
    name: 'X (Twitter)', 
    icon: 'twitter-x',
    color: '#000000',
    bgColor: 'from-gray-900/30 to-black/30',
    borderColor: 'border-gray-600/30',
    description: 'X Ads Manager',
    connected: false
  },
];

// Platform-specific form configurations based on each platform's API requirements
const PLATFORM_FORM_CONFIG = {
  facebook: {
    name: 'Facebook',
    accountLabel: 'Facebook Page',
    accountPlaceholder: 'Select the Page to run ads from',
    objectives: [
      { value: 'AWARENESS', label: 'Awareness', description: 'Reach people likely to remember your ads' },
      { value: 'TRAFFIC', label: 'Traffic', description: 'Drive traffic to your website or app' },
      { value: 'ENGAGEMENT', label: 'Engagement', description: 'Get more likes, comments, and shares' },
      { value: 'LEADS', label: 'Leads', description: 'Collect leads for your business' },
      { value: 'SALES', label: 'Sales', description: 'Find people likely to purchase' },
      { value: 'APP_PROMOTION', label: 'App Promotion', description: 'Get more app installs' },
    ],
    buyingTypes: [
      { value: 'AUCTION', label: 'Interest', description: 'Compete for ad placements in real-time' },
      { value: 'RESERVED', label: 'Domination', description: 'Guaranteed delivery at fixed price' },
    ],
    budgetTypes: ['daily', 'lifetime'],
    placements: ['Facebook Feed', 'Facebook Stories', 'Facebook Reels', 'Facebook Marketplace', 'Facebook Right Column'],
    creativeFormats: ['Single Image', 'Video', 'Carousel', 'Collection'],
    specialFields: ['special_ad_categories'],
    sharedFields: ['campaignName', 'objective', 'budget', 'startDate', 'endDate', 'targeting'],
  },
  instagram: {
    name: 'Instagram',
    accountLabel: 'Instagram Account',
    accountPlaceholder: 'Select the Instagram account to run ads from',
    objectives: [
      { value: 'AWARENESS', label: 'Awareness', description: 'Reach people likely to remember your ads' },
      { value: 'TRAFFIC', label: 'Traffic', description: 'Drive traffic to your website or profile' },
      { value: 'ENGAGEMENT', label: 'Engagement', description: 'Get more likes, comments, and shares' },
      { value: 'LEADS', label: 'Leads', description: 'Collect leads for your business' },
      { value: 'SALES', label: 'Sales', description: 'Find people likely to purchase' },
    ],
    buyingTypes: [
      { value: 'AUCTION', label: 'Interest', description: 'Compete for ad placements in real-time' },
      { value: 'RESERVED', label: 'Domination', description: 'Guaranteed delivery at fixed price' },
    ],
    budgetTypes: ['daily', 'lifetime'],
    placements: ['Instagram Feed', 'Instagram Stories', 'Instagram Reels', 'Instagram Explore'],
    creativeFormats: ['Single Image', 'Video', 'Carousel', 'Stories'],
    sharedFields: ['campaignName', 'objective', 'budget', 'startDate', 'endDate', 'targeting'],
  },
  tiktok: {
    name: 'TikTok',
    accountLabel: 'TikTok Advertiser Account',
    accountPlaceholder: 'Link your TikTok for Business account',
    objectives: [
      { value: 'REACH', label: 'Reach', description: 'Maximize the number of people who see your ad' },
      { value: 'TRAFFIC', label: 'Traffic', description: 'Drive traffic to your website or app' },
      { value: 'VIDEO_VIEWS', label: 'Video Views', description: 'Get more views on your video ads' },
      { value: 'LEAD_GENERATION', label: 'Lead Generation', description: 'Collect leads with instant forms' },
      { value: 'COMMUNITY_INTERACTION', label: 'Community Interaction', description: 'Grow followers and engagement' },
      { value: 'APP_PROMOTION', label: 'App Promotion', description: 'Get more app installs' },
      { value: 'WEBSITE_CONVERSIONS', label: 'Website Conversions', description: 'Drive conversions on your website' },
      { value: 'PRODUCT_SALES', label: 'Product Sales', description: 'Sell products from your catalog' },
    ],
    buyingTypes: [
      { value: 'AUCTION', label: 'Interest', description: 'Bid for ad placements' },
      { value: 'REACH_FREQUENCY', label: 'Reach & Frequency', description: 'Predictable reach at fixed price' },
    ],
    budgetTypes: ['daily', 'lifetime'],
    placements: ['TikTok Feed', 'TikTok TopView', 'Pangle', 'News Feed App Series'],
    creativeFormats: ['In-Feed Video', 'TopView', 'Spark Ads', 'Playable Ads'],
    bidTypes: ['NO_BID', 'BID_TYPE_CUSTOM', 'BID_TYPE_MAX_CONVERSION'],
    sharedFields: ['campaignName', 'objective', 'budget', 'startDate', 'endDate', 'targeting'],
  },
  google: {
    name: 'Google Ads',
    accountLabel: 'Google Ads Account',
    accountPlaceholder: 'Link your Google Ads account',
    campaignTypes: [
      { value: 'SEARCH', label: 'Search', description: 'Text ads on Google Search results' },
      { value: 'PERFORMANCE_MAX', label: 'Performance Max', description: 'AI-powered campaigns across all Google channels' },
      { value: 'DISPLAY', label: 'Display', description: 'Visual ads across websites and apps' },
      { value: 'SHOPPING', label: 'Shopping', description: 'Product listings on Google Shopping' },
      { value: 'DEMAND_GEN', label: 'Demand Gen', description: 'Discover, YouTube, and Gmail placements' },
    ],
    budgetTypes: ['daily'],
    biddingStrategies: [
      { value: 'MAXIMIZE_CONVERSIONS', label: 'Maximize Conversions' },
      { value: 'MAXIMIZE_CONVERSION_VALUE', label: 'Maximize Conversion Value' },
      { value: 'TARGET_CPA', label: 'Target CPA' },
      { value: 'TARGET_ROAS', label: 'Target ROAS' },
      { value: 'MAXIMIZE_CLICKS', label: 'Maximize Clicks' },
    ],
    specialFields: ['keywords', 'ad_groups', 'contains_eu_political_advertising'],
    sharedFields: ['campaignName', 'budget', 'startDate', 'endDate', 'targeting'],
  },
  snapchat: {
    name: 'Snapchat',
    accountLabel: 'Snapchat Ad Account',
    accountPlaceholder: 'Link your Snapchat Business account',
    objectives: SNAPCHAT_OBJECTIVES,
    budgetTypes: ['daily', 'lifetime'],
    placements: ['Snap Ads', 'Story Ads', 'Collection Ads', 'Spotlight', 'AR Lenses'],
    creativeFormats: ['Single Image/Video', 'Story Ad', 'Collection', 'AR Experience'],
    sharedFields: ['campaignName', 'objective', 'budget', 'startDate', 'endDate', 'targeting'],
  },
  youtube: {
    name: 'YouTube',
    accountLabel: 'YouTube Channel',
    accountPlaceholder: 'Link your YouTube channel via Google Ads',
    campaignTypes: [
      { value: 'VIDEO_REACH', label: 'Video Reach', description: 'Maximize views and reach' },
      { value: 'VIDEO_VIEWS', label: 'Video Views', description: 'Get more views on your videos' },
      { value: 'DEMAND_GEN', label: 'Demand Gen', description: 'YouTube, Discover, and Gmail' },
      { value: 'PERFORMANCE_MAX', label: 'Performance Max', description: 'AI-optimized across Google' },
    ],
    budgetTypes: ['daily', 'campaign_total'],
    videoFormats: [
      { value: 'SKIPPABLE_IN_STREAM', label: 'Skippable In-Stream', description: 'Plays before/during videos, skippable after 5s' },
      { value: 'NON_SKIPPABLE_IN_STREAM', label: 'Non-Skippable In-Stream', description: '15-second ads that cannot be skipped' },
      { value: 'BUMPER', label: 'Bumper Ads', description: '6-second non-skippable ads' },
      { value: 'IN_FEED', label: 'In-Feed Video', description: 'Appears in YouTube search and related' },
      { value: 'SHORTS', label: 'YouTube Shorts', description: 'Vertical ads in Shorts feed' },
    ],
    specialFields: ['video_url', 'companion_banner'],
    sharedFields: ['campaignName', 'budget', 'startDate', 'endDate', 'targeting'],
  },
  twitter: {
    name: 'X (Twitter)',
    accountLabel: 'X Ads Account',
    accountPlaceholder: 'Link your X for Business account',
    objectives: [
      { value: 'REACH', label: 'Reach', description: 'Maximize ad impressions' },
      { value: 'ENGAGEMENT', label: 'Engagement', description: 'Get likes, reposts, and replies' },
      { value: 'FOLLOWERS', label: 'Followers', description: 'Grow your follower count' },
      { value: 'WEBSITE_TRAFFIC', label: 'Website Traffic', description: 'Drive clicks to your website' },
      { value: 'APP_INSTALLS', label: 'App Installs', description: 'Promote app downloads' },
      { value: 'APP_RE_ENGAGEMENTS', label: 'App Re-engagements', description: 'Bring users back to your app' },
      { value: 'VIDEO_VIEWS', label: 'Video Views', description: 'Get more views on your videos' },
    ],
    buyingTypes: [
      { value: 'AUCTION', label: 'Interest', description: 'Bid for ad placements' },
    ],
    budgetTypes: ['daily', 'total'],
    placements: ['Timeline', 'Profile', 'Search Results', 'Replies'],
    creativeFormats: ['Promoted Post', 'Image Ad', 'Video Ad', 'Carousel', 'Moment'],
    specialFields: ['funding_instrument_id'],
    sharedFields: ['campaignName', 'objective', 'budget', 'startDate', 'endDate', 'targeting'],
  },
};

// Shared fields that sync across platforms when filled
const SHARED_FIELD_MAPPING = {
  campaignName: 'campaignName',
  objective: 'objective', // Will map to platform-specific objectives
  budget: 'budgetAmount',
  budgetType: 'budgetType',
  startDate: 'startDate',
  endDate: 'endDate',
  targeting: {
    ageMin: 'minAge',
    ageMax: 'maxAge',
    gender: 'gender',
    languages: 'languages',
    locations: 'locationName',
  },
};

// Meta Campaign Objectives
const CAMPAIGN_OBJECTIVES = [
  { value: 'OUTCOME_AWARENESS', label: 'Awareness', description: 'Reach people likely to remember your ads' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic', description: 'Send people to a destination like your website' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement', description: 'Get more messages, video views, or post engagement' },
  { value: 'OUTCOME_LEADS', label: 'Leads', description: 'Collect leads for your business' },
  { value: 'OUTCOME_APP_PROMOTION', label: 'App Promotion', description: 'Get people to install or take action in your app' },
  { value: 'OUTCOME_SALES', label: 'Sales', description: 'Find people likely to purchase your product or service' },
];

// Special Ad Categories
const SPECIAL_AD_CATEGORIES = [
  { value: 'NONE', label: 'None', requiresVerification: false },
  { value: 'HOUSING', label: 'Housing', description: 'Ads for property listings, home insurance, mortgages or other related opportunities', requiresVerification: true },
  { value: 'EMPLOYMENT', label: 'Employment', description: 'Ads for job opportunities, internships, professional certification or other related opportunities', requiresVerification: true },
  { value: 'FINANCIAL_PRODUCTS_SERVICES', label: 'Financial Products & Services', description: 'Ads for credit cards, long-term financing, current and savings accounts, investment services, insurance services or other related financial opportunities', requiresVerification: true },
  { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Social Issues/Politics', description: 'Ads about social issues, elections, politics or related topics', requiresVerification: true, requiresDisclaimer: true },
];

// Call-to-Action Types
const CTA_TYPES = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'BOOK_NOW', label: 'Book Now' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'SUBSCRIBE', label: 'Subscribe' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'APPLY_NOW', label: 'Apply Now' },
  { value: 'GET_OFFER', label: 'Get Offer' },
  { value: 'ORDER_NOW', label: 'Order Now' },
  { value: 'WATCH_MORE', label: 'Watch More' },
];

// Optimization Goals based on Objective
const OPTIMIZATION_GOALS = {
  OUTCOME_AWARENESS: [
    { value: 'REACH', label: 'Reach' },
    { value: 'IMPRESSIONS', label: 'Impressions' },
    { value: 'AD_RECALL_LIFT', label: 'Ad Recall Lift' },
  ],
  OUTCOME_TRAFFIC: [
    { value: 'LINK_CLICKS', label: 'Link Clicks' },
    { value: 'LANDING_PAGE_VIEWS', label: 'Landing Page Views' },
  ],
  OUTCOME_ENGAGEMENT: [
    { value: 'POST_ENGAGEMENT', label: 'Post Engagement' },
    { value: 'PAGE_LIKES', label: 'Page Likes' },
    { value: 'EVENT_RESPONSES', label: 'Event Responses' },
  ],
  OUTCOME_LEADS: [
    { value: 'LEAD_GENERATION', label: 'Lead Generation' },
    { value: 'CONVERSATIONS', label: 'Conversations' },
  ],
  OUTCOME_APP_PROMOTION: [
    { value: 'APP_INSTALLS', label: 'App Installs' },
    { value: 'APP_EVENTS', label: 'App Events' },
  ],
  OUTCOME_SALES: [
    { value: 'OFFSITE_CONVERSIONS', label: 'Conversions' },
    { value: 'VALUE', label: 'Value' },
  ],
};

// Mock Facebook Pages (for pages_show_list permission demo)
const MOCK_FACEBOOK_PAGES = [
  { id: 'page_001', name: 'Loading...', category: 'Page', followers: 0, picture: 'https://ui-avatars.com/api/?name=Loading&background=1877f2&color=fff' },
];

// Mock Ad Accounts (fallback only)
const MOCK_AD_ACCOUNTS = [
  { id: 'act_loading', name: 'Loading...', currency: 'NGN', status: 'LOADING', spend_cap: 0 },
];

// Empty campaigns (will be populated with real data)
const EMPTY_CAMPAIGNS = [];

// Age options (13-65+)
const AGE_OPTIONS = [
  { value: '13', label: '13' },
  { value: '18', label: '18' },
  { value: '21', label: '21' },
  { value: '25', label: '25' },
  { value: '30', label: '30' },
  { value: '35', label: '35' },
  { value: '40', label: '40' },
  { value: '45', label: '45' },
  { value: '50', label: '50' },
  { value: '55', label: '55' },
  { value: '60', label: '60' },
  { value: '65', label: '65+' },
];

// Gender options
const GENDER_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
];

// Language options (Nigerian focus + common)
const LANGUAGE_OPTIONS = [
  { value: 'all', label: 'All Languages' },
  { value: 'en', label: 'English' },
  { value: 'ha', label: 'Hausa' },
  { value: 'yo', label: 'Yoruba' },
  { value: 'ig', label: 'Igbo' },
  { value: 'pcm', label: 'Nigerian Pidgin' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
];

export default function MetaAdsGlobePage({ metaReviewMode = false }) {
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const sidebarRef = useRef(null);
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Platform state
  const [activePlatform, setActivePlatform] = useState('facebook');
  const [platformSettings, setPlatformSettings] = useState({
    facebook: { enabled: true, connected: false, accountName: '' },
    instagram: { enabled: true, connected: false, accountName: '' },
    tiktok: { enabled: false, connected: false, accountName: '' },
    google: { enabled: false, connected: false, accountName: '' },
    snapchat: { enabled: true, connected: false, accountName: '' },
    youtube: { enabled: false, connected: false, accountName: '' },
    twitter: { enabled: false, connected: false, accountName: '' },
  });
  // Hide platform bar in review mode
  const [showPlatformBar, setShowPlatformBar] = useState(!metaReviewMode);
  
  // Feature flags - disabled for Meta App Review mode
  const SHOW_POST_UP_FEATURE = !metaReviewMode;
  
  // Influencers for Post Up feature
  const [influencers, setInfluencers] = useState([]);
  const [loadingInfluencers, setLoadingInfluencers] = useState(true);
  
  // Form State
  const [formData, setFormData] = useState({
    campaignName: '',
    objective: '',
    // Facebook Page & Ad Account selection (for Meta permissions)
    selectedPageId: '',
    selectedAdAccountId: '',
    // Order type
    orderType: 'AUCTION',
    // Post Up feature
    postUpInfluencers: [],
    postUpContentUrl: '',
    specialAdCategory: 'NONE',
    adSetName: '',
    budgetType: 'daily',
    budgetAmount: '',
    startDate: '',
    endDate: '',
    optimizationGoal: '',
    bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
    latitude: null,
    longitude: null,
    radius: 1,
    locationName: '',
    locationType: 'region',
    dailyReach: 0,
    // Target Audience
    minAge: '18',
    maxAge: '65',
    gender: 'ALL',
    languages: ['all'],
    // Creative
    primaryText: '',
    headline: '',
    description: '',
    destinationUrl: '',
    ctaType: 'LEARN_MORE',
    imageFile: null,
    imagePreview: null,
    videoFile: null,
    videoPreview: null,
    mediaType: 'image', // 'image' or 'video'
    // Reservation Estimates (for Reserved buying type)
    reservationTargetReach: 200000,
    reservationFrequencyTimes: 2,
    reservationFrequencyDays: 7,
  });
  
  // Reservation Estimates State
  const [reservationEstimates, setReservationEstimates] = useState(null);
  const [loadingReservation, setLoadingReservation] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(''); // Progress message during submission
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [highlightCampaignId, setHighlightCampaignId] = useState(null); // newly created campaign to highlight in list
  const [submittedData, setSubmittedData] = useState(null);
  const [activeSection, setActiveSection] = useState('campaigns');
  
  // Meta Login State
  const [showMetaLoginModal, setShowMetaLoginModal] = useState(false);  // Reverted - not showing immediately
  const [isMetaConnected, setIsMetaConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState(null);
  
  // Real Meta Data State
  const [facebookPages, setFacebookPages] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isLoadingAdAccounts, setIsLoadingAdAccounts] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [metaApiError, setMetaApiError] = useState(null);
  
  // Date range selector for campaign insights
  const [selectedDateRange, setSelectedDateRange] = useState('lifetime');
  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7d', label: 'Last 7 Days' },
    { value: 'last_14d', label: 'Last 14 Days' },
    { value: 'last_30d', label: 'Last 30 Days' },
    { value: 'last_90d', label: 'Last 90 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'lifetime', label: 'Lifetime (All Time)' },
  ];
  
  // Simulated campaigns for Meta Review Mode (local state only)
  const [simulatedCampaigns, setSimulatedCampaigns] = useState([]);
  
  // Real Page Posts from Meta API (pages_read_engagement permission)
  const [pagePosts, setPagePosts] = useState([]);
  const [isLoadingPagePosts, setIsLoadingPagePosts] = useState(false);
  const [pagePostsError, setPagePostsError] = useState(null);
  
  // Creative source: 'new' (create new creative) or 'existing' (use existing post)
  const [creativeSource, setCreativeSource] = useState('existing'); // Default to existing to avoid dev mode issues
  const [selectedExistingPost, setSelectedExistingPost] = useState(null);
  const [showPostSelector, setShowPostSelector] = useState(false);
  const [engagementModalPost, setEngagementModalPost] = useState(null); // For showing engagement metrics modal
  const [engagementDetailKind, setEngagementDetailKind] = useState(null); // 'comments' | 'reactions' | 'shares'
  
  // Business Portfolio state for Meta Review Mode (business_management permission demo)
  const [showBusinessSelector, setShowBusinessSelector] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false);
  const [isLoadingBusinessAdAccounts, setIsLoadingBusinessAdAccounts] = useState(false);
  const [businessAdAccounts, setBusinessAdAccounts] = useState({});
  
  // Simulated businesses fallback (for demo/bypass mode)
  const simulatedBusinesses = [
    {
      id: 'biz_1001',
      name: 'Adlinka Business',
      business_id: '1234567890123456',
      verification_status: 'verified',
      created_time: '2023-06-15',
      ad_account_count: 2,
      ad_accounts: [
        {
          id: '342828819640091',
          name: 'Adlinka Ads Account',
          currency: 'NGN',
          status: 'ACTIVE',
          timezone: 'Africa/Lagos',
          amount_spent: 1250000,
          balance: 500000,
        },
        {
          id: '567891234567890',
          name: 'Adlinka Secondary Account',
          currency: 'NGN',
          status: 'ACTIVE',
          timezone: 'Africa/Lagos',
          amount_spent: 450000,
          balance: 200000,
        }
      ]
    },
    {
      id: 'biz_1002',
      name: 'Kano Digital Marketing',
      business_id: '9876543210987654',
      verification_status: 'verified',
      created_time: '2024-01-10',
      ad_account_count: 1,
      ad_accounts: [
        {
          id: '111222333444555',
          name: 'Kano Ads Main',
          currency: 'NGN',
          status: 'ACTIVE',
          timezone: 'Africa/Lagos',
          amount_spent: 780000,
          balance: 350000,
        }
      ]
    }
  ];
  
  // Fetch real businesses from Meta API
  const fetchBusinesses = async (accessToken) => {
    setIsLoadingBusinesses(true);
    try {
      console.log('Fetching businesses with token:', accessToken?.substring(0, 20) + '...');
      const response = await fetch(`${API_URL}/api/meta/businesses?access_token=${accessToken}`);
      const data = await response.json();
      
      console.log('Businesses API response:', response.status, data);
      
      if (response.ok && data.businesses && data.businesses.length > 0) {
        console.log('Found real businesses:', data.businesses.length);
        setBusinesses(data.businesses);
        toast.success(`Found ${data.businesses.length} Business Portfolio(s)`);
        return data.businesses;
      } else if (response.ok && data.businesses && data.businesses.length === 0) {
        // User has no businesses - show message and use simulated for demo
        console.log('No businesses found for user, using simulated data');
        toast.info('No Business Portfolios found in your Meta account. Showing demo data.');
        setBusinesses(simulatedBusinesses);
        return simulatedBusinesses;
      } else {
        // API returned an error
        const errorMsg = data.detail || 'Unknown error';
        console.log('API error:', errorMsg);
        toast.error(`Meta API: ${errorMsg}`, {
          description: 'Using demo data instead. Make sure business_management permission is granted.'
        });
        setBusinesses(simulatedBusinesses);
        return simulatedBusinesses;
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to fetch businesses', {
        description: 'Using demo data. Check console for details.'
      });
      // Fall back to simulated data on error
      setBusinesses(simulatedBusinesses);
      return simulatedBusinesses;
    } finally {
      setIsLoadingBusinesses(false);
    }
  };
  
  // Fetch ad accounts for a specific business
  const fetchBusinessAdAccounts = async (businessId, accessToken) => {
    setIsLoadingBusinessAdAccounts(true);
    try {
      const response = await fetch(`${API_URL}/api/meta/businesses/${businessId}/ad_accounts?access_token=${accessToken}`);
      const data = await response.json();
      
      if (response.ok && data.ad_accounts) {
        setBusinessAdAccounts(prev => ({
          ...prev,
          [businessId]: data.ad_accounts
        }));
        return data.ad_accounts;
      }
      return [];
    } catch (error) {
      console.error('Error fetching ad accounts:', error);
      return [];
    } finally {
      setIsLoadingBusinessAdAccounts(false);
    }
  };
  
  // Fetch real page posts with engagement metrics (pages_read_engagement permission)
  const fetchPagePosts = async (pageId) => {
    setIsLoadingPagePosts(true);
    setPagePostsError(null);
    
    // Get the page's access token (preferred) or fall back to user access token
    const page = facebookPages.find(p => p.id === pageId);
    const accessToken = page?.access_token || connectedAccount?.accessToken;
    
    if (!accessToken) {
      setPagePostsError('No access token available. Please reconnect to Meta.');
      setIsLoadingPagePosts(false);
      return [];
    }
    
    // If we're using user access token instead of page token, log a warning
    if (!page?.access_token && connectedAccount?.accessToken) {
      console.warn('Using user access token instead of page access token - some features may be limited');
    }
    
    try {
      const response = await fetch(`${API_URL}/api/meta/pages/${pageId}/posts?access_token=${accessToken}&limit=10`);
      const data = await response.json();
      
      if (response.ok && data.posts) {
        setPagePosts(data.posts);
        if (data.posts.length === 0) {
          setPagePostsError('No posts found on this page');
        } else if (!data.has_engagement_data) {
          // Posts loaded but without engagement metrics
          toast.info('Posts loaded without engagement metrics. Add pages_read_user_content permission for full data.');
        }
        return data.posts;
      } else {
        const errorMsg = data.detail || 'Failed to fetch page posts';
        // Check if it's a permissions error and provide helpful message
        if (errorMsg.includes('pages_read_engagement') || errorMsg.includes('pages_read_user_content') || errorMsg.includes('Page Public Content Access')) {
          setPagePostsError('Missing permissions. Add "pages_read_engagement" AND "pages_read_user_content" to your Meta App, then reconnect.');
        } else {
          setPagePostsError(errorMsg);
        }
        toast.error(`Page Posts: ${errorMsg}`);
        return [];
      }
    } catch (error) {
      console.error('Error fetching page posts:', error);
      setPagePostsError('Network error fetching posts');
      return [];
    } finally {
      setIsLoadingPagePosts(false);
    }
  };
  
  // Fetch real campaigns from Meta Ads Manager (ads_read permission)
  const fetchRealCampaigns = async (adAccountId, accessToken, skipLoadingState = false, datePreset = null) => {
    try {
      if (!skipLoadingState) setIsLoadingCampaigns(true);
      // Use provided datePreset or fall back to state
      const dateRange = datePreset || selectedDateRange;
      const response = await fetch(`${API_URL}/api/meta/campaigns?ad_account_id=${adAccountId}&access_token=${accessToken}&date_preset=${dateRange}`);
      const data = await response.json();
      
      if (response.ok && data.campaigns) {
        setCampaigns(data.campaigns);
        console.log(`Fetched ${data.campaigns.length} campaigns with ${dateRange} insights via fetchRealCampaigns`);
        return data.campaigns;
      } else {
        const errorMsg = data.detail || 'Failed to fetch campaigns';
        console.error('Campaigns fetch error:', errorMsg);
        return [];
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      return [];
    } finally {
      if (!skipLoadingState) setIsLoadingCampaigns(false);
    }
  };
  
  // Create real campaign in Meta Ads Manager (ads_management permission)
  const createRealCampaign = async (campaignData, accessToken, adAccountId) => {
    try {
      const response = await fetch(`${API_URL}/api/meta/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: accessToken,
          ad_account_id: adAccountId,
          name: campaignData.name,
          objective: mapObjectiveToMeta(campaignData.objective),
          status: 'PAUSED', // Start paused for review
          special_ad_categories: campaignData.specialAdCategory !== 'NONE' ? [campaignData.specialAdCategory] : [],
          buying_type: campaignData.buyingType || 'AUCTION',
          daily_budget: campaignData.budgetType === 'daily' ? Math.round(parseFloat(campaignData.budgetAmount) * 100) : null,
          lifetime_budget: campaignData.budgetType === 'lifetime' ? Math.round(parseFloat(campaignData.budgetAmount) * 100) : null,
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Campaign "${campaignData.name}" created successfully!`);
        return data;
      } else {
        const errorMsg = data.detail || 'Failed to create campaign';
        toast.error(`Campaign creation failed: ${errorMsg}`);
        return null;
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Network error creating campaign');
      return null;
    }
  };
  
  // Update campaign status (ads_management permission)
  const updateCampaignStatus = async (campaignId, newStatus, accessToken) => {
    try {
      const response = await fetch(`${API_URL}/api/meta/campaigns/${campaignId}?access_token=${accessToken}&status=${newStatus}`, {
        method: 'PUT'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Campaign status updated to ${newStatus}`);
        // Refresh campaigns list
        if (formData.selectedAdAccountId && connectedAccount?.accessToken) {
          await fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken);
        }
        return true;
      } else {
        toast.error(data.detail || 'Failed to update campaign');
        return false;
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      return false;
    }
  };
  
  // Delete campaign function
  const deleteCampaign = async (campaignId, accessToken) => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/meta/campaigns/${campaignId}?access_token=${accessToken}&status=DELETED`, {
        method: 'PUT'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Campaign deleted successfully');
        // Remove from local state
        setCampaigns(prev => prev.filter(c => c.id !== campaignId));
        setDeletingCampaignId(null);
        return true;
      } else {
        toast.error(data.detail || 'Failed to delete campaign');
        return false;
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
      return false;
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Update campaign with full details
  const updateCampaignFull = async (campaignId, updates, accessToken) => {
    try {
      // For now, we update name and budget via the existing endpoint
      // Name update
      if (updates.name) {
        await fetch(`${API_URL}/api/meta/campaigns/${campaignId}/name?access_token=${accessToken}&name=${encodeURIComponent(updates.name)}`, {
          method: 'PUT'
        });
      }
      
      // Budget update (need to update at ad set level for Meta)
      if (updates.daily_budget !== undefined && updates.adset_id) {
        await fetch(`${API_URL}/api/meta/adsets/${updates.adset_id}/budget?access_token=${accessToken}&daily_budget=${Math.round(updates.daily_budget * 100)}`, {
          method: 'PUT'
        });
      }
      
      toast.success('Campaign updated successfully');
      
      // Refresh campaigns
      if (formData.selectedAdAccountId && connectedAccount?.accessToken) {
        await fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken);
      }
      
      setShowFullEditModal(false);
      setEditingCampaignId(null);
      setEditingCampaignData({});
      return true;
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Failed to update campaign');
      return false;
    }
  };
  
  // Map internal objective names to Meta API objective names
  const mapObjectiveToMeta = (objective) => {
    const objectiveMap = {
      'OUTCOME_AWARENESS': 'OUTCOME_AWARENESS',
      'OUTCOME_ENGAGEMENT': 'OUTCOME_ENGAGEMENT',
      'OUTCOME_TRAFFIC': 'OUTCOME_TRAFFIC',
      'OUTCOME_LEADS': 'OUTCOME_LEADS',
      'OUTCOME_APP_PROMOTION': 'OUTCOME_APP_PROMOTION',
      'OUTCOME_SALES': 'OUTCOME_SALES',
      // Legacy mappings
      'BRAND_AWARENESS': 'OUTCOME_AWARENESS',
      'REACH': 'OUTCOME_AWARENESS',
      'POST_ENGAGEMENT': 'OUTCOME_ENGAGEMENT',
      'VIDEO_VIEWS': 'OUTCOME_ENGAGEMENT',
      'LINK_CLICKS': 'OUTCOME_TRAFFIC',
      'WEBSITE_CONVERSIONS': 'OUTCOME_SALES',
      'LEAD_GENERATION': 'OUTCOME_LEADS',
      'APP_INSTALLS': 'OUTCOME_APP_PROMOTION',
    };
    return objectiveMap[objective] || 'OUTCOME_AWARENESS';
  };
  
  // Edit campaign state for Meta Review Mode
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [editingCampaignData, setEditingCampaignData] = useState({});
  const [showFullEditModal, setShowFullEditModal] = useState(false); // Full edit mode
  const [deletingCampaignId, setDeletingCampaignId] = useState(null); // For delete confirmation
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Track if campaigns have been manually imported
  const [campaignsImported, setCampaignsImported] = useState(false);
  
  // Loading states with progress messages
  const [isConnectingToMeta, setIsConnectingToMeta] = useState(false);
  const [connectionProgress, setConnectionProgress] = useState('');
  const [importProgress, setImportProgress] = useState('');
  
  // Connection progress messages sequence
  const connectionProgressMessages = [
    'Connecting to Meta Marketing API...',
    'Authenticating your account...',
    'Fetching account permissions...',
    'Loading your Pages and Ad Accounts...',
  ];
  
  // Import progress messages sequence
  const importProgressMessages = [
    'Connecting to Meta Ads Manager...',
    'Retrieving campaign performance data...',
    'Fetching engagement metrics...',
    'Processing campaign insights...',
  ];
  
  // DISABLED: Auto-fetch campaigns when ad account changes
  // Now user must click "Import Campaigns" button manually
  /*
  useEffect(() => {
    if (connectedAccount?.accessToken && formData.selectedAdAccountId) {
      fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken);
    }
  }, [formData.selectedAdAccountId, connectedAccount?.accessToken]);
  */
  
  // Special Ad Category Verification State
  const [identityVerificationStatus, setIdentityVerificationStatus] = useState(null); // 'verified', 'pending', 'not_verified'
  const [disclaimerStatus, setDisclaimerStatus] = useState(null); // 'active', 'pending', 'none'
  const [disclaimers, setDisclaimers] = useState([]); // List of user's disclaimers
  const [selectedDisclaimer, setSelectedDisclaimer] = useState(null); // Selected disclaimer for the campaign
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [isLoadingDisclaimers, setIsLoadingDisclaimers] = useState(false);

  // Platform-specific form data - stores data for each platform independently
  const [platformFormData, setPlatformFormData] = useState({
    facebook: { campaignName: '', objective: '', budgetAmount: '', startDate: '', endDate: '', linkedAccount: null },
    instagram: { campaignName: '', objective: '', budgetAmount: '', startDate: '', endDate: '', linkedAccount: null },
    tiktok: { campaignName: '', objective: '', budgetAmount: '', startDate: '', endDate: '', linkedAccount: null, bidType: 'NO_BID' },
    google: { campaignName: '', campaignType: '', budgetAmount: '', startDate: '', endDate: '', linkedAccount: null, biddingStrategy: 'MAXIMIZE_CLICKS', keywords: '' },
    snapchat: { campaignName: '', objective: '', budgetAmount: '', startDate: '', endDate: '', linkedAccount: null },
    youtube: { campaignName: '', campaignType: '', budgetAmount: '', startDate: '', endDate: '', linkedAccount: null, videoFormat: 'SKIPPABLE_IN_STREAM', videoUrl: '' },
    twitter: { campaignName: '', objective: '', budgetAmount: '', startDate: '', endDate: '', linkedAccount: null },
  });

  // Function to sync shared fields across platforms when a field is updated
  const syncSharedFieldsAcrossPlatforms = (fieldName, value, sourcePlatform) => {
    // Fields that should be shared across all platforms
    const sharedFields = ['campaignName', 'budgetAmount', 'startDate', 'endDate'];
    
    if (sharedFields.includes(fieldName)) {
      setPlatformFormData(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(platform => {
          if (platform !== sourcePlatform) {
            // Only sync if the target platform's field is empty or matches the source
            if (!updated[platform][fieldName] || updated[platform][fieldName] === prev[sourcePlatform][fieldName]) {
              updated[platform] = { ...updated[platform], [fieldName]: value };
            }
          }
        });
        updated[sourcePlatform] = { ...updated[sourcePlatform], [fieldName]: value };
        return updated;
      });
    } else {
      // For non-shared fields, only update the current platform
      setPlatformFormData(prev => ({
        ...prev,
        [sourcePlatform]: { ...prev[sourcePlatform], [fieldName]: value }
      }));
    }
  };

  // Update platform form field with auto-sync
  const updatePlatformField = (fieldName, value) => {
    syncSharedFieldsAcrossPlatforms(fieldName, value, activePlatform);
    
    // Also update main formData for backward compatibility
    if (['campaignName', 'objective', 'budgetAmount', 'startDate', 'endDate'].includes(fieldName)) {
      setFormData(prev => ({ ...prev, [fieldName]: value }));
    }
  };

  // Get current platform's form config
  const currentPlatformConfig = PLATFORM_FORM_CONFIG[activePlatform] || PLATFORM_FORM_CONFIG.facebook;
  const currentPlatformData = platformFormData[activePlatform] || {};
  const isSnapchat = activePlatform === 'snapchat';

  // Snapchat Marketing API integration (OAuth, accounts, campaigns, submission)
  const snap = useSnapchatAds(setPlatformSettings);
  const [snapSubmitResult, setSnapSubmitResult] = useState(null);
  const [isActivatingSnap, setIsActivatingSnap] = useState(false);


  // Fetch influencers on mount
  useEffect(() => {
    const fetchInfluencers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/influencers`);
        setInfluencers(response.data);
      } catch (error) {
        console.error('Failed to fetch influencers:', error);
        toast.error('Failed to load influencers');
      } finally {
        setLoadingInfluencers(false);
      }
    };
    fetchInfluencers();
  }, []);

  // Fetch Facebook Pages using Graph API
  const fetchFacebookPages = async (accessToken) => {
    setIsLoadingPages(true);
    try {
      const response = await fetch(
        `https://graph.facebook.com/v19.0/me/accounts?fields=id,name,category,fan_count,picture.width(100),access_token&access_token=${accessToken}`
      );
      const data = await response.json();
      
      if (data.error) {
        console.error('Error fetching pages:', data.error);
        setMetaApiError(`Pages: ${data.error.message}`);
        setFacebookPages([]);
        return [];
      }
      
      const pages = (data.data || []).map(page => ({
        id: page.id,
        name: page.name,
        category: page.category || 'Page',
        followers: page.fan_count || 0,
        picture: page.picture?.data?.url || `https://ui-avatars.com/api/?name=${encodeURIComponent(page.name)}&background=1877f2&color=fff`,
        access_token: page.access_token // Store page access token for API calls
      }));
      
      setFacebookPages(pages);
      return pages;
    } catch (error) {
      console.error('Failed to fetch Facebook pages:', error);
      setMetaApiError('Failed to fetch Facebook pages');
      setFacebookPages([]);
      return [];
    } finally {
      setIsLoadingPages(false);
    }
  };

  // Fetch Ad Accounts using Graph API
  const fetchAdAccounts = async (accessToken) => {
    setIsLoadingAdAccounts(true);
    try {
      const response = await fetch(
        `https://graph.facebook.com/v19.0/me/adaccounts?fields=id,name,account_status,currency,amount_spent,spend_cap,business_name&access_token=${accessToken}`
      );
      const data = await response.json();
      
      if (data.error) {
        console.error('Error fetching ad accounts:', data.error);
        setMetaApiError(`Ad Accounts: ${data.error.message}`);
        setAdAccounts([]);
        return [];
      }
      
      const accounts = (data.data || []).map(account => ({
        id: account.id,
        name: account.name || account.business_name || `Account ${account.id}`,
        currency: account.currency || 'NGN',
        status: account.account_status === 1 ? 'ACTIVE' : account.account_status === 2 ? 'DISABLED' : 'PENDING',
        spend_cap: account.spend_cap ? parseInt(account.spend_cap) / 100 : 0,
        amount_spent: account.amount_spent ? parseInt(account.amount_spent) / 100 : 0
      }));
      
      setAdAccounts(accounts);
      return accounts;
    } catch (error) {
      console.error('Failed to fetch ad accounts:', error);
      setMetaApiError('Failed to fetch ad accounts');
      setAdAccounts([]);
      return [];
    } finally {
      setIsLoadingAdAccounts(false);
    }
  };

  // Fetch Campaigns for a specific Ad Account - NOW ROUTED THROUGH BACKEND
  const fetchCampaigns = async (accessToken, adAccountId, datePreset = null) => {
    if (!adAccountId) return [];
    
    // Use the provided datePreset or fall back to state
    const dateRange = datePreset || selectedDateRange;
    
    setIsLoadingCampaigns(true);
    setMetaApiError(null);
    
    try {
      // Route through backend API for better logging and error handling
      const params = new URLSearchParams({
        access_token: accessToken,
        ad_account_id: adAccountId,
        date_preset: dateRange
      });
      
      console.log(`Fetching campaigns via backend with date_preset: ${dateRange}`);
      
      const response = await fetch(`${API_URL}/api/meta/campaigns?${params.toString()}`);
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Backend error fetching campaigns:', data);
        setMetaApiError(data.detail || 'Failed to fetch campaigns');
        setCampaigns([]);
        return [];
      }
      
      // Map backend response to frontend format
      const campaignList = (data.campaigns || []).map(campaign => ({
        id: campaign.id,
        name: campaign.name,
        status: campaign.status,
        objective: campaign.objective,
        daily_budget: campaign.daily_budget || 0,
        lifetime_budget: campaign.lifetime_budget || 0,
        budget_remaining: campaign.budget_remaining || 0,
        spent: campaign.spent || 0,
        impressions: campaign.impressions || 0,
        reach: campaign.reach || 0,
        clicks: campaign.clicks || 0,
        ctr: campaign.ctr || 0,
        cpm: campaign.cpm || 0,
        likes: campaign.likes || 0,
        comments: campaign.comments || 0,
        shares: campaign.shares || 0,
        post_engagement: campaign.post_engagement || 0,
        link_clicks: campaign.link_clicks || 0,
        buying_type: campaign.buying_type || 'AUCTION',
        created_at: campaign.created_time ? campaign.created_time.split('T')[0] : ''
      }));
      
      // Log engagement metrics for debugging
      campaignList.forEach(c => {
        if (c.likes > 0 || c.comments > 0 || c.shares > 0) {
          console.log(`Campaign "${c.name}" engagement: likes=${c.likes}, comments=${c.comments}, shares=${c.shares}`);
        }
      });
      
      console.log(`Fetched ${campaignList.length} campaigns with ${dateRange} insights`);
      setCampaigns(campaignList);
      return campaignList;
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      setMetaApiError('Failed to fetch campaigns from server');
      setCampaigns([]);
      return [];
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  // Fetch all Meta data after successful login
  const fetchAllMetaData = async (accessToken) => {
    setMetaApiError(null);
    
    // Fetch pages and ad accounts in parallel (but NOT campaigns)
    // Campaigns are fetched manually via "Import Campaigns" button
    const [pages, accounts] = await Promise.all([
      fetchFacebookPages(accessToken),
      fetchAdAccounts(accessToken)
    ]);
    
    // Don't auto-select - user must manually choose from dropdowns
    
    return { pages, accounts };
  };

  // Check identity verification status for Special Ad Categories
  const checkIdentityVerification = async (accessToken, adAccountId) => {
    if (!accessToken || !adAccountId) return null;
    
    setIsCheckingVerification(true);
    try {
      // Check ad account for any restrictions related to identity verification
      const response = await fetch(
        `https://graph.facebook.com/v19.0/${adAccountId}?fields=account_status,disable_reason,funding_source_details,is_personal&access_token=${accessToken}`
      );
      const data = await response.json();
      
      if (data.error) {
        console.error('Error checking verification:', data.error);
        return null;
      }
      
      // Account status 1 = ACTIVE, 2 = DISABLED, 3 = UNSETTLED, etc.
      // For Special Ad Categories, we need to check if the account is in good standing
      const isAccountActive = data.account_status === 1;
      
      // Check Page verification status for political ads
      // This requires checking the Page's verification status
      setIdentityVerificationStatus(isAccountActive ? 'verified' : 'not_verified');
      
      return {
        accountStatus: data.account_status,
        isActive: isAccountActive,
        disableReason: data.disable_reason,
        isPersonal: data.is_personal
      };
    } catch (error) {
      console.error('Failed to check verification status:', error);
      return null;
    } finally {
      setIsCheckingVerification(false);
    }
  };

  // Fetch disclaimers for political/social issue ads
  // Meta's "Paid for by" disclaimers are set up at the Business/Page level
  const fetchDisclaimers = async (accessToken, pageId) => {
    if (!accessToken || !pageId) {
      setDisclaimers([]);
      setDisclaimerStatus('none');
      return null;
    }
    
    setIsLoadingDisclaimers(true);
    try {
      const foundDisclaimers = [];
      const adAccountId = formData.selectedAdAccountId || adAccounts[0]?.id;
      
      // Try multiple approaches to find disclaimers
      
      // 1. Try to get business-level disclaimers via the ad account's business
      if (adAccountId) {
        const accountResponse = await fetch(
          `https://graph.facebook.com/v19.0/${adAccountId}?fields=business,owner&access_token=${accessToken}`
        );
        const accountData = await accountResponse.json();
        
        if (accountData.business) {
          // Try to fetch owned_ad_disclaimers from business (if this edge exists)
          const businessId = accountData.business.id;
          
          // Try multiple potential edges for disclaimers
          const potentialEdges = [
            'owned_ad_disclaimers',
            'ad_disclaimers', 
            'disclaimers',
            'funding_source_infos'
          ];
          
          for (const edge of potentialEdges) {
            try {
              const edgeResponse = await fetch(
                `https://graph.facebook.com/v19.0/${businessId}/${edge}?access_token=${accessToken}`
              );
              const edgeData = await edgeResponse.json();
              
              if (edgeData.data && Array.isArray(edgeData.data) && edgeData.data.length > 0) {
                edgeData.data.forEach((item) => {
                  const name = item.name || item.display_string || item.title || item.disclaimer_text;
                  if (name && !foundDisclaimers.some(d => d.name === name)) {
                    foundDisclaimers.push({
                      id: item.id || `disc_${foundDisclaimers.length}`,
                      name: name,
                      type: 'disclaimer',
                      status: item.status || 'active'
                    });
                  }
                });
                break; // Found disclaimers, stop trying other edges
              }
            } catch (e) {
              // Edge doesn't exist or no permission, try next
              continue;
            }
          }
        }
      }
      
      // 2. Try to get disclaimers from the Page directly
      if (foundDisclaimers.length === 0) {
        const pageEdges = ['ad_disclaimers', 'disclaimers', 'authorized_ad_accounts'];
        
        for (const edge of pageEdges) {
          try {
            const pageEdgeResponse = await fetch(
              `https://graph.facebook.com/v19.0/${pageId}/${edge}?access_token=${accessToken}`
            );
            const pageEdgeData = await pageEdgeResponse.json();
            
            if (pageEdgeData.data && Array.isArray(pageEdgeData.data) && pageEdgeData.data.length > 0) {
              pageEdgeData.data.forEach((item) => {
                const name = item.name || item.display_string || item.title;
                if (name && !foundDisclaimers.some(d => d.name === name)) {
                  foundDisclaimers.push({
                    id: item.id || `disc_${foundDisclaimers.length}`,
                    name: name,
                    type: 'disclaimer',
                    status: item.status || 'active'
                  });
                }
              });
              break;
            }
          } catch (e) {
            continue;
          }
        }
      }
      
      // 3. Try to get from user's ad authorization data
      if (foundDisclaimers.length === 0) {
        try {
          const meResponse = await fetch(
            `https://graph.facebook.com/v19.0/me?fields=id,name&access_token=${accessToken}`
          );
          const meData = await meResponse.json();
          
          if (meData.id) {
            // Try to get ad authorization / disclaimers from user
            const authResponse = await fetch(
              `https://graph.facebook.com/v19.0/${meData.id}/ad_authorizations?access_token=${accessToken}`
            );
            const authData = await authResponse.json();
            
            if (authData.data && Array.isArray(authData.data)) {
              authData.data.forEach((item) => {
                const name = item.disclaimer_name || item.name || item.payer_name;
                if (name && !foundDisclaimers.some(d => d.name === name)) {
                  foundDisclaimers.push({
                    id: item.id || `disc_${foundDisclaimers.length}`,
                    name: name,
                    type: 'disclaimer',
                    status: 'active'
                  });
                }
              });
            }
          }
        } catch (e) {
          console.log('Could not fetch ad authorizations');
        }
      }
      
      // 4. Fallback: Use Page name as disclaimer option
      if (foundDisclaimers.length === 0) {
        const pageResponse = await fetch(
          `https://graph.facebook.com/v19.0/${pageId}?fields=id,name&access_token=${accessToken}`
        );
        const pageData = await pageResponse.json();
        
        if (!pageData.error && pageData.name) {
          foundDisclaimers.push({
            id: pageId,
            name: pageData.name,
            type: 'page_fallback',
            status: 'active',
            note: 'Using Page name as fallback. Set up custom disclaimer at facebook.com/id'
          });
        }
      }
      
      if (foundDisclaimers.length > 0) {
        setDisclaimers(foundDisclaimers);
        setDisclaimerStatus('active');
        return foundDisclaimers;
      }
      
      // No disclaimers found
      setDisclaimers([]);
      setDisclaimerStatus('none');
      return [];
      
    } catch (error) {
      console.error('Failed to fetch disclaimers:', error);
      setDisclaimers([]);
      setDisclaimerStatus('none');
      return [];
    } finally {
      setIsLoadingDisclaimers(false);
    }
  };

  // Handle Special Ad Category selection
  const handleSpecialAdCategoryChange = async (value) => {
    updateField('specialAdCategory', value);
    
    const category = SPECIAL_AD_CATEGORIES.find(c => c.value === value);
    
    if (category?.requiresVerification) {
      const token = connectedAccount?.accessToken || localStorage.getItem('meta_access_token');
      const adAccountId = formData.selectedAdAccountId || adAccounts[0]?.id;
      
      if (token && adAccountId) {
        await checkIdentityVerification(token, adAccountId);
      }
      
      // If political ads, also fetch disclaimers
      if (category?.requiresDisclaimer) {
        const pageId = formData.selectedPageId || facebookPages[0]?.id;
        if (token && pageId) {
          await fetchDisclaimers(token, pageId);
        }
      }
    } else {
      // Reset verification states for non-special categories
      setIdentityVerificationStatus(null);
      setDisclaimerStatus(null);
      setDisclaimers([]);
      setSelectedDisclaimer(null);
    }
  };

  // Simulated Meta Login (will be replaced with real OAuth after Meta approval)
  const handleMetaLogin = async (loginType) => {
    setIsConnecting(true);
    
    // Facebook Login for Business uses config_id - permissions are defined in Meta App Dashboard
    // IMPORTANT: The config_id configuration in Meta App Dashboard MUST include ads_management permission
    // Go to: Meta App Dashboard > Facebook Login for Business > Configurations > Edit your config
    // Make sure these permissions are enabled: pages_show_list, ads_read, ads_management, business_management
    const configId = '1018089424501122';
    const appId = '26924612877172486';
    
    // Get the current URL for redirect
    const redirectUri = encodeURIComponent(window.location.href.split('?')[0]);
    
    // Build the Facebook OAuth URL for Login for Business
    // Note: When using config_id, permissions are controlled by the Login Configuration in Meta App Dashboard
    // The scope parameter is ignored when config_id is present
    // auth_type=rerequest forces Facebook to re-prompt for permissions
    const authUrl = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${appId}&config_id=${configId}&redirect_uri=${redirectUri}&response_type=token&auth_type=rerequest&display=popup`;
    
    // Open popup window
    const width = 600;
    const height = 700;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    
    const popup = window.open(
      authUrl,
      'facebook-login',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );
    
    // Listen for the popup to close or redirect back
    const checkPopup = setInterval(() => {
      try {
        // Check if popup is closed
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          setIsConnecting(false);
          
          // Check if we got a token in the URL hash
          const hash = window.location.hash;
          if (hash && hash.includes('access_token')) {
            handleOAuthCallback(hash, loginType);
          }
          return;
        }
        
        // Check if popup redirected back to our domain
        if (popup.location.href.includes(window.location.hostname)) {
          const popupHash = popup.location.hash;
          popup.close();
          clearInterval(checkPopup);
          
          if (popupHash && popupHash.includes('access_token')) {
            handleOAuthCallback(popupHash, loginType);
          } else {
            setIsConnecting(false);
            toast.error('Login was cancelled');
          }
        }
      } catch (e) {
        // Cross-origin error - popup is still on Facebook domain, keep waiting
      }
    }, 500);
    
    // Timeout after 5 minutes
    setTimeout(() => {
      clearInterval(checkPopup);
      if (popup && !popup.closed) {
        popup.close();
      }
      setIsConnecting(false);
    }, 300000);
  };
  
  // Handle OAuth callback with access token
  const handleOAuthCallback = async (hash, loginType) => {
    // Parse the hash to get access token
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    
    if (!accessToken) {
      setIsConnecting(false);
      toast.error('Failed to get access token');
      return;
    }
    
    // Store the token
    localStorage.setItem('meta_access_token', accessToken);
    
    // Clear the hash from URL
    window.history.replaceState(null, '', window.location.pathname);
    
    try {
      // Get user info using the Graph API
      const userResponse = await fetch(`https://graph.facebook.com/me?fields=name,email,picture.width(100)&access_token=${accessToken}`);
      const userInfo = await userResponse.json();
      
      if (userInfo.error) {
        throw new Error(userInfo.error.message);
      }
      
      localStorage.setItem('meta_user_id', userInfo.id);
      
      setConnectedAccount({
        name: userInfo.name || 'Meta User',
        email: userInfo.email || '',
        profilePicture: userInfo.picture?.data?.url || `https://ui-avatars.com/api/?name=${encodeURIComponent(userInfo.name || 'User')}&background=1877f2&color=fff`,
        loginType: loginType,
        accessToken: accessToken,
        userId: userInfo.id,
      });
      
      setIsMetaConnected(true);
      setIsConnecting(false);
      setShowMetaLoginModal(false);
      
      // Show connection progress loading screen
      setIsConnectingToMeta(true);
      
      // Animate through progress messages
      for (let i = 0; i < connectionProgressMessages.length; i++) {
        setConnectionProgress(connectionProgressMessages[i]);
        await new Promise(resolve => setTimeout(resolve, 750)); // 750ms per message = 3 seconds total
      }
      
      // In Meta Review Mode with Business Suite login, show Business Portfolio selector first
      if (metaReviewMode && (loginType === 'business' || loginType === 'business_suite')) {
        setIsConnectingToMeta(false);
        setConnectionProgress('');
        setShowBusinessSelector(true);
        toast.success(`Welcome, ${userInfo.name}!`, {
          description: 'Fetching your Business Portfolios...',
        });
        
        // Fetch real businesses from Meta API
        await fetchBusinesses(accessToken);
      } else if (metaReviewMode) {
        // Regular Facebook login in Review Mode - just fetch pages and ad accounts
        await fetchAllMetaData(accessToken);
        setIsConnectingToMeta(false);
        setConnectionProgress('');
        toast.success(`Welcome, ${userInfo.name}! Meta data loaded successfully.`);
      } else {
        // Fetch all Meta data (pages, ad accounts, campaigns)
        await fetchAllMetaData(accessToken);
        setIsConnectingToMeta(false);
        setConnectionProgress('');
        toast.success(`Welcome, ${userInfo.name}! Meta data loaded successfully.`);
      }
      
    } catch (err) {
      console.error('Error during OAuth callback:', err);
      setIsConnecting(false);
      toast.error('Failed to connect: ' + err.message);
    }
  };

  const handleDisconnectMeta = () => {
    // Clear stored tokens
    localStorage.removeItem('meta_access_token');
    localStorage.removeItem('meta_user_id');
    
    // Clear all Meta data
    setFacebookPages([]);
    setAdAccounts([]);
    setCampaigns([]);
    setMetaApiError(null);
    setCampaignsImported(false);
    
    setIsMetaConnected(false);
    setConnectedAccount(null);
    setShowMetaLoginModal(false); // Don't auto-show login modal
    toast.info('Disconnected from Meta');
  };

  // Disconnect Google Ads
  const handleDisconnectGoogle = async () => {
    try {
      await fetch(`${API_URL}/api/google-ads/disconnect`, { method: 'POST' });
      
      // Clear Google Ads state
      setGoogleAdsConnection(null);
      setGoogleAdsAccounts([]);
      setSelectedGoogleAccount(null);
      
      // Update platform settings
      setPlatformSettings(prev => ({
        ...prev,
        google: { ...prev.google, enabled: false, connected: false, accountName: '' },
        youtube: { ...prev.youtube, enabled: false, connected: false, accountName: '' }
      }));
      
      toast.info('Disconnected from Google Ads');
    } catch (error) {
      console.error('Failed to disconnect Google Ads:', error);
      toast.error('Failed to disconnect Google Ads');
    }
  };

  // Check for OAuth callback on page load (redirect flow)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      handleOAuthCallback(hash, 'facebook');
      return; // Don't check stored token if we have a new one
    }
    
    // Also check if user has a stored token
    const storedToken = localStorage.getItem('meta_access_token');
    const storedUserId = localStorage.getItem('meta_user_id');
    
    if (storedToken && storedUserId) {
      // Verify the token is still valid
      fetch(`https://graph.facebook.com/me?fields=name,email,picture.width(100)&access_token=${storedToken}`)
        .then(res => res.json())
        .then(async (userInfo) => {
          if (userInfo.error) {
            // Token expired, clear it
            localStorage.removeItem('meta_access_token');
            localStorage.removeItem('meta_user_id');
            return;
          }
          
          setConnectedAccount({
            name: userInfo.name || 'Meta User',
            email: userInfo.email || '',
            profilePicture: userInfo.picture?.data?.url || `https://ui-avatars.com/api/?name=${encodeURIComponent(userInfo.name || 'User')}&background=1877f2&color=fff`,
            loginType: 'facebook',
            accessToken: storedToken,
            userId: storedUserId,
          });
          
          setIsMetaConnected(true);
          setShowMetaLoginModal(false);
          
          // Fetch all Meta data with stored token
          await fetchAllMetaData(storedToken);
        })
        .catch(() => {
          // Token invalid, clear it
          localStorage.removeItem('meta_access_token');
          localStorage.removeItem('meta_user_id');
        });
    }
  }, []);

  // State for selected campaign details view
  const [selectedCampaignDetails, setSelectedCampaignDetails] = useState(null);
  const [showCampaignDetailsModal, setShowCampaignDetailsModal] = useState(false);

  // Auto-trigger Meta Login Modal in review mode if not connected - DISABLED
  // Now user must click "Connect Account" button first
  /*
  useEffect(() => {
    if (metaReviewMode && !isMetaConnected) {
      const timer = setTimeout(() => {
        if (!isMetaConnected) {
          setShowMetaLoginModal(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [metaReviewMode, isMetaConnected]);
  */

  // Silent keyboard shortcut to bypass login (Ctrl+Shift+B) - Uses demo data for testing
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Shift+B to bypass login silently
      if (e.ctrlKey && e.shiftKey && e.key === 'B') {
        e.preventDefault();
        setShowMetaLoginModal(false);
        setIsMetaConnected(true);
        setConnectedAccount({
          name: 'Demo User',
          email: 'demo@adlinka.com',
          profilePicture: 'https://ui-avatars.com/api/?name=Demo&background=1877f2&color=fff',
          loginType: 'bypass',
          accessToken: 'demo_token',
          userId: 'demo_user',
        });
        
        // In Meta Review Mode, set demo data directly (bypass simulates regular Facebook login)
        // For Business Suite testing, user should use the actual Business Suite login button
        setFacebookPages([
          { id: 'demo_page_1', name: 'Demo Page', category: 'Business', followers: 1000, picture: 'https://ui-avatars.com/api/?name=Demo&background=1877f2&color=fff' }
        ]);
        setAdAccounts([
          { id: 'act_demo', name: 'Demo Ad Account', currency: 'NGN', status: 'ACTIVE', spend_cap: 100000, timezone: 'Africa/Lagos' }
        ]);
        setCampaigns([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [metaReviewMode]);
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'GLOBE_LOCATION_UPDATE') {
        const { latitude, longitude, radius, locationName, locationType, dailyReach } = event.data;
        setFormData(prev => ({
          ...prev,
          latitude,
          longitude,
          radius: radius / 1000,
          locationName: locationName || prev.locationName,
          locationType: locationType || 'region',
          dailyReach: dailyReach || 0,
        }));
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handle Google Ads OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleAuthSuccess = urlParams.get('google_auth_success');
    const googleAuthError = urlParams.get('google_auth_error');
    const googleEmail = urlParams.get('email');
    
    if (googleAuthSuccess === 'true') {
      toast.success('Google Ads Connected!', {
        description: googleEmail ? `Connected as ${googleEmail}` : 'Your Google Ads account is now connected.'
      });
      
      // Update platform settings to show Google as connected
      setPlatformSettings(prev => ({
        ...prev,
        google: { ...prev.google, enabled: true, connected: true, accountName: googleEmail || 'Connected' },
        youtube: { ...prev.youtube, enabled: true, connected: true, accountName: googleEmail || 'Connected' }
      }));
      
      // Fetch Google Ads connection status
      fetchGoogleAdsStatus();
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (googleAuthError) {
      toast.error('Google Ads Connection Failed', {
        description: decodeURIComponent(googleAuthError)
      });
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // State for Google Ads connection
  const [googleAdsConnection, setGoogleAdsConnection] = useState(null);
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
  const [googleAdsClientAccounts, setGoogleAdsClientAccounts] = useState([]);
  const [googleAdsManagerId, setGoogleAdsManagerId] = useState(null);
  const [selectedGoogleAccount, setSelectedGoogleAccount] = useState(null);
  const [isCreatingClientAccount, setIsCreatingClientAccount] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [newClientAccountName, setNewClientAccountName] = useState('');
  const [showLinkAccountModal, setShowLinkAccountModal] = useState(false);
  const [linkAccountId, setLinkAccountId] = useState('');
  const [isLinkingAccount, setIsLinkingAccount] = useState(false);

  // State for X (Twitter) Ads connection
  const [xAdsConnection, setXAdsConnection] = useState(null);
  const [xAdsAccounts, setXAdsAccounts] = useState([]);
  const [selectedXAdsAccount, setSelectedXAdsAccount] = useState(null);
  const [xAdsFundingInstruments, setXAdsFundingInstruments] = useState([]);

  // Fetch X Ads connection status
  const fetchXAdsStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/x-ads/connection-status`);
      const data = await response.json();
      
      if (data.connected) {
        setXAdsConnection(data);
        
        // Update platform settings
        setPlatformSettings(prev => ({
          ...prev,
          twitter: { ...prev.twitter, enabled: true, connected: true, accountName: `@${data.username}` || 'Connected' }
        }));
        
        // Fetch X Ads accounts
        await fetchXAdsAccounts();
      }
    } catch (error) {
      console.error('Failed to fetch X Ads status:', error);
    }
  };

  // Fetch X Ads accounts
  const fetchXAdsAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/x-ads/accounts`);
      const data = await response.json();
      
      if (data.success && data.accounts) {
        setXAdsAccounts(data.accounts);
        
        // Auto-select first active account
        const activeAccount = data.accounts.find(acc => !acc.deleted && acc.approval_status === 'ACCEPTED');
        if (activeAccount) {
          setSelectedXAdsAccount(activeAccount);
          // Fetch funding instruments for this account
          await fetchXAdsFundingInstruments(activeAccount.id);
        } else if (data.accounts.length > 0) {
          setSelectedXAdsAccount(data.accounts[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch X Ads accounts:', error);
    }
  };

  // Fetch funding instruments for X Ads account
  const fetchXAdsFundingInstruments = async (accountId) => {
    try {
      const response = await fetch(`${API_URL}/api/x-ads/funding-instruments/${accountId}`);
      const data = await response.json();
      
      if (data.success) {
        setXAdsFundingInstruments(data.funding_instruments || []);
      }
    } catch (error) {
      console.error('Failed to fetch X Ads funding instruments:', error);
    }
  };

  // Handle X Ads OAuth with popup window
  const handleXAdsConnect = async () => {
    try {
      toast.info('Connecting to X Ads...', { description: 'Opening X login...' });
      
      const response = await fetch(`${API_URL}/api/x-ads/auth`);
      const data = await response.json();
      
      if (data.success && data.authorization_url) {
        // Open popup window for OAuth
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        window.open(
          data.authorization_url,
          'X Ads Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );
      } else {
        toast.error('Failed to start X Ads login');
      }
    } catch (error) {
      console.error('X Ads auth error:', error);
      toast.error('X Ads Connection Failed', {
        description: 'Failed to connect to X Ads. Please try again.'
      });
    }
  };

  // Disconnect X Ads
  const handleDisconnectXAds = async () => {
    try {
      await fetch(`${API_URL}/api/x-ads/disconnect`, { method: 'POST' });
      
      // Clear X Ads state
      setXAdsConnection(null);
      setXAdsAccounts([]);
      setSelectedXAdsAccount(null);
      setXAdsFundingInstruments([]);
      
      setPlatformSettings(prev => ({
        ...prev,
        twitter: { ...prev.twitter, connected: false, accountName: '' }
      }));
      
      toast.info('Disconnected from X Ads');
    } catch (error) {
      console.error('Failed to disconnect X Ads:', error);
      toast.error('Failed to disconnect X Ads');
    }
  };

  // Fetch Google Ads connection status
  const fetchGoogleAdsStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/google-ads/connection-status`);
      const data = await response.json();
      
      if (data.connected) {
        setGoogleAdsConnection(data);
        
        // Update platform settings
        setPlatformSettings(prev => ({
          ...prev,
          google: { ...prev.google, enabled: true, connected: true, accountName: data.user_email || 'Connected' },
          youtube: { ...prev.youtube, enabled: true, connected: true, accountName: data.user_email || 'Connected' }
        }));
        
        // Fetch full accounts data
        await fetchGoogleAdsAccounts();
      }
    } catch (error) {
      console.error('Failed to fetch Google Ads status:', error);
    }
  };

  // Fetch Google Ads accounts (MCC and client accounts)
  const fetchGoogleAdsAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/google-ads/ad-accounts`);
      const data = await response.json();
      
      if (data.success) {
        setGoogleAdsAccounts(data.accounts || []);
        setGoogleAdsClientAccounts(data.client_accounts || []);
        setGoogleAdsManagerId(data.manager_account_id);
        
        // Auto-select first client account if available, otherwise first account
        if (data.client_accounts && data.client_accounts.length > 0) {
          setSelectedGoogleAccount(data.client_accounts[0]);
        } else if (data.accounts && data.accounts.length > 0) {
          // If only manager account, check if it's not a manager
          const nonManagerAccount = data.accounts.find(acc => !acc.is_manager);
          if (nonManagerAccount) {
            setSelectedGoogleAccount(nonManagerAccount);
          } else {
            setSelectedGoogleAccount(data.accounts[0]);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch Google Ads accounts:', error);
    }
  };

  // Create a new client account under MCC
  const createGoogleAdsClientAccount = async () => {
    if (!newClientAccountName.trim()) {
      toast.error('Please enter an account name');
      return;
    }
    
    setIsCreatingClientAccount(true);
    try {
      const response = await fetch(`${API_URL}/api/google-ads/create-client-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          descriptive_name: newClientAccountName.trim(),
          currency_code: 'NGN',
          time_zone: 'Africa/Lagos'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Client Account Created!', {
          description: `Account "${data.descriptive_name}" (${data.customer_id}) created successfully.`
        });
        setShowCreateAccountModal(false);
        setNewClientAccountName('');
        
        // Refresh accounts list
        await fetchGoogleAdsAccounts();
      } else {
        toast.error('Failed to create account', { description: data.detail || 'Unknown error' });
      }
    } catch (error) {
      console.error('Failed to create client account:', error);
      toast.error('Failed to create account', { description: error.message });
    } finally {
      setIsCreatingClientAccount(false);
    }
  };

  // Link an existing Google Ads account to MCC
  const linkExistingGoogleAdsAccount = async () => {
    if (!linkAccountId.trim()) {
      toast.error('Please enter a Customer ID');
      return;
    }
    
    setIsLinkingAccount(true);
    try {
      const response = await fetch(`${API_URL}/api/google-ads/link-existing-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: linkAccountId.trim()
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Link Invitation Sent!', {
          description: `Invitation sent to account ${data.customer_id}. The account owner must accept it in Google Ads.`,
          duration: 8000
        });
        setShowLinkAccountModal(false);
        setLinkAccountId('');
        
        // Refresh accounts list after a short delay
        setTimeout(() => fetchGoogleAdsAccounts(), 2000);
      } else {
        toast.error('Failed to link account', { description: data.detail || 'Unknown error' });
      }
    } catch (error) {
      console.error('Failed to link account:', error);
      toast.error('Failed to link account', { description: error.message });
    } finally {
      setIsLinkingAccount(false);
    }
  };

  // Check Google Ads status on mount
  useEffect(() => {
    fetchGoogleAdsStatus();
  }, []);

  // Check X Ads status on mount
  useEffect(() => {
    fetchXAdsStatus();
  }, []);

  // Listen for Google OAuth popup messages
  useEffect(() => {
    const handleMessage = (event) => {
      // Handle Google Ads auth messages from popup
      if (event.data?.type === 'GOOGLE_ADS_AUTH_SUCCESS') {
        toast.success('Google Ads Connected!', {
          description: event.data.email ? `Connected as ${event.data.email}` : 'Your Google Ads account is now connected.'
        });
        fetchGoogleAdsStatus();
      } else if (event.data?.type === 'GOOGLE_ADS_AUTH_ERROR') {
        toast.error('Google Ads Connection Failed', {
          description: event.data.error || 'Please try again.'
        });
      }
      // Handle X Ads auth messages from popup
      else if (event.data?.type === 'X_ADS_AUTH_SUCCESS') {
        toast.success('X Ads Connected!', {
          description: event.data.username ? `Connected as @${event.data.username}` : 'Your X Ads account is now connected.'
        });
        fetchXAdsStatus();
      } else if (event.data?.type === 'X_ADS_AUTH_ERROR') {
        toast.error('X Ads Connection Failed', {
          description: event.data.error || 'Please try again.'
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Toggle influencer selection for Post Up
  const toggleInfluencer = (influencerId) => {
    setFormData(prev => {
      const currentSelected = prev.postUpInfluencers;
      if (currentSelected.includes(influencerId)) {
        return { ...prev, postUpInfluencers: currentSelected.filter(id => id !== influencerId) };
      } else {
        return { ...prev, postUpInfluencers: [...currentSelected, influencerId] };
      }
    });
  };

  // Get selected influencer names for display
  const getSelectedInfluencerNames = () => {
    return formData.postUpInfluencers
      .map(id => influencers.find(inf => inf._id === id || inf.id === id)?.name)
      .filter(Boolean);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 30 * 1024 * 1024) {
        toast.error('Image must be less than 30MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          imageFile: file,
          imagePreview: reader.result,
          mediaType: 'image',
          videoFile: null,
          videoPreview: null,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024 * 1024) {
        toast.error('Video must be less than 500MB');
        return;
      }
      // Create video preview URL
      const videoUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        videoFile: file,
        videoPreview: videoUrl,
        mediaType: 'video',
        imageFile: null,
        imagePreview: null,
      }));
      toast.success('Video uploaded successfully');
    }
  };

  const removeMedia = () => {
    // Revoke video URL to free memory
    if (formData.videoPreview && formData.videoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.videoPreview);
    }
    setFormData(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: null,
      videoFile: null,
      videoPreview: null,
      mediaType: 'image',
    }));
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: null,
    }));
  };

  // Fetch Reservation Estimates from Meta API
  const fetchReservationEstimates = async () => {
    if (!formData.locationName && !formData.latitude) {
      toast.error('Please select a location first');
      return;
    }
    
    setLoadingReservation(true);
    
    try {
      // Calculate campaign duration from start/end dates
      let campaignDays = 7;
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        campaignDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
      }
      
      // Map languages to backend format (filter out 'all')
      const languageFilter = formData.languages.includes('all') 
        ? [] 
        : formData.languages;
      
      const response = await fetch(`${API_URL}/api/meta/reach-estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location_name: formData.locationName || 'Nigeria',
          location_type: formData.locationType || 'region',
          daily_reach: formData.dailyReach || 0,
          target_reach: formData.reservationTargetReach,
          campaign_duration_days: campaignDays,
          frequency_cap: formData.reservationFrequencyTimes,
          frequency_cap_interval: formData.reservationFrequencyDays,
          // Age targeting
          age_min: parseInt(formData.minAge) || 18,
          age_max: parseInt(formData.maxAge) || 65,
          // Gender targeting (ALL, MALE, FEMALE)
          gender: formData.gender || 'ALL',
          // Language targeting
          languages: languageFilter,
          // Prediction mode: 2 = reach-based (recalculate budget based on reach target)
          prediction_mode: 2,
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setReservationEstimates(data);
        // Auto-fill budget amount with the reservation estimate
        updateField('budgetAmount', Math.round(data.budget_naira).toString());
      } else {
        toast.error('Failed to fetch domination estimates');
      }
    } catch (error) {
      console.error('Reservation estimate error:', error);
      toast.error('Error fetching domination estimates');
    } finally {
      setLoadingReservation(false);
    }
  };
  
  // Fetch estimates when Reserved mode is selected and any filter changes
  // This ensures 100% parity with Meta Ads Manager - every filter change triggers a new API call
  useEffect(() => {
    if (formData.orderType === 'RESERVED' && (formData.locationName || formData.latitude)) {
      fetchReservationEstimates();
    }
  }, [
    formData.orderType, 
    formData.locationName, 
    formData.dailyReach, 
    formData.locationType, 
    formData.reservationTargetReach, 
    formData.reservationFrequencyTimes, 
    formData.reservationFrequencyDays, 
    formData.startDate, 
    formData.endDate,
    // NEW: Filter changes that trigger API refresh
    formData.minAge,
    formData.maxAge,
    formData.gender,
    JSON.stringify(formData.languages), // Stringify array for proper comparison
  ]);

  const validateForm = () => {
    const errors = [];
    if (!formData.campaignName) errors.push('Campaign name is required');
    if (!formData.objective) errors.push('Campaign objective is required');
    if (!formData.budgetAmount || parseFloat(formData.budgetAmount) <= 0) errors.push('Valid budget amount is required');
    if (!formData.startDate) errors.push('Start date is required');
    if (formData.budgetType === 'lifetime' && !formData.endDate) errors.push('End date is required for lifetime budget');
    if (!formData.latitude || !formData.longitude) errors.push('Please select a location on the globe');
    
    // Creative validation depends on source
    if (creativeSource === 'existing') {
      // Using existing post
      if (!selectedExistingPost) errors.push('Please select a post to promote');
    } else {
      // Creating new creative
      if (!formData.primaryText) errors.push('Primary text is required');
      if (!formData.headline) errors.push('Headline is required');
      if (!formData.destinationUrl) errors.push('Destination URL is required');
    }
    // Image is optional - can create link ads without image
    return errors;
  };

  const handleSnapchatSubmit = async () => {
    const errors = validateSnapchatForm(formData);
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return;
    }
    if (!snap.connection?.connected || !snap.selectedAccount) {
      toast.error('Please connect Snapchat and select an ad account first');
      return;
    }
    if (!snap.selectedProfileId || snap.selectedProfileId.length < 8) {
      toast.error('Snapchat requires a Public Profile', { description: 'Select or paste your Public Profile ID in the Ad Account section (New Campaign tab).' });
      setActiveSection('campaign');
      return;
    }
    setIsSubmitting(true);
    setSubmissionProgress('Connecting to Snapchat Marketing API...');
    try {
      const result = await snap.submitCampaign(formData, setSubmissionProgress);
      setSnapSubmitResult(result);
      if (result.status === 'success') {
        setSubmissionProgress('Campaign created on Snapchat!');
        toast.success('Campaign submitted to Snapchat!');
      } else if (result.status === 'partial') {
        toast.warning('Snapchat campaign partially created. Some steps failed.');
      } else {
        toast.error(`Snapchat submission failed: ${result.errors?.join(', ') || 'Unknown error'}`);
      }
      snap.fetchCampaigns(snap.selectedAccount.id);
    } catch (error) {
      toast.error('Snapchat submission error', { description: error.message });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmissionProgress(''), 1000);
    }
  };

  const handleActivateSnapCampaign = async () => {
    if (!snapSubmitResult?.campaign_id) return;
    setIsActivatingSnap(true);
    try {
      await snap.activateCampaign(snapSubmitResult.campaign_id);
      toast.success('Campaign activated! Snapchat will review your ad before it starts running.');
      setSnapSubmitResult(null);
      setActiveSection('campaigns');
      snap.fetchCampaigns(snap.selectedAccount?.id);
    } catch (error) {
      toast.error('Activation failed', { description: error.message });
    } finally {
      setIsActivatingSnap(false);
    }
  };

  const handleSubmit = async () => {
    if (isSnapchat) return handleSnapchatSubmit();
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return;
    }
    
    // Check if connected to Meta
    if (!connectedAccount?.accessToken || !formData.selectedAdAccountId) {
      toast.error('Please connect a Meta Ad Account first');
      return;
    }
    
    if (!formData.selectedPageId) {
      toast.error('Please select a Facebook Page for your ad');
      return;
    }
    
    setIsSubmitting(true);
    setSubmissionProgress('Connecting to Marketing API...');
    
    // Simulate a brief delay to show the initial message
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Build targeting spec
    const targeting = {
      geo_locations: {
        custom_locations: [{
          latitude: formData.latitude,
          longitude: formData.longitude,
          radius: formData.radius,
          distance_unit: 'kilometer',
        }]
      },
      age_min: parseInt(formData.minAge) || 18,
      age_max: parseInt(formData.maxAge) || 65,
    };
    
    // Add gender targeting if not ALL
    if (formData.gender !== 'ALL') {
      targeting.genders = formData.gender === 'MALE' ? [1] : [2];
    }
    
    // Add locales if specific language selected
    if (formData.languages && !formData.languages.includes('all')) {
      // Meta uses locale codes
      const localeMap = {
        'english': 6, 'hausa': 1001, 'yoruba': 1002, 'igbo': 1003
      };
      targeting.locales = formData.languages.map(l => localeMap[l]).filter(Boolean);
    }
    
    // Get image URL if we have an uploaded image
    let imageUrl = null;
    if (formData.imagePreview) {
      // If it's a blob URL, we need the actual uploaded URL
      // The imagePreview should be the uploaded URL from the server
      imageUrl = formData.imagePreview.startsWith('blob:') ? null : formData.imagePreview;
      // If we have an uploaded image URL from api.js, use it
      if (formData.imageUrl) {
        imageUrl = formData.imageUrl;
      }
    }
    
    try {
      // Update progress - Creating Campaign
      setSubmissionProgress('Creating campaign...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update progress - Submitting to API
      setSubmissionProgress('Submitting campaign to Meta...');
      
      // Submit complete ad to Meta
      const response = await fetch(`${API_URL}/api/meta/submit-ad`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: connectedAccount.accessToken,
          ad_account_id: formData.selectedAdAccountId,
          page_id: formData.selectedPageId,
          
          // Campaign
          campaign_name: formData.campaignName,
          objective: mapObjectiveToMeta(formData.objective),
          special_ad_categories: formData.specialAdCategory === 'NONE' ? [] : [formData.specialAdCategory],
          buying_type: formData.orderType || 'AUCTION',
          
          // Ad Set
          adset_name: formData.adSetName || `${formData.campaignName} - Ad Set`,
          daily_budget: formData.budgetType === 'daily' ? Math.round(parseFloat(formData.budgetAmount) * 100) : null,
          lifetime_budget: formData.budgetType === 'lifetime' ? Math.round(parseFloat(formData.budgetAmount) * 100) : null,
          start_time: new Date(formData.startDate).toISOString(),
          end_time: formData.endDate ? new Date(formData.endDate).toISOString() : null,
          optimization_goal: formData.optimizationGoal || OPTIMIZATION_GOALS[formData.objective]?.[0]?.value || 'REACH',
          billing_event: 'IMPRESSIONS',
          bid_strategy: formData.bidStrategy || 'LOWEST_COST_WITHOUT_CAP',
          
          // Targeting
          targeting: targeting,
          
          // Creative
          primary_text: creativeSource === 'new' ? formData.primaryText : null,
          headline: creativeSource === 'new' ? formData.headline : null,
          description: formData.description,
          link_url: formData.destinationUrl,
          call_to_action: formData.ctaType || 'LEARN_MORE',
          image_url: creativeSource === 'new' ? imageUrl : null,
          
          // Existing post (bypasses development mode restriction)
          existing_post_id: creativeSource === 'existing' && selectedExistingPost ? selectedExistingPost.id : null,
        })
      });
      
      const result = await response.json();
      
      // Update progress based on completed steps
      if (result.steps_completed?.includes('campaign')) {
        setSubmissionProgress('Campaign created! Creating ad set...');
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      if (result.steps_completed?.includes('adset')) {
        setSubmissionProgress('Ad set created! Creating creative...');
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      if (result.steps_completed?.includes('creative')) {
        setSubmissionProgress('Creative created! Finalizing ad...');
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      if (result.status === 'success') {
        setSubmissionProgress('Ad submitted successfully!');
        toast.success('Ad submitted successfully to Meta!');
        
        // Add to local campaigns list
        const newCampaign = {
          id: result.campaign_id,
          name: formData.campaignName,
          status: 'PAUSED',
          objective: formData.objective,
          budget: parseFloat(formData.budgetAmount) || 0,
          daily_budget: formData.budgetType === 'daily' ? parseFloat(formData.budgetAmount) || 0 : 0,
          lifetime_budget: formData.budgetType === 'lifetime' ? parseFloat(formData.budgetAmount) || 0 : 0,
          spent: 0,
          reach: 0,
          impressions: 0,
          clicks: 0,
          likes: 0,
          comments: 0,
          shares: 0,
          ctr: 0,
          created_time: new Date().toISOString(),
          start_time: formData.startDate,
          end_time: formData.endDate,
          location: formData.locationName || 'Nigeria',
          buying_type: formData.orderType,
          adset_id: result.adset_id,
          creative_id: result.creative_id,
          ad_id: result.ad_id,
          ad_account_id: formData.selectedAdAccountId,
          ad_account_name: adAccounts.find(a => a.id === formData.selectedAdAccountId)?.name || formData.selectedAdAccountName,
        };
        setCampaigns(prev => [newCampaign, ...prev.filter(c => c.id !== newCampaign.id)]);
        
        // Build submitted data for success modal
        setSubmittedData({
          campaign: {
            id: result.campaign_id,
            name: formData.campaignName,
            objective: formData.objective,
          },
          adset: {
            id: result.adset_id,
            budget: formData.budgetAmount,
            budget_type: formData.budgetType,
          },
          creative: {
            id: result.creative_id,
          },
          ad: {
            id: result.ad_id,
          },
          meta_results: result,
          ad_account: { id: formData.selectedAdAccountId, name: adAccounts.find(a => a.id === formData.selectedAdAccountId)?.name || formData.selectedAdAccountName },
          connected_user: connectedAccount?.name,
        });
        
        setShowSuccessModal(true);
        
        // Refresh campaigns list, keeping the new campaign visible even if Meta hasn't indexed it yet
        setTimeout(async () => {
          await fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken, true);
          setCampaigns(prev => prev.some(c => c.id === newCampaign.id) ? prev : [newCampaign, ...prev]);
        }, 2000);
        
      } else if (result.status === 'partial') {
        toast.warning('Ad partially created. Some steps failed.');
        setShowSuccessModal(true);
        setSubmittedData({ meta_results: result, partial: true, ad_account: { id: formData.selectedAdAccountId, name: adAccounts.find(a => a.id === formData.selectedAdAccountId)?.name || formData.selectedAdAccountName }, connected_user: connectedAccount?.name });
      } else {
        toast.error(`Ad submission failed: ${result.errors?.join(', ') || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error('Ad submission error:', error);
      toast.error(`Submission error: ${error.message}`);
      setSubmissionProgress('');
    } finally {
      setIsSubmitting(false);
      // Clear progress after a short delay on success
      setTimeout(() => setSubmissionProgress(''), 1000);
    }
  };
  
  // Activate a submitted ad (starts Meta review process)
  const activateAd = async (adId, adsetId, campaignId) => {
    if (!connectedAccount?.accessToken) {
      toast.error('Not connected to Meta');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/meta/activate-ad?ad_id=${adId}&adset_id=${adsetId}&campaign_id=${campaignId}&access_token=${connectedAccount.accessToken}`,
        { method: 'POST' }
      );
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Ad activated! Meta will review it before serving.');
        // Update local state
        setCampaigns(prev => prev.map(c => 
          c.id === campaignId ? { ...c, status: 'PENDING_REVIEW' } : c
        ));
        // Refresh campaigns
        fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken);
      } else {
        toast.error(`Activation failed: ${result.errors?.join(', ')}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const resetForm = () => {
    // Revoke any existing video URL to free memory
    if (formData.videoPreview && formData.videoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.videoPreview);
    }
    setFormData({
      campaignName: '',
      objective: '',
      selectedPageId: '',
      selectedAdAccountId: '',
      orderType: 'AUCTION',
      postUpInfluencers: [],
      postUpContentUrl: '',
      specialAdCategory: 'NONE',
      adSetName: '',
      budgetType: 'daily',
      budgetAmount: '',
      startDate: '',
      endDate: '',
      optimizationGoal: '',
      bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
      latitude: null,
      longitude: null,
      radius: 1,
      locationName: '',
      locationType: 'region',
      dailyReach: 0,
      minAge: '18',
      maxAge: '65',
      gender: 'ALL',
      languages: ['all'],
      primaryText: '',
      headline: '',
      description: '',
      destinationUrl: '',
      ctaType: 'LEARN_MORE',
      imageFile: null,
      imagePreview: null,
      videoFile: null,
      videoPreview: null,
      mediaType: 'image',
    });
    setShowSuccessModal(false);
    setSubmittedData(null);
  };

  // Sections - exclude settings in review mode
  const allSections = [
    { id: 'campaigns', label: 'Campaigns', icon: BarChart3, color: 'from-cyan-500 to-cyan-600' },
    { id: 'insights', label: 'Page Post Insights', icon: Eye, color: 'from-pink-500 to-rose-600' },
    { id: 'campaign', label: 'New Campaign', icon: Target, color: 'from-blue-500 to-blue-600' },
    { id: 'targeting', label: 'Target Audience', icon: Users, color: 'from-green-500 to-green-600' },
    { id: 'budget', label: 'Budget', icon: DollarSign, color: 'from-amber-500 to-amber-600' },
    { id: 'creative', label: 'Creative', icon: ImageIcon, color: 'from-purple-500 to-purple-600' },
    { id: 'settings', label: 'Settings', icon: Settings, color: 'from-gray-500 to-gray-600' },
  ];
  
  // Filter out settings in review mode
  const sections = metaReviewMode 
    ? allSections.filter(s => s.id !== 'settings')
    : allSections;

  // Toggle platform enabled state
  const togglePlatformEnabled = (platformId) => {
    setPlatformSettings(prev => ({
      ...prev,
      [platformId]: { ...prev[platformId], enabled: !prev[platformId].enabled }
    }));
  };

  // Connect platform account (simulated)
  const connectPlatformAccount = async (platformId) => {
    // For now, simulate connection. Later integrate real OAuth for each platform
    if (platformId === 'facebook' || platformId === 'instagram') {
      // Use existing Meta login for both Facebook and Instagram
      setShowMetaLoginModal(true);
      return;
    }
    
    // Handle Google Ads OAuth with popup window
    if (platformId === 'google' || platformId === 'youtube') {
      try {
        toast.info('Connecting to Google Ads...', { description: 'Opening Google login...' });
        
        // Call backend to get the OAuth URL
        const response = await fetch(`${API_URL}/api/google-ads/auth`);
        const data = await response.json();
        
        if (data.auth_url) {
          // Open popup window for OAuth
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          
          const popup = window.open(
            data.auth_url,
            'Google Ads Login',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );
          
          // Listen for popup close and check for connection
          const checkPopupClosed = setInterval(async () => {
            if (popup && popup.closed) {
              clearInterval(checkPopupClosed);
              
              // Check if connection was successful by fetching status
              await fetchGoogleAdsStatus();
              
              // Check if now connected
              const statusResponse = await fetch(`${API_URL}/api/google-ads/connection-status`);
              const statusData = await statusResponse.json();
              
              if (statusData.connected) {
                toast.success('Google Ads Connected!', {
                  description: `Connected as ${statusData.user_email || 'your account'}`
                });
              }
            }
          }, 500);
          
          // Clear interval after 5 minutes to prevent memory leak
          setTimeout(() => clearInterval(checkPopupClosed), 300000);
          
        } else if (data.detail) {
          toast.error('Google Ads Not Configured', {
            description: data.detail
          });
        } else {
          toast.error('Failed to start Google Ads login');
        }
      } catch (error) {
        console.error('Google Ads auth error:', error);
        toast.error('Connection Error', {
          description: 'Failed to connect to Google Ads. Please try again.'
        });
      }
      return;
    }
    
    // Simulate connection for other platforms
    toast.info(`${AD_PLATFORMS.find(p => p.id === platformId)?.name} integration coming soon!`, {
      description: 'This platform will be available after API approval.'
    });
  };

  // Get platform display name
  const getPlatformName = (platformId) => {
    return AD_PLATFORMS.find(p => p.id === platformId)?.name || platformId;
  };

  // Get the Ads Manager name for each platform
  const getPlatformAdsManagerName = (platformId) => {
    const adsManagerNames = {
      facebook: 'Meta Ads Manager',
      instagram: 'Meta Ads Manager',
      google: 'Google Ads Manager',
      youtube: 'Google Ads Manager',
      tiktok: 'TikTok Ads Manager',
      snapchat: 'Snapchat Ads Manager',
      twitter: 'X Ads Manager'
    };
    return adsManagerNames[platformId] || `${getPlatformName(platformId)} Ads Manager`;
  };

  // Get active platforms count
  const getActivePlatformsCount = () => {
    return Object.values(platformSettings).filter(p => p.enabled).length;
  };

  return (
    <div className="h-screen w-full flex bg-slate-900 overflow-hidden relative">
      {/* Globe - Full Screen Background */}
      <div className="absolute inset-0">
        <iframe
          ref={iframeRef}
          src={`/meta-ads-globe.html${metaReviewMode ? '?reviewMode=true' : ''}`}
          className="w-full h-full border-0"
          title="AdGlobe 3D"
        />
      </div>

      {/* Toggle Button when sidebar is closed */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="absolute top-4 right-4 z-30 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white p-3 rounded-xl shadow-lg shadow-blue-500/30 transition-all duration-300 flex items-center gap-2 group"
        >
          <span className="text-sm font-medium">Open Panel</span>
          <PanelRight className="h-5 w-5" />
        </button>
      )}

      {/* Floating Platform Selector */}
      {sidebarOpen && showPlatformBar && (
        <div className="absolute right-0 top-[70px] w-1/2 z-30 px-4">
          <div className="bg-slate-800/95 backdrop-blur-lg rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
            {/* Collapse Handle */}
            <button 
              onClick={() => setShowPlatformBar(false)}
              data-testid="platform-bar-collapse-btn"
              className="w-full flex justify-center py-2 hover:bg-white/5 transition-colors"
            >
              <ChevronUp className="h-5 w-5 text-white/40" />
            </button>
            
            {/* Header */}
            <div className="text-center pb-3 px-4">
              <h2 className="text-white font-bold text-base">PUBLISH ACROSS PLATFORMS</h2>
              <p className="text-white/50 text-xs mt-1">Deploy your campaign to multiple platforms</p>
            </div>
            
            {/* Platform Icons */}
            <div className="flex justify-center gap-4 px-6 pb-5">
              {AD_PLATFORMS.map((platform) => {
                const isActive = activePlatform === platform.id;
                const isEnabled = platformSettings[platform.id]?.enabled;
                const isConnected = (platform.id === 'facebook' || platform.id === 'instagram') 
                  ? isMetaConnected 
                  : (platform.id === 'google' || platform.id === 'youtube') 
                    ? googleAdsConnection?.connected 
                    : platform.id === 'snapchat'
                      ? snap.connection?.connected
                      : platformSettings[platform.id]?.connected;
                
                return (
                  <button
                    key={platform.id}
                    data-testid={`platform-tile-${platform.id}`}
                    onClick={() => {
                      if (isEnabled) {
                        setActivePlatform(platform.id);
                      } else {
                        toast.info(`Enable ${platform.name} in Settings to use it`);
                      }
                    }}
                    className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all ${
                      isActive 
                        ? 'bg-white/10 ring-2 ring-white/30 scale-105'
                        : isEnabled
                          ? 'hover:bg-white/5'
                          : 'opacity-30'
                    }`}
                  >
                    <div className="relative">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden ${
                        isActive ? 'ring-2 ring-white/40' : ''
                      }`} style={{ backgroundColor: platform.id === 'facebook' ? '#1877F2' : platform.id === 'instagram' ? '#E4405F' : platform.id === 'tiktok' ? '#000' : platform.id === 'snapchat' ? '#FFFC00' : platform.id === 'youtube' ? '#FF0000' : platform.id === 'twitter' ? '#000' : '#fff' }}>
                        {/* Facebook icon */}
                        {platform.icon === 'facebook' ? (
                          <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        ) : platform.icon === 'instagram' ? (
                          /* Instagram icon */
                          <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                          </svg>
                        ) : platform.icon === 'twitter-x' ? (
                          /* X (Twitter) logo */
                          <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        ) : (
                          <img 
                            src={platform.icon} 
                            alt={platform.name}
                            className="w-10 h-10"
                          />
                        )}
                      </div>
                      {isConnected && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-800 flex items-center justify-center">
                          <CheckCircle className="h-2.5 w-2.5 text-white" />
                        </div>
                      )}
                    </div>
                    <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-white/60'}`}>
                      {platform.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Sidebar - Right Side (Fixed 50% width) */}
      {sidebarOpen && (
        <div 
          ref={sidebarRef}
          className="absolute right-0 top-0 h-full w-1/2 flex flex-col z-20 shadow-2xl shadow-black/50"
        >
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 opacity-98" />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10" />
          
          {/* Content */}
          <div className="relative flex flex-col h-full">
            {/* Header */}
            <div className="px-5 py-3 border-b border-white/10 bg-gradient-to-r from-blue-600/10 to-purple-600/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Back Arrow - Hidden in review mode */}
                  {!metaReviewMode && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => navigate('/admin')}
                      className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  )}
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center`} style={{ backgroundColor: AD_PLATFORMS.find(p => p.id === activePlatform)?.color }}>
                      {/* Platform icon - SVG for Facebook/Instagram/Twitter, image for others */}
                      {activePlatform === 'facebook' ? (
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      ) : activePlatform === 'instagram' ? (
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                        </svg>
                      ) : activePlatform === 'twitter' ? (
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      ) : (
                        <img 
                          src={AD_PLATFORMS.find(p => p.id === activePlatform)?.icon}
                          alt={activePlatform}
                          className="w-6 h-6"
                        />
                      )}
                    </div>
                    <div>
                      <h1 className="text-base font-bold text-white">
                        {getPlatformName(activePlatform)} Campaign
                      </h1>
                      <p className="text-xs text-white/50">
                        {AD_PLATFORMS.find(p => p.id === activePlatform)?.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Connected Account Indicator */}
                  {(activePlatform === 'facebook' || activePlatform === 'instagram') && isMetaConnected && connectedAccount && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 border border-blue-500/30">
                      <img 
                        src={connectedAccount.profilePicture} 
                        alt={connectedAccount.name}
                        className="w-5 h-5 rounded-full"
                      />
                      <span className="text-xs text-white/60">Connected Account:</span>
                      <span className="text-xs text-white font-medium">{connectedAccount.name}</span>
                      <button
                        onClick={handleDisconnectMeta}
                        className="p-0.5 rounded-full hover:bg-white/10 text-white/50 hover:text-white"
                        title="Disconnect"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {/* Google Ads Connected Indicator */}
                  {isSnapchat && snap.connection?.connected && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFFC00]/15 border border-[#FFFC00]/30" data-testid="snapchat-header-indicator">
                      <div className="w-5 h-5 rounded-full bg-[#FFFC00] text-black flex items-center justify-center">
                        <SnapchatGhostIcon className="h-3 w-3" />
                      </div>
                      <span className="text-xs text-white/80 font-medium max-w-[140px] truncate">{snap.selectedAccount?.name || snap.connection.display_name || 'Connected'}</span>
                      <button onClick={snap.disconnect} className="p-0.5 rounded-full hover:bg-white/10 text-white/50 hover:text-white" title="Disconnect">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {(activePlatform === 'google' || activePlatform === 'youtube') && googleAdsConnection?.connected && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-white">G</span>
                      </div>
                      <span className="text-xs text-white/80 font-medium max-w-[100px] truncate">{googleAdsConnection.user_email || 'Connected'}</span>
                      <button
                        onClick={handleDisconnectGoogle}
                        className="p-0.5 rounded-full hover:bg-white/10 text-white/50 hover:text-white"
                        title="Disconnect"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSidebarOpen(false)}
                    className="h-8 w-8 text-white/50 hover:text-white hover:bg-white/10"
                  >
                    <PanelRightClose className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Platform Bar Toggle - Hidden in review mode */}
              {!showPlatformBar && !metaReviewMode && (
                <button 
                  onClick={() => setShowPlatformBar(true)}
                  className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-white/60 hover:text-white"
                >
                  <span className="text-xs font-medium">Show Platforms</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              )}
            </div>

            {/* Persistent Meta Ad Account bar - visible across every form step */}
            {(activePlatform === 'facebook' || activePlatform === 'instagram') && isMetaConnected && connectedAccount && (
              <div
                className={`flex items-center gap-3 px-4 py-2 border-b ${formData.selectedAdAccountId ? 'bg-green-500/10 border-green-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}
                data-testid="meta-ad-account-bar"
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${formData.selectedAdAccountId ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
                  {formData.selectedAdAccountId ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Building2 className="h-4 w-4 text-amber-300" />}
                </div>
                <div className="min-w-0 flex-1 leading-tight">
                  {formData.selectedAdAccountId ? (
                    <p className="text-sm truncate" data-testid="meta-ad-account-bar-value">
                      <span className="text-white/50">Selected ad account:</span>{' '}
                      <span className="font-medium text-green-300">{adAccounts.find(a => a.id === formData.selectedAdAccountId)?.name || formData.selectedAdAccountName || 'Ad Account'}</span>
                      <span className="mx-2 text-white/20">|</span>
                      <span className="text-white/50">ID</span>{' '}
                      <span className="font-mono text-xs text-white">{formData.selectedAdAccountId.startsWith('act_') ? formData.selectedAdAccountId : `act_${formData.selectedAdAccountId}`}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-white" data-testid="meta-ad-account-bar-empty">
                      <span className="text-amber-200/80">Selected ad account:</span> none — choose one in the New Campaign step
                    </p>
                  )}
                </div>
                {activeSection !== 'campaign' && (
                  <button
                    type="button"
                    onClick={() => setActiveSection('campaign')}
                    className="text-xs text-green-300 hover:text-white underline flex-shrink-0"
                    data-testid="meta-ad-account-bar-change"
                  >
                    Change
                  </button>
                )}
              </div>
            )}

            {/* Section Navigation */}
            <div className="flex gap-1 p-3 border-b border-white/10 bg-black/20">
              {sections.map((section) => (
                <button
                  key={section.id}
                  data-testid={`section-tab-${section.id}`}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    activeSection === section.id 
                      ? `bg-gradient-to-r ${section.color} text-white shadow-lg` 
                      : 'text-white/50 hover:bg-white/10 hover:text-white/80'
                  }`}
                >
                  <section.icon className="h-3.5 w-3.5" />
                  {section.label}
                </button>
              ))}
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar">
              
              {/* API Error Display */}
              {metaApiError && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-medium">API Error</span>
                  </div>
                  <p className="text-xs text-red-300/70 mt-1">{metaApiError}</p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-2 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10"
                    onClick={() => {
                      const token = connectedAccount?.accessToken || localStorage.getItem('meta_access_token');
                      if (token) fetchAllMetaData(token);
                    }}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Retry
                  </Button>
                </div>
              )}
              
              {/* Setup Required Warning - Show if user has no Pages or Ad Accounts */}
              {isMetaConnected && !isLoadingPages && !isLoadingAdAccounts && (facebookPages.length === 0 || adAccounts.length === 0) && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-yellow-300 text-sm">Setup Required</h3>
                      <p className="text-xs text-yellow-200/70 mt-1 mb-3">
                        To create ad campaigns, you need both a Facebook Page and an Ad Account.
                      </p>
                      <div className="space-y-2">
                        {facebookPages.length === 0 && (
                          <div className="flex items-center gap-2">
                            <X className="h-3 w-3 text-red-400" />
                            <span className="text-xs text-white/70">No Facebook Page found</span>
                            <a 
                              href="https://www.facebook.com/pages/create" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            >
                              Create Page <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                        {facebookPages.length > 0 && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-400" />
                            <span className="text-xs text-white/70">Facebook Page connected</span>
                          </div>
                        )}
                        {adAccounts.length === 0 && (
                          <div className="flex items-center gap-2">
                            <X className="h-3 w-3 text-red-400" />
                            <span className="text-xs text-white/70">No Ad Account found</span>
                            <a 
                              href="https://business.facebook.com/settings/ad-accounts" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            >
                              Create Account <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                        {adAccounts.length > 0 && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-400" />
                            <span className="text-xs text-white/70">Ad Account connected</span>
                          </div>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-3 text-xs border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/10"
                        onClick={() => {
                          const token = connectedAccount?.accessToken || localStorage.getItem('meta_access_token');
                          if (token) fetchAllMetaData(token);
                        }}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" /> Refresh Data
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Connection Loading Screen - Shown after successful Meta login */}
              {isConnectingToMeta && (
                <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-sm z-50 flex items-center justify-center rounded-xl">
                  <div className="text-center space-y-6 p-8">
                    <div className="relative mx-auto w-20 h-20">
                      <div className="absolute inset-0 rounded-full border-4 border-blue-500/20"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Facebook className="h-8 w-8 text-blue-400" />
                      </div>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-lg mb-2">{connectionProgress}</p>
                      <p className="text-white/50 text-sm">Setting up your Meta Ads workspace...</p>
                    </div>
                    <div className="flex justify-center gap-1">
                      {connectionProgressMessages.map((_, index) => (
                        <div 
                          key={index}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            connectionProgressMessages.indexOf(connectionProgress) >= index 
                              ? 'bg-blue-500' 
                              : 'bg-white/20'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Snapchat Campaigns List (Snapchat Marketing API) */}
              {activeSection === 'campaigns' && isSnapchat && (
                <SnapchatCampaignsPanel snap={snap} onNewCampaign={() => setActiveSection('campaign')} />
              )}

              {/* Campaigns List Section - for ads_read permission */}
              {activeSection === 'campaigns' && !isSnapchat && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                        <BarChart3 className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-white">My Campaigns</h2>
                        <p className="text-xs text-white/50">View and manage your ad campaigns</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          const token = connectedAccount?.accessToken || localStorage.getItem('meta_access_token');
                          const adAccountId = formData.selectedAdAccountId || adAccounts[0]?.id;
                          if (token && adAccountId) {
                            await fetchCampaigns(token, adAccountId);
                            toast.success('Campaigns refreshed');
                          }
                        }}
                        className="border-white/20 text-white/70 hover:text-white hover:bg-white/10 text-xs"
                        disabled={isLoadingCampaigns}
                      >
                        <RefreshCw className={`h-3 w-3 mr-1 ${isLoadingCampaigns ? 'animate-spin' : ''}`} /> Refresh
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setActiveSection('campaign')}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs"
                      >
                        + New Campaign
                      </Button>
                    </div>
                  </div>

                  {/* Source Indicator */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <Facebook className="h-4 w-4 text-blue-400" />
                    <span className="text-xs text-white/60">Source:</span>
                    <span className="text-xs text-blue-400 font-medium">Meta Marketing API</span>
                  </div>

                  {/* Date Range Selector - Controls the time period for metrics */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-white/10">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/70">Insights Period:</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedDateRange}
                        onChange={async (e) => {
                          const newRange = e.target.value;
                          setSelectedDateRange(newRange);
                          // Auto-refresh campaigns with new date range if already imported
                          if (campaignsImported) {
                            const token = connectedAccount?.accessToken || localStorage.getItem('meta_access_token');
                            const adAccountId = formData.selectedAdAccountId;
                            if (token && adAccountId) {
                              await fetchCampaigns(token, adAccountId, newRange);
                              toast.success(`Metrics updated to ${dateRangeOptions.find(o => o.value === newRange)?.label || newRange}`);
                            }
                          }
                        }}
                        className="bg-slate-800 border border-white/20 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                        disabled={isLoadingCampaigns}
                        data-testid="insights-date-range-select"
                      >
                        {dateRangeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {isLoadingCampaigns && (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                      )}
                    </div>
                  </div>

                  {/* Campaign Stats Summary - Only show real data after import */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                      <div className="text-2xl font-bold text-green-400">
                        {campaignsImported ? campaigns.filter(c => c.status === 'ACTIVE').length : 0}
                      </div>
                      <div className="text-xs text-green-300/70">Active Campaigns</div>
                    </div>
                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
                      <div className="text-2xl font-bold text-blue-400">
                        ₦{campaignsImported ? campaigns.reduce((sum, c) => sum + (c.spent || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'}
                      </div>
                      <div className="text-xs text-blue-300/70">Total Spent</div>
                    </div>
                    <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                      <div className="text-2xl font-bold text-purple-400">
                        {campaignsImported ? campaigns.reduce((sum, c) => sum + (c.reach || 0), 0).toLocaleString() : '0'}
                      </div>
                      <div className="text-xs text-purple-300/70">Total Reach</div>
                    </div>
                  </div>

                  {/* Refresh Campaigns Button */}
                  {connectedAccount?.accessToken && formData.selectedAdAccountId && (
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken)}
                        disabled={isLoadingCampaigns}
                        className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                      >
                        {isLoadingCampaigns ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Refresh Campaigns
                      </Button>
                    </div>
                  )}

                  {/* Campaign List */}
                  <div className="space-y-3">
                    {!isMetaConnected ? (
                      <div className="p-8 text-center rounded-xl bg-gradient-to-br from-blue-500/5 to-indigo-500/5 border border-blue-500/20">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center">
                          <svg className="h-8 w-8 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </div>
                        <h3 className="text-white font-semibold text-lg mb-2">Connect Your Meta Account</h3>
                        <p className="text-white/50 text-sm mb-4 max-w-sm mx-auto">
                          To create and manage ad campaigns, connect your Facebook or Meta Business account
                        </p>
                        <Button
                          onClick={() => setShowMetaLoginModal(true)}
                          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                          data-testid="connect-account-btn"
                        >
                          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                          Connect Account
                        </Button>
                      </div>
                    ) : !campaignsImported ? (
                      /* Connected but campaigns not imported yet - Show Page/Ad Account Selection */
                      <div className="p-6 rounded-xl bg-gradient-to-br from-green-500/5 to-emerald-500/5 border border-green-500/20">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-green-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-semibold">Account Connected</h3>
                            <p className="text-white/50 text-sm">Select a page and ad account to import your campaigns</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          {/* Page Selection */}
                          <div className="space-y-2">
                            <Label className="text-white/80 text-sm font-medium">Facebook Page *</Label>
                            <Select
                              value={formData.selectedPageId || ''}
                              onValueChange={(value) => {
                                const selectedPage = facebookPages.find(p => p.id === value);
                                setFormData(prev => ({ 
                                  ...prev, 
                                  selectedPageId: value,
                                  selectedPageName: selectedPage?.name || ''
                                }));
                              }}
                            >
                              <SelectTrigger className="bg-white/5 border-white/20 text-white">
                                <SelectValue placeholder="Select a page" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-white/20">
                                {facebookPages.map((page) => (
                                  <SelectItem key={page.id} value={page.id} className="text-white hover:bg-white/10">
                                    <div className="flex items-center gap-2">
                                      {page.picture && (
                                        <img src={page.picture} alt="" className="w-5 h-5 rounded-full" />
                                      )}
                                      <span>{page.name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                                {facebookPages.length === 0 && (
                                  <SelectItem value="none" disabled className="text-white/50">
                                    No pages found
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Ad Account Selection */}
                          <div className="space-y-2">
                            <Label className="text-white/80 text-sm font-medium">Ad Account *</Label>
                            <Select
                              value={formData.selectedAdAccountId || ''}
                              onValueChange={(value) => {
                                const selectedAccount = adAccounts.find(a => a.id === value);
                                setFormData(prev => ({ 
                                  ...prev, 
                                  selectedAdAccountId: value,
                                  selectedAdAccountName: selectedAccount?.name || ''
                                }));
                              }}
                            >
                              <SelectTrigger className="bg-white/5 border-white/20 text-white">
                                <SelectValue placeholder="Select an ad account" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-white/20">
                                {adAccounts.map((account) => (
                                  <SelectItem key={account.id} value={account.id} className="text-white hover:bg-white/10">
                                    <div className="flex items-center gap-2">
                                      <span>{account.name}</span>
                                      <span className="text-white/40 text-xs">({account.currency})</span>
                                    </div>
                                  </SelectItem>
                                ))}
                                {adAccounts.length === 0 && (
                                  <SelectItem value="none" disabled className="text-white/50">
                                    No ad accounts found
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <Button
                          onClick={async () => {
                            if (formData.selectedAdAccountId && formData.selectedPageId && connectedAccount?.accessToken) {
                              // Set loading state with progress messages
                              setIsLoadingCampaigns(true);
                              
                              // Animate through progress messages (3 seconds total)
                              for (let i = 0; i < importProgressMessages.length; i++) {
                                setImportProgress(importProgressMessages[i]);
                                await new Promise(resolve => setTimeout(resolve, 750)); // 750ms per message
                              }
                              
                              // Fetch campaigns
                              await fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken, true);
                              setCampaignsImported(true);
                              setIsLoadingCampaigns(false);
                              setImportProgress('');
                            } else {
                              toast.error('Please select both a page and ad account');
                            }
                          }}
                          disabled={!formData.selectedAdAccountId || !formData.selectedPageId || isLoadingCampaigns}
                          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white disabled:opacity-50"
                          data-testid="import-campaigns-btn"
                        >
                          {isLoadingCampaigns ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              {importProgress || 'Importing Campaigns...'}
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Import Campaigns
                            </>
                          )}
                        </Button>
                      </div>
                    ) : isLoadingCampaigns ? (
                      <div className="flex flex-col items-center justify-center p-8 space-y-4">
                        <div className="relative">
                          <div className="w-16 h-16 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin"></div>
                          <Facebook className="absolute inset-0 m-auto h-6 w-6 text-blue-400" />
                        </div>
                        <div className="text-center">
                          <p className="text-white font-medium">{importProgress || 'Loading campaigns...'}</p>
                          <p className="text-white/50 text-sm mt-1">Please wait while we fetch your data</p>
                        </div>
                      </div>
                    ) : campaigns.length === 0 ? (
                      <div className="p-8 text-center rounded-xl bg-white/5 border border-white/10">
                        <BarChart3 className="h-12 w-12 mx-auto text-white/30 mb-3" />
                        <p className="text-white/60 font-medium">No campaigns found</p>
                        <p className="text-white/40 text-sm mt-1">Create your first campaign to get started</p>
                        <Button
                          size="sm"
                          onClick={() => setActiveSection('campaign')}
                          className="mt-4 bg-gradient-to-r from-blue-600 to-indigo-600"
                        >
                          + Create Campaign
                        </Button>
                      </div>
                    ) : campaigns.map((campaign) => (
                      <div 
                        key={campaign.id}
                        className={`p-4 rounded-xl bg-white/5 border hover:bg-white/10 transition-all cursor-pointer group ${
                          highlightCampaignId === campaign.id ? 'border-green-400/70 ring-2 ring-green-400/40 bg-green-500/5' : 'border-white/10 hover:border-blue-500/30'
                        }`}
                        onClick={() => {
                          setSelectedCampaignDetails(campaign);
                          setShowCampaignDetailsModal(true);
                        }}
                        data-testid={`campaign-card-${campaign.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1" onClick={(e) => e.stopPropagation()}>
                            {/* Editing Mode */}
                            {editingCampaignId === campaign.id ? (
                              <div className="space-y-2">
                                <Input
                                  value={editingCampaignData.name || campaign.name}
                                  onChange={(e) => setEditingCampaignData(prev => ({ ...prev, name: e.target.value }))}
                                  className="bg-white/10 border-white/20 text-white text-sm h-8"
                                  placeholder="Campaign name"
                                />
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-white">{campaign.name}</h3>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    campaign.status === 'ACTIVE' 
                                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                      : campaign.status === 'IN_REVIEW'
                                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                  }`}>
                                    <span className="flex items-center gap-1">
                                      {campaign.status === 'ACTIVE' ? <Play className="h-3 w-3" /> : campaign.status === 'IN_REVIEW' ? <Clock className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                                      {campaign.status === 'IN_REVIEW' ? 'In Review' : campaign.status}
                                    </span>
                                  </span>
                                </div>
                                <div className="text-xs text-white/50 mt-1">
                                  {(campaign.objective || '').replace('OUTCOME_', '')} • Created {campaign.created_at || new Date(campaign.created_time).toLocaleDateString()}
                                  {highlightCampaignId === campaign.id && <span className="ml-2 px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 text-[10px] font-semibold uppercase">New</span>}
                                </div>
                                <div className="text-[11px] text-white/40 mt-0.5 font-mono truncate" data-testid={`campaign-meta-${campaign.id}`}>
                                  ID {campaign.id}
                                  {(() => {
                                    const accId = campaign.ad_account_id || formData.selectedAdAccountId;
                                    const accName = campaign.ad_account_name || adAccounts.find(a => a.id === accId)?.name || formData.selectedAdAccountName;
                                    return accId ? ` • Ad account ${accName ? `${accName} (` : ''}${accId.startsWith('act_') ? accId : `act_${accId}`}${accName ? ')' : ''}` : '';
                                  })()}
                                </div>
                              </>
                            )}
                          </div>
                          
                          {/* Edit Controls - Show when connected to Meta */}
                          {connectedAccount?.accessToken && (
                            <div className="flex items-center gap-2 ml-3" onClick={(e) => e.stopPropagation()}>
                              {editingCampaignId === campaign.id ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      // Update locally first
                                      setCampaigns(prev => prev.map(c => 
                                        c.id === campaign.id 
                                          ? { ...c, ...editingCampaignData }
                                          : c
                                      ));
                                      
                                      // Update via API
                                      if (connectedAccount?.accessToken) {
                                        const updateParams = new URLSearchParams({
                                          access_token: connectedAccount.accessToken
                                        });
                                        if (editingCampaignData.name !== campaign.name) {
                                          updateParams.append('name', editingCampaignData.name);
                                        }
                                        if (editingCampaignData.daily_budget && editingCampaignData.daily_budget !== campaign.daily_budget) {
                                          updateParams.append('daily_budget', Math.round(editingCampaignData.daily_budget * 100));
                                        }
                                        
                                        try {
                                          const response = await fetch(`${API_URL}/api/meta/campaigns/${campaign.id}?${updateParams.toString()}`, {
                                            method: 'PUT'
                                          });
                                          const data = await response.json();
                                          
                                          if (response.ok && data.success) {
                                            toast.success('Campaign updated successfully');
                                            // Refresh campaigns
                                            if (formData.selectedAdAccountId) {
                                              await fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken);
                                            }
                                          } else {
                                            toast.error(data.detail || 'Failed to update campaign');
                                          }
                                        } catch (error) {
                                          console.error('Error updating campaign:', error);
                                          toast.error('Failed to update campaign');
                                        }
                                      }
                                      
                                      setEditingCampaignId(null);
                                      setEditingCampaignData({});
                                    }}
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingCampaignId(null);
                                      setEditingCampaignData({});
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingCampaignId(campaign.id);
                                      setEditingCampaignData({
                                        name: campaign.name,
                                        daily_budget: campaign.daily_budget,
                                        lifetime_budget: campaign.lifetime_budget,
                                        status: campaign.status,
                                        bid_strategy: campaign.bid_strategy || 'LOWEST_COST_WITHOUT_CAP',
                                        spend_cap: campaign.spend_cap,
                                        start_time: campaign.start_time,
                                        end_time: campaign.end_time
                                      });
                                    }}
                                    title="Edit Campaign"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className={`h-7 px-2 ${campaign.status === 'ACTIVE' ? 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10' : 'text-green-400 hover:text-green-300 hover:bg-green-500/10'}`}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const newStatus = campaign.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
                                      
                                      // Update locally first
                                      setCampaigns(prev => prev.map(c => 
                                        c.id === campaign.id 
                                          ? { ...c, status: newStatus }
                                          : c
                                      ));
                                      
                                      // Update via API
                                      const success = await updateCampaignStatus(campaign.id, newStatus, connectedAccount.accessToken);
                                      if (!success) {
                                        // Revert on failure
                                        setCampaigns(prev => prev.map(c => 
                                          c.id === campaign.id 
                                            ? { ...c, status: campaign.status }
                                            : c
                                        ));
                                      } else {
                                        // Refresh campaigns to get latest data
                                        if (formData.selectedAdAccountId) {
                                          await fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken);
                                        }
                                      }
                                    }}
                                    title={campaign.status === 'ACTIVE' ? 'Pause Campaign' : 'Activate Campaign'}
                                  >
                                    {campaign.status === 'ACTIVE' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeletingCampaignId(campaign.id);
                                    }}
                                    title="Delete Campaign"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Delete Confirmation */}
                        {deletingCampaignId === campaign.id && (
                          <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30" onClick={(e) => e.stopPropagation()}>
                            <div className="text-sm text-red-400 font-medium mb-2">Delete this campaign?</div>
                            <p className="text-xs text-white/60 mb-3">This action cannot be undone. The campaign and all its ad sets and ads will be permanently deleted.</p>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-7 text-xs"
                                disabled={isDeleting}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteCampaign(campaign.id, connectedAccount.accessToken);
                                }}
                              >
                                {isDeleting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Trash2 className="h-3 w-3 mr-1" />}
                                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-white/70"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingCampaignId(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Full Edit Form (when editing) */}
                        {editingCampaignId === campaign.id && connectedAccount?.accessToken && (
                          <div className="mb-3 p-3 rounded-lg bg-white/5 border border-blue-500/30 space-y-3" onClick={(e) => e.stopPropagation()}>
                            <div className="text-xs text-blue-400 font-medium flex items-center gap-1">
                              <Edit className="h-3 w-3" /> Edit Campaign
                            </div>
                            
                            {/* Campaign Name */}
                            <div>
                              <Label className="text-white/70 text-xs mb-1 block">Campaign Name</Label>
                              <Input
                                type="text"
                                value={editingCampaignData.name || campaign.name}
                                onChange={(e) => setEditingCampaignData(prev => ({ ...prev, name: e.target.value }))}
                                className="bg-white/10 border-white/20 text-white text-sm h-8"
                                placeholder="Enter campaign name"
                              />
                            </div>
                            
                            {/* Budget Row */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-white/70 text-xs mb-1 block">Daily Budget (₦)</Label>
                                <Input
                                  type="number"
                                  value={editingCampaignData.daily_budget ?? campaign.daily_budget ?? ''}
                                  onChange={(e) => setEditingCampaignData(prev => ({ ...prev, daily_budget: parseFloat(e.target.value) || 0 }))}
                                  className="bg-white/10 border-white/20 text-white text-sm h-8"
                                  placeholder="Daily budget"
                                />
                              </div>
                              <div>
                                <Label className="text-white/70 text-xs mb-1 block">Lifetime Budget (₦)</Label>
                                <Input
                                  type="number"
                                  value={editingCampaignData.lifetime_budget ?? campaign.lifetime_budget ?? ''}
                                  onChange={(e) => setEditingCampaignData(prev => ({ ...prev, lifetime_budget: parseFloat(e.target.value) || 0 }))}
                                  className="bg-white/10 border-white/20 text-white text-sm h-8"
                                  placeholder="Lifetime budget"
                                />
                              </div>
                            </div>
                            
                            {/* Status & Bid Strategy */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-white/70 text-xs mb-1 block">Status</Label>
                                <Select
                                  value={editingCampaignData.status || campaign.status}
                                  onValueChange={(value) => setEditingCampaignData(prev => ({ ...prev, status: value }))}
                                >
                                  <SelectTrigger className="bg-white/10 border-white/20 text-white text-sm h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="ACTIVE">Active</SelectItem>
                                    <SelectItem value="PAUSED">Paused</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-white/70 text-xs mb-1 block">Bid Strategy</Label>
                                <Select
                                  value={editingCampaignData.bid_strategy || campaign.bid_strategy || 'LOWEST_COST_WITHOUT_CAP'}
                                  onValueChange={(value) => setEditingCampaignData(prev => ({ ...prev, bid_strategy: value }))}
                                >
                                  <SelectTrigger className="bg-white/10 border-white/20 text-white text-sm h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="LOWEST_COST_WITHOUT_CAP">Lowest Cost</SelectItem>
                                    <SelectItem value="LOWEST_COST_WITH_BID_CAP">Bid Cap</SelectItem>
                                    <SelectItem value="COST_CAP">Cost Cap</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            {/* Spend Cap */}
                            <div>
                              <Label className="text-white/70 text-xs mb-1 block">Spend Cap (₦) - Leave empty for no cap</Label>
                              <Input
                                type="number"
                                value={editingCampaignData.spend_cap ?? campaign.spend_cap ?? ''}
                                onChange={(e) => setEditingCampaignData(prev => ({ ...prev, spend_cap: parseFloat(e.target.value) || null }))}
                                className="bg-white/10 border-white/20 text-white text-sm h-8"
                                placeholder="Maximum total spend"
                              />
                            </div>
                            
                            {/* Schedule */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-white/70 text-xs mb-1 block">Start Date</Label>
                                <Input
                                  type="date"
                                  value={editingCampaignData.start_time ? new Date(editingCampaignData.start_time).toISOString().split('T')[0] : ''}
                                  onChange={(e) => setEditingCampaignData(prev => ({ ...prev, start_time: e.target.value }))}
                                  className="bg-white/10 border-white/20 text-white text-sm h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-white/70 text-xs mb-1 block">End Date</Label>
                                <Input
                                  type="date"
                                  value={editingCampaignData.end_time ? new Date(editingCampaignData.end_time).toISOString().split('T')[0] : ''}
                                  onChange={(e) => setEditingCampaignData(prev => ({ ...prev, end_time: e.target.value }))}
                                  className="bg-white/10 border-white/20 text-white text-sm h-8"
                                />
                              </div>
                            </div>
                            
                            {/* Campaign Info (Read-only) */}
                            <div className="pt-2 border-t border-white/10">
                              <div className="text-xs text-white/50 mb-1">Campaign Info</div>
                              <div className="flex flex-wrap gap-1">
                                <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded">
                                  Objective: {(campaign.objective || '').replace('OUTCOME_', '')}
                                </span>
                                {campaign.targeting?.geo_locations && (
                                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                                    <MapPin className="h-3 w-3 inline mr-1" />
                                    {campaign.targeting.geo_locations.countries?.join(', ') || 'Custom Location'}
                                  </span>
                                )}
                                {campaign.targeting?.age_min && (
                                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">
                                    Age: {campaign.targeting.age_min}-{campaign.targeting.age_max || 65}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Action buttons */}
                            <div className="flex justify-between pt-2 border-t border-white/10">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-white/60 hover:text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingCampaignId(null);
                                  setEditingCampaignData({});
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Build update params - only include fields that have changed
                                  const updateParams = new URLSearchParams({
                                    access_token: connectedAccount.accessToken
                                  });
                                  
                                  // Name
                                  if (editingCampaignData.name && editingCampaignData.name !== campaign.name) {
                                    updateParams.append('name', editingCampaignData.name);
                                  }
                                  // Status
                                  if (editingCampaignData.status && editingCampaignData.status !== campaign.status) {
                                    updateParams.append('status', editingCampaignData.status);
                                  }
                                  // Daily Budget - compare as numbers, handle 0 explicitly
                                  const newDailyBudget = parseFloat(editingCampaignData.daily_budget) || 0;
                                  const oldDailyBudget = parseFloat(campaign.daily_budget) || 0;
                                  if (newDailyBudget !== oldDailyBudget && newDailyBudget > 0) {
                                    updateParams.append('daily_budget', Math.round(newDailyBudget * 100));
                                  }
                                  // Lifetime Budget
                                  const newLifetimeBudget = parseFloat(editingCampaignData.lifetime_budget) || 0;
                                  const oldLifetimeBudget = parseFloat(campaign.lifetime_budget) || 0;
                                  if (newLifetimeBudget !== oldLifetimeBudget && newLifetimeBudget > 0) {
                                    updateParams.append('lifetime_budget', Math.round(newLifetimeBudget * 100));
                                  }
                                  // Bid Strategy
                                  if (editingCampaignData.bid_strategy && editingCampaignData.bid_strategy !== (campaign.bid_strategy || 'LOWEST_COST_WITHOUT_CAP')) {
                                    updateParams.append('bid_strategy', editingCampaignData.bid_strategy);
                                  }
                                  // Spend Cap - compare as numbers
                                  const newSpendCap = parseFloat(editingCampaignData.spend_cap) || 0;
                                  const oldSpendCap = parseFloat(campaign.spend_cap) || 0;
                                  if (newSpendCap !== oldSpendCap) {
                                    updateParams.append('spend_cap', newSpendCap > 0 ? Math.round(newSpendCap * 100) : 0);
                                  }
                                  
                                  // Only make API call if there are changes
                                  const hasChanges = Array.from(updateParams.keys()).length > 1; // More than just access_token
                                  
                                  if (hasChanges) {
                                    try {
                                      const response = await fetch(`${API_URL}/api/meta/campaigns/${campaign.id}?${updateParams.toString()}`, {
                                        method: 'PUT'
                                      });
                                      const data = await response.json();
                                      
                                      if (response.ok && data.success) {
                                        toast.success('Campaign updated successfully');
                                        // Refresh campaigns
                                        if (formData.selectedAdAccountId) {
                                          await fetchRealCampaigns(formData.selectedAdAccountId, connectedAccount.accessToken);
                                        }
                                      } else {
                                        toast.error(data.detail || 'Failed to update campaign');
                                      }
                                    } catch (error) {
                                      console.error('Error updating campaign:', error);
                                      toast.error('Failed to update campaign');
                                    }
                                  } else {
                                    toast.info('No changes to save');
                                  }
                                  
                                  setEditingCampaignId(null);
                                  setEditingCampaignData({});
                                }}
                              >
                                <Save className="h-3 w-3 mr-1" />
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Budget Display (when not editing) */}
                        {!(metaReviewMode && editingCampaignId === campaign.id) && (
                          <div className="flex justify-between items-center mb-3 p-2 rounded-lg bg-white/5">
                            <div>
                              <div className="text-sm font-semibold text-white">
                                ₦{(campaign.daily_budget || campaign.lifetime_budget || 0).toLocaleString()}{campaign.daily_budget ? '/day' : ''}
                              </div>
                              <div className="text-xs text-white/50">Budget</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-white">₦{(campaign.spent || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                              <div className="text-xs text-white/50">Spent</div>
                            </div>
                          </div>
                        )}
                        
                        {/* Campaign Metrics - Performance */}
                        <div className="grid grid-cols-4 gap-2 mb-2">
                          <div className="p-2 rounded-lg bg-white/5">
                            <div className="flex items-center gap-1 text-xs text-white/50 mb-1">
                              <Eye className="h-3 w-3" /> Impressions
                            </div>
                            <div className="font-semibold text-white text-sm">{(campaign.impressions || 0).toLocaleString()}</div>
                          </div>
                          <div className="p-2 rounded-lg bg-white/5">
                            <div className="flex items-center gap-1 text-xs text-white/50 mb-1">
                              <Users className="h-3 w-3" /> Reach
                            </div>
                            <div className="font-semibold text-white text-sm">{(campaign.reach || 0).toLocaleString()}</div>
                          </div>
                          <div className="p-2 rounded-lg bg-white/5">
                            <div className="flex items-center gap-1 text-xs text-white/50 mb-1">
                              <MousePointerClick className="h-3 w-3" /> Clicks
                            </div>
                            <div className="font-semibold text-white text-sm">{(campaign.clicks || 0).toLocaleString()}</div>
                          </div>
                          <div className="p-2 rounded-lg bg-white/5">
                            <div className="flex items-center gap-1 text-xs text-white/50 mb-1">
                              <TrendingUp className="h-3 w-3" /> CTR
                            </div>
                            <div className="font-semibold text-cyan-400 text-sm">{(campaign.ctr || 0).toFixed(2)}%</div>
                          </div>
                        </div>
                        
                        {/* Campaign Metrics - Engagement (Likes, Comments, Shares) */}
                        <div className="grid grid-cols-3 gap-2">
                          <div className="p-2 rounded-lg bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/20">
                            <div className="flex items-center gap-1 text-xs text-pink-300/70 mb-1">
                              <Heart className="h-3 w-3" /> Likes
                            </div>
                            <div className="font-semibold text-pink-300 text-sm">{(campaign.likes || 0).toLocaleString()}</div>
                          </div>
                          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
                            <div className="flex items-center gap-1 text-xs text-blue-300/70 mb-1">
                              <MessageCircle className="h-3 w-3" /> Comments
                            </div>
                            <div className="font-semibold text-blue-300 text-sm">{(campaign.comments || 0).toLocaleString()}</div>
                          </div>
                          <div className="p-2 rounded-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                            <div className="flex items-center gap-1 text-xs text-green-300/70 mb-1">
                              <Share2 className="h-3 w-3" /> Shares
                            </div>
                            <div className="font-semibold text-green-300 text-sm">{(campaign.shares || 0).toLocaleString()}</div>
                          </div>
                        </div>
                        
                        {/* Click to view details indicator */}
                        <div className="flex items-center justify-center mt-3 pt-3 border-t border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-xs text-blue-400 flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" />
                            Click to view details
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

              {/* Info Note */}
                  <div className="p-3 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-cyan-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-cyan-300">Campaign Performance</p>
                        <p className="text-xs text-cyan-200/60 mt-1">
                          Data synced from {getPlatformAdsManagerName(activePlatform)}. Click on a campaign to view detailed analytics and make edits.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Page Post Insights Section */}
              {activeSection === 'insights' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                        <Eye className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-white">Page Post Insights</h2>
                        <p className="text-xs text-white/50">View engagement metrics for your recent posts</p>
                      </div>
                    </div>
                    {/* Page selector and refresh button */}
                    {facebookPages.length > 0 && connectedAccount?.accessToken && (
                      <div className="flex items-center gap-2">
                        <select
                          className="px-3 py-1.5 rounded-lg bg-slate-800 border border-orange-500/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/50 [&>option]:bg-slate-800 [&>option]:text-white"
                          style={{ colorScheme: 'dark' }}
                          value={formData.selectedPageId || ''}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, selectedPageId: e.target.value }));
                            // Clear previous posts when page changes
                            setPagePosts([]);
                            setPagePostsError(null);
                          }}
                        >
                          <option value="" className="bg-slate-800 text-white">Select a Page</option>
                          {facebookPages.map(page => (
                            <option key={page.id} value={page.id} className="bg-slate-800 text-white">{page.name}</option>
                          ))}
                        </select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => formData.selectedPageId && fetchPagePosts(formData.selectedPageId)}
                          disabled={isLoadingPagePosts || !formData.selectedPageId}
                          className="bg-gradient-to-r from-orange-500 to-amber-500 border-0 text-white hover:from-orange-600 hover:to-amber-600 font-medium"
                        >
                          {isLoadingPagePosts ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Load Posts
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Loading state */}
                  {isLoadingPagePosts && (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                      <span className="ml-2 text-white/70">Loading page posts...</span>
                    </div>
                  )}

                  {/* Error state */}
                  {pagePostsError && !isLoadingPagePosts && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300">
                      <p className="text-sm">{pagePostsError}</p>
                      <p className="text-xs text-red-300/70 mt-1">Make sure you have pages_read_engagement permission.</p>
                    </div>
                  )}

                  {/* No page selected */}
                  {!formData.selectedPageId && !isLoadingPagePosts && pagePosts.length === 0 && (
                    <div className="p-8 rounded-xl bg-white/5 border border-white/10 text-center">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-white/30" />
                      <p className="text-white/70">Select a Facebook Page to view post insights</p>
                    </div>
                  )}

                  {/* Page selected but posts not loaded */}
                  {formData.selectedPageId && !isLoadingPagePosts && pagePosts.length === 0 && !pagePostsError && (
                    <div className="p-8 rounded-xl bg-gradient-to-br from-orange-500/5 to-amber-500/5 border border-orange-500/20 text-center">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-orange-400/50" />
                      <p className="text-white/70">Click "Load Posts" to fetch posts from your page</p>
                      <p className="text-xs text-white/50 mt-1">Posts will include engagement metrics (likes, comments, shares)</p>
                    </div>
                  )}

                  {/* Summary Stats */}
                  {pagePosts.length > 0 && (
                    <>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="p-3 rounded-xl bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/20">
                          <div className="text-2xl font-bold text-pink-400">
                            {(pagePosts.reduce((sum, p) => sum + (p.likes || 0), 0) / 1000).toFixed(1)}K
                          </div>
                          <div className="text-xs text-pink-300/70">Total Reactions</div>
                        </div>
                        <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
                          <div className="text-2xl font-bold text-blue-400">
                            {(pagePosts.reduce((sum, p) => sum + (p.comments || 0), 0)).toLocaleString()}
                          </div>
                          <div className="text-xs text-blue-300/70">Total Comments</div>
                        </div>
                        <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                          <div className="text-2xl font-bold text-green-400">
                            {(pagePosts.reduce((sum, p) => sum + (p.shares || 0), 0)).toLocaleString()}
                          </div>
                          <div className="text-xs text-green-300/70">Total Shares</div>
                        </div>
                      </div>

                      {/* Posts List */}
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2">
                          <Facebook className="h-4 w-4 text-blue-400" />
                          Recent Page Posts
                        </h3>
                        
                        {(() => {
                          // Find the post with highest engagement (likes + comments + shares)
                          const highestEngagementPostId = pagePosts.length > 0 
                            ? pagePosts.reduce((maxPost, post) => {
                                const engagement = (post.likes || 0) + (post.comments || 0) + (post.shares || 0);
                                const maxEngagement = (maxPost.likes || 0) + (maxPost.comments || 0) + (maxPost.shares || 0);
                                return engagement > maxEngagement ? post : maxPost;
                              }, pagePosts[0])?.id
                            : null;
                          
                          return pagePosts.map((post) => {
                            const isRecommended = post.id === highestEngagementPostId && 
                              ((post.likes || 0) + (post.comments || 0) + (post.shares || 0)) > 0;
                            
                            return (
                              <div 
                                key={post.id}
                                onClick={() => { setEngagementDetailKind(null); setEngagementModalPost(post); }}
                                className={`p-4 rounded-xl border transition-all relative cursor-pointer ${
                                  isRecommended 
                                    ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/40 hover:border-amber-500/60' 
                                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                                }`}
                              >
                                {/* Boost Recommended Badge */}
                                {isRecommended && (
                                  <div className="absolute -top-2 -right-2 z-10">
                                    <div className="flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-lg shadow-orange-500/30">
                                      <Sparkles className="h-3 w-3 text-white" />
                                      <span className="text-xs font-bold text-white">Boost Recommended</span>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Post Header */}
                                <div className="flex gap-3 mb-3">
                              {post.thumbnail ? (
                                <img 
                                  src={post.thumbnail} 
                                  alt="Post thumbnail"
                                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
                                  <MessageCircle className="h-6 w-6 text-blue-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white/90 line-clamp-2">{post.message || '(No text content)'}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    post.type === 'photo' ? 'bg-purple-500/20 text-purple-300' :
                                    post.type === 'video' ? 'bg-red-500/20 text-red-300' :
                                    post.type === 'link' ? 'bg-blue-500/20 text-blue-300' :
                                    'bg-gray-500/20 text-gray-300'
                                  }`}>
                                    {(post.type || 'status').charAt(0).toUpperCase() + (post.type || 'status').slice(1)}
                                  </span>
                                  <span className="text-xs text-white/40">
                                    {(() => {
                                      const diff = Date.now() - new Date(post.created_time).getTime();
                                      const hours = Math.floor(diff / (1000 * 60 * 60));
                                      const days = Math.floor(hours / 24);
                                      if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
                                      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
                                    })()}
                                  </span>
                                  {post.permalink_url && (
                                    <a href={post.permalink_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Engagement Metrics */}
                            <div className="grid grid-cols-3 gap-2 mb-2">
                              <div className="p-2 rounded-lg bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/20">
                                <div className="flex items-center gap-1 text-xs text-pink-300/70 mb-1">
                                  <Heart className="h-3 w-3" /> Reactions
                                </div>
                                <div className="font-semibold text-pink-300">{(post.likes || 0).toLocaleString()}</div>
                              </div>
                              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
                                <div className="flex items-center gap-1 text-xs text-blue-300/70 mb-1">
                                  <MessageCircle className="h-3 w-3" /> Comments
                                </div>
                                <div className="font-semibold text-blue-300">{(post.comments || 0).toLocaleString()}</div>
                              </div>
                              <div className="p-2 rounded-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                                <div className="flex items-center gap-1 text-xs text-green-300/70 mb-1">
                                  <Share2 className="h-3 w-3" /> Shares
                                </div>
                                <div className="font-semibold text-green-300">{(post.shares || 0).toLocaleString()}</div>
                              </div>
                            </div>

                            {/* Reach & Impressions */}
                            <div className="grid grid-cols-2 gap-2">
                              <div className="p-2 rounded-lg bg-white/5">
                                <div className="flex items-center gap-1 text-xs text-white/50 mb-1">
                                  <Users className="h-3 w-3" /> Reach
                                </div>
                                <div className="font-semibold text-white text-sm">
                                  {post.reach > 0 ? `${(post.reach / 1000).toFixed(1)}K` : 'N/A'}
                                </div>
                              </div>
                              <div className="p-2 rounded-lg bg-white/5">
                                <div className="flex items-center gap-1 text-xs text-white/50 mb-1">
                                  <Eye className="h-3 w-3" /> Impressions
                                </div>
                                <div className="font-semibold text-white text-sm">
                                  {post.impressions > 0 ? `${(post.impressions / 1000).toFixed(1)}K` : 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                            );
                          });
                        })()}
                      </div>
                    </>
                  )}

                  {/* Data Source Indicator */}
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/10">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-xs text-white/40">Data synced from Facebook Page Insights API</span>
                  </div>
                </div>
              )}

              {/* Campaign Section */}
              {activeSection === 'campaign' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: AD_PLATFORMS.find(p => p.id === activePlatform)?.color || '#3B82F6' }}>
                      <Target className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-white">{currentPlatformConfig.name} Campaign Settings</h2>
                      <p className="text-xs text-white/50">Configure your {currentPlatformConfig.name} ad campaign</p>
                    </div>
                  </div>

                  {/* Shared Fields Info Banner - Only show on multi-platform mode */}
                  {!metaReviewMode && (
                    <div className="p-3 rounded-lg bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-green-400" />
                        <span className="text-xs text-green-300">Campaign name, budget, and dates sync automatically across all platforms</span>
                      </div>
                    </div>
                  )}

                  {/* Platform-specific Account Selection */}
                  {(activePlatform === 'facebook' || activePlatform === 'instagram') ? (
                    <>
                      {/* Facebook/Instagram Page Selection */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: activePlatform === 'facebook' ? '#1877F2' : '#E4405F' }}>
                            {activePlatform === 'facebook' ? (
                              <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                            ) : (
                              <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                              </svg>
                            )}
                          </div>
                          <div>
                            <Label className="text-white/90 text-sm font-semibold">{currentPlatformConfig.accountLabel} *</Label>
                            <p className="text-xs text-blue-200/60">{currentPlatformConfig.accountPlaceholder}</p>
                          </div>
                        </div>
                        <Select value={formData.selectedPageId} onValueChange={(v) => updateField('selectedPageId', v)}>
                          <SelectTrigger className="bg-white/5 border-blue-500/20 text-white">
                            <SelectValue placeholder={isLoadingPages ? "Loading pages..." : `Select a ${activePlatform === 'facebook' ? 'Facebook' : 'Instagram'} Page`} />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-white/10">
                            {isLoadingPages ? (
                              <div className="p-4 text-center text-white/60">
                                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                                Loading your Pages...
                              </div>
                            ) : facebookPages.length === 0 ? (
                              <div className="p-4 text-white/70">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                                  <span className="font-medium text-yellow-300">No Pages Found</span>
                                </div>
                                <p className="text-xs text-white/50">Connect your Meta account to see your pages.</p>
                              </div>
                            ) : facebookPages.map((page) => (
                              <SelectItem key={page.id} value={page.id} className="text-white hover:bg-white/10">
                                <div className="flex items-center gap-3">
                                  <img src={page.picture} alt={page.name} className="w-8 h-8 rounded-full" />
                                  <div>
                                    <div className="font-medium">{page.name}</div>
                                    <div className="text-xs text-white/50">{page.category}</div>
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Ad Account Selector */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-blue-400" />
                            <Label className="text-white/90 text-sm font-semibold">Ad Account *</Label>
                          </div>
                          {selectedBusiness && metaReviewMode && (
                            <span className="text-xs text-blue-300/70 flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              From: {selectedBusiness.name}
                            </span>
                          )}
                        </div>
                        <Select value={formData.selectedAdAccountId} onValueChange={(v) => updateField('selectedAdAccountId', v)}>
                          <SelectTrigger className="bg-white/5 border-blue-500/20 text-white">
                            <SelectValue placeholder={isLoadingAdAccounts ? "Loading ad accounts..." : "Select an Ad Account"} />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-white/10">
                            {isLoadingAdAccounts ? (
                              <div className="p-4 flex items-center gap-2 text-white/70">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading ad accounts...</span>
                              </div>
                            ) : adAccounts.length === 0 ? (
                              <div className="p-4 text-white/70">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                                  <span className="font-medium text-yellow-300">No Ad Accounts Found</span>
                                </div>
                                <p className="text-xs text-white/50 mb-2">You need a Meta Ad Account to run campaigns.</p>
                                <a 
                                  href="https://business.facebook.com/settings/ad-accounts" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                >
                                  <span>Create Ad Account in Business Settings</span>
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                            ) : adAccounts.map((account) => (
                              <SelectItem key={account.id} value={account.id} className="text-white hover:bg-white/10">
                                <div>
                                  <div className="font-medium">{account.name}</div>
                                  <div className="text-xs text-white/50">
                                    {account.id} • {account.currency} • {account.status}
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formData.selectedAdAccountId && (
                          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 space-y-2">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-400" />
                              <span className="text-sm font-medium text-green-300">
                                {adAccounts.find(a => a.id === formData.selectedAdAccountId)?.name}
                              </span>
                            </div>
                            {(() => {
                              const account = adAccounts.find(a => a.id === formData.selectedAdAccountId);
                              return account ? (
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                  <div className="p-2 rounded bg-white/5">
                                    <span className="text-white/50 block">Account ID</span>
                                    <span className="text-white font-mono">{account.id}</span>
                                  </div>
                                  <div className="p-2 rounded bg-white/5">
                                    <span className="text-white/50 block">Currency</span>
                                    <span className="text-white">{account.currency}</span>
                                  </div>
                                  <div className="p-2 rounded bg-white/5">
                                    <span className="text-white/50 block">Status</span>
                                    <span className={account.status === 'ACTIVE' ? 'text-green-400' : 'text-yellow-400'}>{account.status}</span>
                                  </div>
                                  {account.timezone && (
                                    <div className="p-2 rounded bg-white/5 col-span-3">
                                      <span className="text-white/50 block">Timezone</span>
                                      <span className="text-white">{account.timezone}</span>
                                    </div>
                                  )}
                                </div>
                              ) : null;
                            })()}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    /* Other Platforms - Account Connection */
                    <div className="space-y-3 p-4 rounded-xl border border-white/10" style={{ background: `linear-gradient(to right, ${AD_PLATFORMS.find(p => p.id === activePlatform)?.color}15, ${AD_PLATFORMS.find(p => p.id === activePlatform)?.color}05)` }}>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: AD_PLATFORMS.find(p => p.id === activePlatform)?.color }}>
                          {activePlatform === 'twitter' ? (
                            <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                          ) : (
                            <Building2 className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <div>
                          <Label className="text-white/90 text-sm font-semibold">{currentPlatformConfig.accountLabel} *</Label>
                          <p className="text-xs text-white/50">{currentPlatformConfig.accountPlaceholder}</p>
                        </div>
                      </div>
                      
                      {/* Check for Google/YouTube connection status */}
                      {(activePlatform === 'google' || activePlatform === 'youtube') && googleAdsConnection?.connected ? (
                        <div className="space-y-3">
                          {/* Connection Status */}
                          <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                                <span className="text-xs font-bold text-white">G</span>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4 text-green-400" />
                                  <span className="text-sm text-green-300 font-medium">Google Ads Connected</span>
                                </div>
                                <span className="text-xs text-white/60">{googleAdsConnection.user_email}</span>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={handleDisconnectGoogle}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                            >
                              <Unlink className="h-3 w-3 mr-1" />
                              Disconnect
                            </Button>
                          </div>
                          
                          {/* Client Account Selection */}
                          <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                Ad Account *
                              </Label>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowLinkAccountModal(true)}
                                  className="text-green-400 hover:text-green-300 hover:bg-green-500/10 text-xs h-7 px-2"
                                  data-testid="link-account-btn"
                                >
                                  <LinkIcon className="h-3 w-3 mr-1" />
                                  Link
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowCreateAccountModal(true)}
                                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 text-xs h-7 px-2"
                                  data-testid="create-client-account-btn"
                                >
                                  + New
                                </Button>
                              </div>
                            </div>
                            
                            {googleAdsClientAccounts.length > 0 ? (
                              <Select
                                value={selectedGoogleAccount?.id || ''}
                                onValueChange={(value) => {
                                  const account = googleAdsClientAccounts.find(acc => acc.id === value);
                                  setSelectedGoogleAccount(account);
                                }}
                              >
                                <SelectTrigger 
                                  className="bg-white/5 border-white/20 text-white"
                                  data-testid="google-client-account-select"
                                >
                                  <SelectValue placeholder="Select an ad account" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-white/20">
                                  {googleAdsClientAccounts.map((account) => (
                                    <SelectItem 
                                      key={account.id} 
                                      value={account.id}
                                      className="text-white hover:bg-white/10"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>{account.descriptive_name || 'Unnamed'}</span>
                                        <span className="text-white/50 text-xs">({account.id})</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-sm text-yellow-300 font-medium">No Client Accounts Found</p>
                                    <p className="text-xs text-white/60 mt-1">
                                      Your Manager (MCC) account cannot hold ads directly. 
                                      Create a new client account or link an existing one.
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      <Button
                                        onClick={() => setShowCreateAccountModal(true)}
                                        className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/30 text-xs h-8"
                                        data-testid="create-first-client-account-btn"
                                      >
                                        <Building2 className="h-3 w-3 mr-1" />
                                        Create New
                                      </Button>
                                      <Button
                                        onClick={() => setShowLinkAccountModal(true)}
                                        className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 text-xs h-8"
                                        data-testid="link-existing-account-btn"
                                      >
                                        <LinkIcon className="h-3 w-3 mr-1" />
                                        Link Existing
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {selectedGoogleAccount && (
                              <p className="text-xs text-white/40">
                                Selected: {selectedGoogleAccount.descriptive_name || 'Unnamed'} • ID: {selectedGoogleAccount.id}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : activePlatform === 'twitter' && xAdsConnection?.connected ? (
                        /* X (Twitter) Ads Connected */
                        <div className="space-y-3">
                          {/* Connection Status */}
                          <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center">
                                <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4 text-green-400" />
                                  <span className="text-sm text-green-300 font-medium">X Ads Connected</span>
                                </div>
                                <span className="text-xs text-white/60">@{xAdsConnection.username}</span>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={handleDisconnectXAds}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                            >
                              <Unlink className="h-3 w-3 mr-1" />
                              Disconnect
                            </Button>
                          </div>
                          
                          {/* X Ads Account Selection */}
                          {xAdsAccounts.length > 0 && (
                            <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
                              <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                Ad Account
                              </Label>
                              <Select
                                value={selectedXAdsAccount?.id || ''}
                                onValueChange={(value) => {
                                  const account = xAdsAccounts.find(acc => acc.id === value);
                                  setSelectedXAdsAccount(account);
                                  if (account) fetchXAdsFundingInstruments(account.id);
                                }}
                              >
                                <SelectTrigger className="bg-white/5 border-white/20 text-white">
                                  <SelectValue placeholder="Select an ad account" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-white/20">
                                  {xAdsAccounts.map((account) => (
                                    <SelectItem 
                                      key={account.id} 
                                      value={account.id}
                                      className="text-white hover:bg-white/10"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>{account.name || 'Unnamed'}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                                          account.approval_status === 'ACCEPTED' 
                                            ? 'bg-green-500/20 text-green-300' 
                                            : 'bg-yellow-500/20 text-yellow-300'
                                        }`}>
                                          {account.approval_status}
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              
                              {selectedXAdsAccount && (
                                <p className="text-xs text-white/40">
                                  Currency: {selectedXAdsAccount.currency} • Timezone: {selectedXAdsAccount.timezone}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Funding Instrument (Billing) */}
                          {xAdsFundingInstruments.length > 0 && (
                            <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
                              <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
                                <DollarSign className="h-4 w-4" />
                                Payment Method
                              </Label>
                              <div className="space-y-2">
                                {xAdsFundingInstruments.filter(fi => !fi.cancelled && !fi.deleted).map((fi) => (
                                  <div key={fi.id} className="p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-white">{fi.type}</span>
                                      <span className="text-xs text-green-300">
                                        {fi.currency} {fi.credit_remaining?.toLocaleString()} remaining
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {xAdsFundingInstruments.length === 0 && selectedXAdsAccount && (
                            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm text-yellow-300 font-medium">No Payment Method</p>
                                  <p className="text-xs text-white/60 mt-1">
                                    Add a payment method in X Ads Manager to run campaigns.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : isSnapchat ? (
                        <SnapchatAccountSection snap={snap} />
                      ) : activePlatform === 'twitter' && !xAdsConnection?.connected ? (
                        /* X Ads Not Connected */
                        <Button 
                          onClick={handleXAdsConnect}
                          className="w-full bg-black hover:bg-gray-900 border border-white/20 text-white"
                          data-testid="connect-x-ads-btn"
                        >
                          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                          Connect X Ads Account
                        </Button>
                      ) : platformFormData[activePlatform]?.linkedAccount ? (
                        <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-400" />
                            <span className="text-sm text-green-300">Account Connected</span>
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 text-xs">
                            Disconnect
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          onClick={() => connectPlatformAccount(activePlatform)}
                          className="w-full bg-white/10 hover:bg-white/20 border border-white/20"
                        >
                          <Plug className="h-4 w-4 mr-2" />
                          Connect {currentPlatformConfig.name} Account
                        </Button>
                      )}
                      
                      <p className="text-xs text-white/40 italic">
                        {activePlatform === 'google' || activePlatform === 'youtube' 
                          ? 'Requires Google Ads account with linked YouTube channel'
                          : activePlatform === 'tiktok'
                          ? 'Requires TikTok for Business account'
                          : activePlatform === 'snapchat'
                          ? 'Requires Snapchat Business Manager account'
                          : activePlatform === 'twitter'
                          ? 'Requires X Ads account with valid funding instrument'
                          : 'Connect your business account to continue'
                        }
                      </p>
                    </div>
                  )}

                  {/* Campaign Name - Shared across all platforms */}
                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
                      Campaign Name *
                      {!metaReviewMode && (
                        <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 text-xs">Synced</span>
                      )}
                    </Label>
                    <Input
                      value={currentPlatformData.campaignName || formData.campaignName}
                      onChange={(e) => {
                        updatePlatformField('campaignName', e.target.value);
                      }}
                      placeholder={`Enter ${currentPlatformConfig.name} campaign name`}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                    {!metaReviewMode && (
                      <p className="text-xs text-white/40">Max 255 characters • Will sync to other enabled platforms</p>
                    )}
                  </div>

                  {/* Platform-specific Objective/Campaign Type Selection */}
                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm font-medium">
                      {activePlatform === 'google' || activePlatform === 'youtube' ? 'Campaign Type *' : 'Campaign Objective *'}
                    </Label>
                    <Select 
                      value={currentPlatformData.objective || currentPlatformData.campaignType || formData.objective} 
                      onValueChange={(v) => {
                        if (activePlatform === 'google' || activePlatform === 'youtube') {
                          updatePlatformField('campaignType', v);
                        } else {
                          updatePlatformField('objective', v);
                          updateField('objective', v);
                        }
                      }}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder={`Select ${activePlatform === 'google' || activePlatform === 'youtube' ? 'campaign type' : 'objective'}`} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-white/10">
                        {(activePlatform === 'google' || activePlatform === 'youtube' 
                          ? currentPlatformConfig.campaignTypes 
                          : currentPlatformConfig.objectives
                        )?.filter((opt) => {
                          // For Facebook/Instagram with Reserved buying type, only show Awareness and Engagement
                          if (formData.orderType === 'RESERVED' && (activePlatform === 'facebook' || activePlatform === 'instagram')) {
                            return opt.value === 'AWARENESS' || opt.value === 'ENGAGEMENT';
                          }
                          return true;
                        }).map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-white/10">
                            <div>
                              <div className="font-medium">{opt.label}</div>
                              <div className="text-xs text-white/50">{opt.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.orderType === 'RESERVED' && (activePlatform === 'facebook' || activePlatform === 'instagram') && (
                      <p className="text-xs text-yellow-300/80 bg-yellow-500/10 p-2 rounded">
                        Domination buying type only supports Awareness and Engagement objectives
                      </p>
                    )}
                  </div>

                  {/* Snapchat Optimization Goal (depends on objective) */}
                  {isSnapchat && formData.objective && SNAPCHAT_OPTIMIZATION_GOALS[formData.objective] && (
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm font-medium">Optimization Goal</Label>
                      <Select
                        value={SNAPCHAT_OPTIMIZATION_GOALS[formData.objective].some(g => g.value === formData.snapOptimizationGoal) ? formData.snapOptimizationGoal : SNAPCHAT_OPTIMIZATION_GOALS[formData.objective][0].value}
                        onValueChange={(v) => updateField('snapOptimizationGoal', v)}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="snapchat-optimization-goal-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-white/10">
                          {SNAPCHAT_OPTIMIZATION_GOALS[formData.objective].map((g) => (
                            <SelectItem key={g.value} value={g.value} className="text-white hover:bg-white/10">{g.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-white/40">Auto-bid, billed per impression. Location targeting uses your globe radius as a Snapchat geofence.</p>
                    </div>
                  )}

                  {isSnapchat && formData.objective && (
                    <SnapchatObjectiveSetup snap={snap} formData={formData} value={formData.snapConversion} onChange={(v) => updateField('snapConversion', v)} />
                  )}

                  {/* Buying Type - Only for platforms that support it */}
                  {currentPlatformConfig.buyingTypes && (
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm font-medium">Buying Type *</Label>
                      <Select value={formData.orderType} onValueChange={(v) => {
                        updateField('orderType', v);
                        // Reset objective if current objective is not available for Reserved
                        if (v === 'RESERVED' && (activePlatform === 'facebook' || activePlatform === 'instagram')) {
                          // Check if current objective is valid for Reserved
                          const currentObj = currentPlatformData.objective || formData.objective;
                          if (currentObj !== 'AWARENESS' && currentObj !== 'ENGAGEMENT') {
                            updatePlatformField('objective', 'AWARENESS');
                            updateField('objective', 'AWARENESS');
                          }
                        }
                      }}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Select buying type" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-white/10">
                          {currentPlatformConfig.buyingTypes.map((bt) => (
                            <SelectItem key={bt.value} value={bt.value} className="text-white hover:bg-white/10">
                              <div>
                                <div className="font-medium">{bt.label}</div>
                                <div className="text-xs text-white/50">{bt.description}</div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-white/40">Interest is recommended for most campaigns</p>
                    </div>
                  )}

                  {/* Google Ads Specific - Bidding Strategy */}
                  {(activePlatform === 'google' || activePlatform === 'youtube') && currentPlatformConfig.biddingStrategies && (
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm font-medium">Bidding Strategy</Label>
                      <Select 
                        value={currentPlatformData.biddingStrategy || 'MAXIMIZE_CLICKS'} 
                        onValueChange={(v) => updatePlatformField('biddingStrategy', v)}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Select bidding strategy" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-white/10">
                          {currentPlatformConfig.biddingStrategies.map((bs) => (
                            <SelectItem key={bs.value} value={bs.value} className="text-white hover:bg-white/10">
                              {bs.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* TikTok Specific - Bid Type */}
                  {activePlatform === 'tiktok' && currentPlatformConfig.bidTypes && (
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm font-medium">Bid Type</Label>
                      <Select 
                        value={currentPlatformData.bidType || 'NO_BID'} 
                        onValueChange={(v) => updatePlatformField('bidType', v)}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Select bid type" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-white/10">
                          <SelectItem value="NO_BID" className="text-white hover:bg-white/10">Lowest Cost (No Bid Cap)</SelectItem>
                          <SelectItem value="BID_TYPE_CUSTOM" className="text-white hover:bg-white/10">Cost Cap</SelectItem>
                          <SelectItem value="BID_TYPE_MAX_CONVERSION" className="text-white hover:bg-white/10">Maximum Delivery</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* YouTube Specific - Video Format */}
                  {activePlatform === 'youtube' && currentPlatformConfig.videoFormats && (
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm font-medium">Video Ad Format</Label>
                      <Select 
                        value={currentPlatformData.videoFormat || 'SKIPPABLE_IN_STREAM'} 
                        onValueChange={(v) => updatePlatformField('videoFormat', v)}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Select video format" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-white/10">
                          {currentPlatformConfig.videoFormats.map((vf) => (
                            <SelectItem key={vf.value} value={vf.value} className="text-white hover:bg-white/10">
                              <div>
                                <div className="font-medium">{vf.label}</div>
                                <div className="text-xs text-white/50">{vf.description}</div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Google Ads Specific - Keywords */}
                  {activePlatform === 'google' && (
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm font-medium">Keywords (for Search campaigns)</Label>
                      <textarea
                        value={currentPlatformData.keywords || ''}
                        onChange={(e) => updatePlatformField('keywords', e.target.value)}
                        placeholder="Enter keywords, one per line&#10;Example:&#10;buy shoes online&#10;best running shoes&#10;nike air max"
                        className="w-full h-24 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white placeholder:text-white/30 text-sm resize-none"
                      />
                      <p className="text-xs text-white/40">Enter keywords for Search campaigns. Leave empty for other campaign types.</p>
                    </div>
                  )}

                  {/* Post Up Feature - Conditionally hidden for Meta App Review */}
                  {SHOW_POST_UP_FEATURE && (
                  <div className="space-y-3 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Share2 className="h-3.5 w-3.5 text-white" />
                      </div>
                      <div>
                        <Label className="text-white/90 text-sm font-semibold">Post Up By</Label>
                        <p className="text-xs text-purple-200/60">Send content to influencers to repost</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-white/70 text-xs">Select Influencers</Label>
                      {loadingInfluencers ? (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 text-white/50 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading influencers...
                        </div>
                      ) : influencers.length === 0 ? (
                        <div className="p-3 rounded-lg bg-white/5 text-white/50 text-sm">
                          No influencers available
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="max-h-40 overflow-y-auto rounded-lg bg-white/5 border border-white/10 p-2 space-y-1">
                            {influencers.map((influencer) => {
                              const id = influencer._id || influencer.id;
                              const isSelected = formData.postUpInfluencers.includes(id);
                              return (
                                <div
                                  key={id}
                                  onClick={() => toggleInfluencer(id)}
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                    isSelected 
                                      ? 'bg-purple-500/20 border border-purple-500/40' 
                                      : 'hover:bg-white/5 border border-transparent'
                                  }`}
                                >
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                    isSelected 
                                      ? 'bg-purple-500 border-purple-500' 
                                      : 'border-white/30'
                                  }`}>
                                    {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                                  </div>
                                  <img 
                                    src={influencer.profile_picture || influencer.profilePicture || `https://ui-avatars.com/api/?name=${encodeURIComponent(influencer.name)}&background=6366f1&color=fff`}
                                    alt={influencer.name}
                                    className="w-8 h-8 rounded-full object-cover border border-white/20"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white font-medium truncate">{influencer.name}</div>
                                    <div className="text-xs text-white/50 truncate">
                                      {influencer.platform} • @{influencer.handle || influencer.username}
                                    </div>
                                  </div>
                                  <div className="text-xs text-purple-300 font-medium">
                                    {influencer.followers ? `${(influencer.followers / 1000).toFixed(1)}K` : ''}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {formData.postUpInfluencers.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {getSelectedInfluencerNames().map((name, idx) => (
                                <span key={idx} className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-200 text-xs font-medium">
                                  {name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white/70 text-xs">Content URL to Repost</Label>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-purple-300/50" />
                        <Input
                          type="url"
                          placeholder="https://instagram.com/p/..."
                          value={formData.postUpContentUrl}
                          onChange={(e) => updateField('postUpContentUrl', e.target.value)}
                          className="pl-9 bg-white/5 border-purple-500/20 text-white placeholder:text-white/30 focus:border-purple-500 focus:ring-purple-500/20"
                        />
                      </div>
                      <p className="text-xs text-purple-200/50">
                        Paste the Instagram/Facebook post URL for influencers to share
                      </p>
                    </div>

                    {formData.postUpInfluencers.length > 0 && formData.postUpContentUrl && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/30">
                        <CheckCircle className="h-4 w-4 text-green-400" />
                        <span className="text-xs text-green-300">
                          {formData.postUpInfluencers.length} influencer{formData.postUpInfluencers.length > 1 ? 's' : ''} will receive this content
                        </span>
                      </div>
                    )}
                  </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm">Special Ad Category</Label>
                    <Select value={formData.specialAdCategory} onValueChange={handleSpecialAdCategoryChange}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-white/10">
                        {SPECIAL_AD_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-white/10">
                            <div>
                              <div className="font-medium">{cat.label}</div>
                              {cat.description && (
                                <div className="text-xs text-white/50">{cat.description}</div>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-white/40">Required for housing, employment, or political ads</p>
                    
                    {/* Verification Status for Special Ad Categories */}
                    {formData.specialAdCategory && formData.specialAdCategory !== 'NONE' && (
                      <div className="mt-3 space-y-2">
                        {isCheckingVerification ? (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                            <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                            <span className="text-xs text-white/60">Checking verification status...</span>
                          </div>
                        ) : (
                          <>
                            {/* Identity Verification Status */}
                            {identityVerificationStatus === 'verified' ? (
                              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                                <CheckCircle className="h-4 w-4 text-green-400" />
                                <div>
                                  <span className="text-xs text-green-300 font-medium">Identity Verified</span>
                                  <p className="text-xs text-green-200/60">Your ad account is authorized for Special Ad Categories</p>
                                </div>
                              </div>
                            ) : identityVerificationStatus === 'not_verified' ? (
                              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertTriangle className="h-4 w-4 text-red-400" />
                                  <span className="text-xs text-red-300 font-medium">Identity Verification Required</span>
                                </div>
                                <p className="text-xs text-red-200/60 mb-2">
                                  To run {SPECIAL_AD_CATEGORIES.find(c => c.value === formData.specialAdCategory)?.label} ads, you must verify your identity.
                                </p>
                                <a 
                                  href="https://www.facebook.com/id" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                >
                                  <span>Verify Your Identity</span>
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                                <AlertTriangle className="h-4 w-4 text-yellow-400" />
                                <div>
                                  <span className="text-xs text-yellow-300 font-medium">Verification Check Required</span>
                                  <p className="text-xs text-yellow-200/60">Select an Ad Account to check verification status</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Disclaimer Status for Political Ads */}
                            {formData.specialAdCategory === 'ISSUES_ELECTIONS_POLITICS' && (
                              <div className="mt-2">
                                {isLoadingDisclaimers ? (
                                  <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                                    <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                                    <span className="text-xs text-white/60">Loading disclaimer options...</span>
                                  </div>
                                ) : disclaimers.length > 0 ? (
                                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                                    <div className="flex items-center gap-2 mb-2">
                                      <CheckCircle className="h-4 w-4 text-green-400" />
                                      <span className="text-xs text-green-300 font-medium">"Paid for by" Options Available</span>
                                    </div>
                                    <p className="text-xs text-green-200/60 mb-3">
                                      Select or enter the disclaimer for this political ad.
                                    </p>
                                    
                                    {/* Disclaimer Selection Dropdown */}
                                    <div className="space-y-2">
                                      <Label className="text-white/70 text-xs">Select Disclaimer</Label>
                                      <Select value={selectedDisclaimer?.id || ''} onValueChange={(v) => {
                                        if (v === 'manual') {
                                          setSelectedDisclaimer({ id: 'manual', name: '', type: 'manual' });
                                        } else {
                                          const disc = disclaimers.find(d => d.id === v);
                                          setSelectedDisclaimer(disc);
                                        }
                                      }}>
                                        <SelectTrigger className="bg-white/5 border-green-500/30 text-white text-xs">
                                          <SelectValue placeholder="Select disclaimer" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-800 border-white/10">
                                          {disclaimers.map((disc) => (
                                            <SelectItem key={disc.id} value={disc.id} className="text-white hover:bg-white/10">
                                              <div className="flex items-center gap-2">
                                                <CheckCircle className="h-3 w-3 text-green-400" />
                                                <span className="text-xs">{disc.name}</span>
                                                {disc.type === 'page_fallback' && (
                                                  <span className="text-xs text-white/40">(Page)</span>
                                                )}
                                              </div>
                                            </SelectItem>
                                          ))}
                                          <SelectItem value="manual" className="text-white hover:bg-white/10">
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs">+ Enter custom disclaimer</span>
                                            </div>
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      
                                      {/* Manual Entry when "Enter custom" is selected */}
                                      {selectedDisclaimer?.id === 'manual' && (
                                        <div className="mt-2">
                                          <Input
                                            type="text"
                                            placeholder="e.g., APC 2027 CAMPAIGN"
                                            value={selectedDisclaimer?.name || ''}
                                            onChange={(e) => setSelectedDisclaimer({
                                              id: 'manual',
                                              name: e.target.value,
                                              type: 'manual'
                                            })}
                                            className="bg-white/5 border-green-500/30 text-white placeholder:text-white/30 text-xs"
                                          />
                                        </div>
                                      )}
                                      
                                      {selectedDisclaimer && selectedDisclaimer.name && (
                                        <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/20 border border-green-500/40">
                                          <CheckCircle className="h-3 w-3 text-green-400" />
                                          <span className="text-xs text-green-300">
                                            Ad will show: "Paid for by {selectedDisclaimer.name}"
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Option to manage disclaimers */}
                                    <div className="mt-3 pt-3 border-t border-green-500/20">
                                      <a 
                                        href="https://www.facebook.com/id" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                      >
                                        <span>Manage Disclaimers on Facebook</span>
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                                    <div className="flex items-center gap-2 mb-2">
                                      <AlertTriangle className="h-4 w-4 text-yellow-400" />
                                      <span className="text-xs text-yellow-300 font-medium">Disclaimer Required</span>
                                    </div>
                                    <p className="text-xs text-yellow-200/60 mb-3">
                                      Political ads require a "Paid for by" disclaimer. Enter your disclaimer below.
                                    </p>
                                    
                                    {/* Manual Disclaimer Entry */}
                                    <div className="space-y-2 mb-3">
                                      <Label className="text-white/70 text-xs">Enter Disclaimer Name</Label>
                                      <Input
                                        type="text"
                                        placeholder="e.g., APC 2027 CAMPAIGN"
                                        value={selectedDisclaimer?.name || ''}
                                        onChange={(e) => setSelectedDisclaimer({
                                          id: 'manual',
                                          name: e.target.value,
                                          type: 'manual'
                                        })}
                                        className="bg-white/5 border-yellow-500/30 text-white placeholder:text-white/30 text-xs"
                                      />
                                    </div>
                                    
                                    {selectedDisclaimer?.name && (
                                      <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/30 mb-3">
                                        <CheckCircle className="h-3 w-3 text-green-400" />
                                        <span className="text-xs text-green-300">
                                          Ad will show: "Paid for by {selectedDisclaimer.name}"
                                        </span>
                                      </div>
                                    )}
                                    
                                    <div className="flex flex-wrap gap-2">
                                      <a 
                                        href="https://www.facebook.com/id" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg"
                                      >
                                        <span>View My Disclaimers</span>
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {formData.objective && (
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm">Optimization Goal</Label>
                      <Select value={formData.optimizationGoal} onValueChange={(v) => updateField('optimizationGoal', v)}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Select goal" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-white/10">
                          {OPTIMIZATION_GOALS[formData.objective]?.map((goal) => (
                            <SelectItem key={goal.value} value={goal.value} className="text-white hover:bg-white/10">
                              {goal.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Targeting Section */}
              {activeSection === 'targeting' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                      <Users className="h-4 w-4 text-white" />
                    </div>
                    <h2 className="font-semibold text-white">Target Audience</h2>
                  </div>

                  {/* Age Selection */}
                  <div className="space-y-3 p-4 rounded-xl bg-white/5 border border-white/10">
                    <Label className="text-white/90 text-sm font-medium">Age Range</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-white/60 text-xs">Minimum age</Label>
                        <Select value={formData.minAge} onValueChange={(v) => updateField('minAge', v)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue placeholder="Min" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-white/10">
                            {AGE_OPTIONS.map((age) => (
                              <SelectItem key={age.value} value={age.value} className="text-white hover:bg-white/10">
                                {age.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-white/60 text-xs">Maximum age</Label>
                        <Select value={formData.maxAge} onValueChange={(v) => updateField('maxAge', v)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue placeholder="Max" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-white/10">
                            {AGE_OPTIONS.map((age) => (
                              <SelectItem key={age.value} value={age.value} className="text-white hover:bg-white/10">
                                {age.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Gender Selection */}
                  <div className="space-y-3 p-4 rounded-xl bg-white/5 border border-white/10">
                    <Label className="text-white/90 text-sm font-medium">Gender</Label>
                    <Select value={formData.gender} onValueChange={(v) => updateField('gender', v)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-white/10">
                        {GENDER_OPTIONS.map((g) => (
                          <SelectItem key={g.value} value={g.value} className="text-white hover:bg-white/10">
                            {g.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Languages Selection */}
                  {!isSnapchat && (
                  <div className="space-y-3 p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label className="text-white/90 text-sm font-medium">Languages</Label>
                        <Info className="h-3.5 w-3.5 text-white/40" />
                      </div>
                      <span className="text-xs text-blue-400">
                        {formData.languages.includes('all') ? 'All languages' : `${formData.languages.length} selected`}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {LANGUAGE_OPTIONS.map((lang) => {
                        const isSelected = formData.languages.includes(lang.value);
                        const isAll = lang.value === 'all';
                        return (
                          <button
                            key={lang.value}
                            type="button"
                            onClick={() => {
                              if (isAll) {
                                updateField('languages', ['all']);
                              } else {
                                const newLangs = formData.languages.filter(l => l !== 'all');
                                if (isSelected) {
                                  const filtered = newLangs.filter(l => l !== lang.value);
                                  updateField('languages', filtered.length === 0 ? ['all'] : filtered);
                                } else {
                                  updateField('languages', [...newLangs, lang.value]);
                                }
                              }
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              isSelected
                                ? 'bg-green-500/30 text-green-300 border border-green-500/50'
                                : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                            }`}
                          >
                            {lang.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  )}

                  {/* Location Targeting Header */}
                  <div className="flex items-center gap-2 mt-6 mb-3">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                      <MapPin className="h-3 w-3 text-white" />
                    </div>
                    <h3 className="font-medium text-white/90 text-sm">Location Targeting</h3>
                  </div>

                  <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/30">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-blue-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-300">Use the Globe</p>
                        <p className="text-xs text-blue-200/60 mt-1">
                          Search locations on the globe, then use +/- to adjust targeting radius.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs">Latitude</Label>
                      <Input
                        value={formData.latitude ? formData.latitude.toFixed(6) : ''}
                        readOnly
                        placeholder="From globe"
                        className="bg-white/5 border-white/10 text-white/80 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs">Longitude</Label>
                      <Input
                        value={formData.longitude ? formData.longitude.toFixed(6) : ''}
                        readOnly
                        placeholder="From globe"
                        className="bg-white/5 border-white/10 text-white/80 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs">Radius (km)</Label>
                      <Input
                        value={formData.radius ? formData.radius.toFixed(1) : ''}
                        readOnly
                        placeholder="Adjust on globe"
                        className="bg-white/5 border-white/10 text-white/80 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs">Location Name</Label>
                      <Input
                        value={formData.locationName}
                        onChange={(e) => updateField('locationName', e.target.value)}
                        placeholder="e.g., Lagos Central"
                        className="bg-white/5 border-white/10 text-white text-sm"
                      />
                    </div>
                  </div>

                  {formData.latitude && formData.longitude && (
                    <div className="p-3 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30">
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="h-4 w-4" />
                        <span className="font-medium text-sm">Location captured!</span>
                      </div>
                      <p className="text-xs text-green-200/60 mt-1">
                        {formData.radius.toFixed(1)} km radius around {formData.locationName || 'selected point'}
                      </p>
                    </div>
                  )}

                  {isSnapchat && (
                    <>
                      <SnapchatAudienceEstimate snap={snap} formData={formData} />
                      <SnapchatTargetingPanel snap={snap} value={formData.snapTargeting} onChange={(v) => updateField('snapTargeting', v)} />
                    </>
                  )}
                </div>
              )}

              {/* Budget Section */}
              {activeSection === 'budget' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-white" />
                    </div>
                    <h2 className="font-semibold text-white">
                      {formData.orderType === 'RESERVED' ? 'Domination Estimates' : 'Budget & Schedule'}
                    </h2>
                  </div>

                  {/* Ad Set Name - Common for both */}
                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm">Ad Set Name</Label>
                    <Input
                      placeholder="Auto-generated if empty"
                      value={formData.adSetName}
                      onChange={(e) => updateField('adSetName', e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>

                  {/* RESERVED Buying Type - Reservation Estimates */}
                  {formData.orderType === 'RESERVED' ? (
                    <>
                      {/* Target Reach */}
                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Target Reach</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            min="200000"
                            step="50000"
                            value={formData.reservationTargetReach}
                            onChange={(e) => updateField('reservationTargetReach', parseInt(e.target.value) || 200000)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-xs">people (min 200,000)</span>
                        </div>
                      </div>

                      {/* Frequency Control - Meta style */}
                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Frequency Control</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="99"
                            value={formData.reservationFrequencyTimes}
                            onChange={(e) => updateField('reservationFrequencyTimes', Math.min(99, Math.max(1, parseInt(e.target.value) || 1)))}
                            className="w-20 bg-white/5 border-white/10 text-white text-center"
                          />
                          <span className="text-white/60 text-sm">times per</span>
                          <Input
                            type="number"
                            min="1"
                            max="78"
                            value={formData.reservationFrequencyDays}
                            onChange={(e) => updateField('reservationFrequencyDays', Math.min(78, Math.max(1, parseInt(e.target.value) || 1)))}
                            className="w-20 bg-white/5 border-white/10 text-white text-center"
                          />
                          <span className="text-white/60 text-sm">day{formData.reservationFrequencyDays > 1 ? 's' : ''}</span>
                        </div>
                        <p className="text-xs text-white/40">As a maximum, we'll aim to stay under {formData.reservationFrequencyTimes} impression{formData.reservationFrequencyTimes > 1 ? 's' : ''} every {formData.reservationFrequencyDays} day{formData.reservationFrequencyDays > 1 ? 's' : ''}.</p>
                      </div>

                      {/* Campaign Schedule */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-white/60 text-xs">Start Date *</Label>
                          <Input
                            type="date"
                            min={new Date().toISOString().split('T')[0]}
                            value={formData.startDate ? formData.startDate.split('T')[0] : ''}
                            onChange={(e) => updateField('startDate', e.target.value)}
                            className="bg-white/5 border-white/10 text-white text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-white/60 text-xs">End Date *</Label>
                          <Input
                            type="date"
                            min={formData.startDate ? new Date(new Date(formData.startDate).getTime() + 86400000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                            value={formData.endDate ? formData.endDate.split('T')[0] : ''}
                            onChange={(e) => updateField('endDate', e.target.value)}
                            className="bg-white/5 border-white/10 text-white text-sm"
                          />
                        </div>
                      </div>

                      {/* Reservation Estimates Display */}
                      <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-white">Domination Estimates</h3>
                          <button
                            onClick={fetchReservationEstimates}
                            disabled={loadingReservation}
                            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                          >
                            {loadingReservation ? (
                              <span className="animate-spin">↻</span>
                            ) : (
                              '↻ Refresh'
                            )}
                          </button>
                        </div>
                        
                        {loadingReservation ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          </div>
                        ) : reservationEstimates ? (
                          <div className="space-y-2">
                            {/* Multi-Platform Pricing Info */}
                            {getActivePlatformsCount() > 1 && (
                              <div className="p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 mb-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-purple-300 text-xs font-semibold">🎯 Multi-Platform Pricing</span>
                                </div>
                                <p className="text-xs text-white/60 mt-1">
                                  Publishing to <span className="text-purple-300 font-semibold">{getActivePlatformsCount()} platforms</span>
                                </p>
                              </div>
                            )}
                            
                            <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                              <span className="text-white/60 text-sm">
                                {getActivePlatformsCount() > 1 ? `Total Budget (${getActivePlatformsCount()} Platforms)` : 'Budget'}
                              </span>
                              <span className="text-white font-semibold">
                                ₦{((reservationEstimates.budget_naira || 0) * getActivePlatformsCount()).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                              <span className="text-white/60 text-sm">Estimated audience size</span>
                              <span className="text-white text-sm">
                                {(reservationEstimates.estimated_audience_size_lower / 1000000).toFixed(2)}M - {(reservationEstimates.estimated_audience_size_upper / 1000000).toFixed(2)}M
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                              <span className="text-white/60 text-sm">
                                {getActivePlatformsCount() > 1 ? `Total CPM (${getActivePlatformsCount()} Platforms)` : 'CPM'}
                              </span>
                              <span className="text-white text-sm">
                                ₦{((reservationEstimates.cpm_naira || 0) * getActivePlatformsCount())?.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                              <span className="text-white/60 text-sm">Frequency</span>
                              <span className="text-white text-sm">{reservationEstimates.average_frequency}x per {reservationEstimates.frequency_cap_interval} days</span>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                              <span className="text-white/60 text-sm">Impressions</span>
                              <span className="text-white text-sm">{reservationEstimates.impressions?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                              <span className="text-white/60 text-sm">
                                {getActivePlatformsCount() > 1 ? `Total Daily Budget (${getActivePlatformsCount()} Platforms)` : 'Daily Budget'}
                              </span>
                              <span className="text-white text-sm">
                                ₦{((reservationEstimates.daily_budget || 0) * getActivePlatformsCount()).toLocaleString('en-NG', { minimumFractionDigits: 2 })}/day
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5">
                              <span className="text-white/60 text-sm">Campaign Period</span>
                              <span className="text-white text-sm">{reservationEstimates.campaign_duration_days} days</span>
                            </div>
                            
                            {/* Source indicator */}
                            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-white/10">
                              <div className={`w-2 h-2 rounded-full ${reservationEstimates.error ? 'bg-yellow-500' : 'bg-green-500'} animate-pulse`}></div>
                              <span className="text-xs text-white/40">
                                {reservationEstimates.error ? 'Calculated estimate' : reservationEstimates.prediction_id ? 'Live from Meta R&F Predictions' : 'Live from Meta Marketing API'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 text-white/40 text-sm">
                            Select a location and dates to see estimates
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    /* AUCTION Buying Type - Standard Budget */
                    <>
                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Budget Type</Label>
                        <div className="flex gap-3">
                          {['daily', 'lifetime'].map((type) => (
                            <label key={type} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="budgetType"
                                value={type}
                                checked={formData.budgetType === type}
                                onChange={(e) => updateField('budgetType', e.target.value)}
                                className="text-blue-500 focus:ring-blue-500"
                              />
                              <span className="text-sm text-white/70 capitalize">{type} Budget</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Budget Amount (NGN) *</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 font-medium text-sm">₦</span>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            placeholder="50000"
                            value={formData.budgetAmount}
                            onChange={(e) => updateField('budgetAmount', e.target.value)}
                            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-white/60 text-xs">Start Date *</Label>
                          <Input
                            type="datetime-local"
                            value={formData.startDate}
                            onChange={(e) => updateField('startDate', e.target.value)}
                            className="bg-white/5 border-white/10 text-white text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-white/60 text-xs">End Date</Label>
                          <Input
                            type="datetime-local"
                            value={formData.endDate}
                            onChange={(e) => updateField('endDate', e.target.value)}
                            className="bg-white/5 border-white/10 text-white text-sm"
                          />
                        </div>
                      </div>

                      {!isSnapchat && (
                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Bid Strategy</Label>
                        <Select value={formData.bidStrategy} onValueChange={(v) => updateField('bidStrategy', v)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-white/10">
                            <SelectItem value="LOWEST_COST_WITHOUT_CAP" className="text-white hover:bg-white/10">Lowest Cost</SelectItem>
                            <SelectItem value="LOWEST_COST_WITH_BID_CAP" className="text-white hover:bg-white/10">Lowest Cost with Cap</SelectItem>
                            <SelectItem value="COST_CAP" className="text-white hover:bg-white/10">Cost Cap</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      )}
                    </>
                  )}

                  {isSnapchat && (
                    <SnapchatDeliveryPanel value={formData.snapDelivery} onChange={(v) => updateField('snapDelivery', v)} currency={snap.selectedAccount?.currency} />
                  )}
                </div>
              )}

              {/* Creative Section */}
              {activeSection === 'creative' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                      <ImageIcon className="h-4 w-4 text-white" />
                    </div>
                    <h2 className="font-semibold text-white">Ad Creative</h2>
                  </div>

                  {/* Creative Source Toggle - New vs Existing Post */}
                  {!isSnapchat && (
                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm">Creative Source</Label>
                    <div className="flex gap-2 p-1 bg-white/5 rounded-lg">
                      <button
                        type="button"
                        onClick={() => {
                          setCreativeSource('existing');
                          setSelectedExistingPost(null);
                          // Fetch posts when selecting existing
                          if (formData.selectedPageId && pagePosts.length === 0) {
                            fetchPagePosts(formData.selectedPageId);
                          }
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                          creativeSource === 'existing' 
                            ? 'bg-green-500 text-white' 
                            : 'text-white/60 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <FileText className="h-4 w-4" />
                        Use Existing Post
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCreativeSource('new');
                          setSelectedExistingPost(null);
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                          creativeSource === 'new' 
                            ? 'bg-purple-500 text-white' 
                            : 'text-white/60 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <Plus className="h-4 w-4" />
                        Create New
                      </button>
                    </div>
                    {creativeSource === 'existing' && (
                      <p className="text-xs text-green-400/80 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Recommended: Works with apps in Development Mode
                      </p>
                    )}
                    {creativeSource === 'new' && (
                      <p className="text-xs text-amber-400/80 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Requires Meta App in Live Mode
                      </p>
                    )}
                  </div>
                  )}

                  {isSnapchat && (
                    <div className="p-3 rounded-lg bg-[#FFFC00]/10 border border-[#FFFC00]/30 text-xs text-yellow-100 space-y-1" data-testid="snapchat-creative-specs">
                      <p className="font-semibold text-yellow-200">Snapchat creative specs</p>
                      <p>Vertical 9:16 media only — 1080×1920 px. Images: PNG/JPG. Videos: MP4/MOV, 3–180 seconds. Max {SNAPCHAT_LIMITS.maxUploadMB}MB.</p>
                    </div>
                  )}

                  {/* EXISTING POST SELECTOR */}
                  {!isSnapchat && creativeSource === 'existing' && (
                    <div className="space-y-3">
                      <Label className="text-white/80 text-sm">Select a Page Post to Promote *</Label>
                      
                      {/* Load Posts Button */}
                      {pagePosts.length === 0 && !isLoadingPagePosts && (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full border-white/20 text-white hover:bg-white/10"
                          onClick={() => formData.selectedPageId && fetchPagePosts(formData.selectedPageId)}
                          disabled={!formData.selectedPageId}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          {formData.selectedPageId ? 'Load Page Posts' : 'Select a Page first'}
                        </Button>
                      )}
                      
                      {/* Loading */}
                      {isLoadingPagePosts && (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                          <span className="ml-2 text-white/60">Loading posts...</span>
                        </div>
                      )}
                      
                      {/* Posts Grid */}
                      {pagePosts.length > 0 && (
                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                          {pagePosts.map((post) => (
                            <div
                              key={post.id}
                              onClick={() => setSelectedExistingPost(post)}
                              className={`p-3 rounded-xl border cursor-pointer transition-all ${
                                selectedExistingPost?.id === post.id
                                  ? 'border-green-500 bg-green-500/10'
                                  : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'
                              }`}
                            >
                              <div className="flex gap-3">
                                {/* Post Thumbnail */}
                                {post.thumbnail ? (
                                  <img 
                                    src={post.thumbnail} 
                                    alt="" 
                                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText className="h-6 w-6 text-white/40" />
                                  </div>
                                )}
                                
                                {/* Post Content */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-white/90 line-clamp-2">
                                    {post.message || 'No text content'}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-white/50">
                                    <span className="flex items-center gap-1">
                                      <Heart className="h-3 w-3" /> {post.likes || 0}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <MessageCircle className="h-3 w-3" /> {post.comments || 0}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Share2 className="h-3 w-3" /> {post.shares || 0}
                                    </span>
                                  </div>
                                  <p className="text-xs text-white/40 mt-1">
                                    {post.created_time ? new Date(post.created_time).toLocaleDateString() : ''}
                                  </p>
                                </div>
                                
                                {/* Selection Indicator */}
                                {selectedExistingPost?.id === post.id && (
                                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Selected Post Preview */}
                      {selectedExistingPost && (
                        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
                          <div className="flex items-center gap-2 text-green-400 text-sm font-medium mb-2">
                            <CheckCircle className="h-4 w-4" />
                            Selected Post
                          </div>
                          <p className="text-white/80 text-sm line-clamp-2">
                            {selectedExistingPost.message || 'Photo/Video Post'}
                          </p>
                        </div>
                      )}
                      
                      {/* Refresh button if posts loaded */}
                      {pagePosts.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-white/60 hover:text-white"
                          onClick={() => formData.selectedPageId && fetchPagePosts(formData.selectedPageId)}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Refresh Posts
                        </Button>
                      )}
                    </div>
                  )}

                  {/* NEW CREATIVE FORM */}
                  {(creativeSource === 'new' || isSnapchat) && (
                    <>
                      {/* Media Type Toggle */}
                      <div className="flex gap-2 p-1 bg-white/5 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, mediaType: 'image' }))}
                          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                            formData.mediaType === 'image' 
                              ? 'bg-purple-500 text-white' 
                              : 'text-white/60 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          <ImageIcon className="h-4 w-4" />
                          Image
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, mediaType: 'video' }))}
                          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                            formData.mediaType === 'video' 
                              ? 'bg-purple-500 text-white' 
                              : 'text-white/60 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          <Video className="h-4 w-4" />
                          Video
                        </button>
                      </div>

                      {/* Image Upload */}
                      {formData.mediaType === 'image' && (
                        <div className="space-y-2">
                          <Label className="text-white/80 text-sm">Ad Image *</Label>
                          {!formData.imagePreview ? (
                            <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-purple-500/50 hover:bg-purple-500/5 transition-all">
                              <Upload className="h-8 w-8 text-white/40 mb-2" />
                              <span className="text-sm font-medium text-white/60">Click to upload image</span>
                              <span className="text-xs text-white/40 mt-1">{isSnapchat ? 'PNG, JPG • 1080×1920 (9:16) • up to 32MB' : 'PNG, JPG up to 30MB'}</span>
                              <input
                                type="file"
                                className="hidden"
                                accept="image/png,image/jpeg,image/jpg,image/heic,image/heif"
                                onChange={handleImageUpload}
                              />
                            </label>
                          ) : (
                            <div className="relative">
                              <img src={formData.imagePreview} alt="Ad preview" className="w-full h-36 object-cover rounded-xl border border-white/10" />
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute top-2 right-2 h-7 w-7"
                                onClick={removeMedia}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Video Upload */}
                      {formData.mediaType === 'video' && (
                        <div className="space-y-2">
                          <Label className="text-white/80 text-sm">Ad Video *</Label>
                          {!formData.videoPreview ? (
                            <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-purple-500/50 hover:bg-purple-500/5 transition-all">
                              <Video className="h-8 w-8 text-white/40 mb-2" />
                              <span className="text-sm font-medium text-white/60">Click to upload video</span>
                              <span className="text-xs text-white/40 mt-1">MP4, MOV, AVI up to 500MB</span>
                              <input
                                type="file"
                                className="hidden"
                                accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
                                onChange={handleVideoUpload}
                              />
                            </label>
                          ) : (
                            <div className="relative">
                              <video 
                                src={formData.videoPreview} 
                                className="w-full h-36 object-cover rounded-xl border border-white/10"
                                controls
                                muted
                              />
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute top-2 right-2 h-7 w-7"
                                onClick={removeMedia}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white">
                                {formData.videoFile?.name}
                              </div>
                            </div>
                          )}
                          <p className="text-xs text-white/40">
                            {isSnapchat ? 'Snapchat: 9:16 vertical (1080×1920), MP4/MOV, 3–180 seconds, max 32MB' : 'Recommended: 16:9 aspect ratio, 15-60 seconds for best performance'}
                          </p>
                        </div>
                      )}

                      {/* Text fields for new creative */}
                      {isSnapchat ? (
                        <div className="space-y-2">
                          <Label className="text-white/80 text-sm">Brand Name *</Label>
                          <Input
                            placeholder="Your brand as shown on the Snap"
                            value={formData.snapBrandName || ''}
                            maxLength={SNAPCHAT_LIMITS.brandName}
                            onChange={(e) => updateField('snapBrandName', e.target.value)}
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                            data-testid="snapchat-brand-name-input"
                          />
                          <p className="text-xs text-white/40">{(formData.snapBrandName || '').length}/{SNAPCHAT_LIMITS.brandName}</p>
                        </div>
                      ) : (
                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Primary Text *</Label>
                        <Textarea
                          placeholder="Main message of your ad..."
                          value={formData.primaryText}
                          onChange={(e) => updateField('primaryText', e.target.value)}
                          rows={2}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                        />
                        <p className="text-xs text-white/40">{formData.primaryText.length}/125 recommended</p>
                      </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Headline *</Label>
                        <Input
                          placeholder="Attention-grabbing headline"
                          value={formData.headline}
                          maxLength={isSnapchat ? SNAPCHAT_LIMITS.headline : undefined}
                          onChange={(e) => updateField('headline', e.target.value)}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                          data-testid="creative-headline-input"
                        />
                        {isSnapchat && <p className="text-xs text-white/40">{formData.headline.length}/{SNAPCHAT_LIMITS.headline}</p>}
                      </div>
                    </>
                  )}

                  {/* Shared fields: Description, Destination URL, CTA - shown for both */}
                  {!isSnapchat && (
                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm">Description</Label>
                    <Input
                      placeholder="Additional details"
                      value={formData.description}
                      onChange={(e) => updateField('description', e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm">Destination URL {isSnapchat ? '(optional — adds a swipe-up web view)' : '*'}</Label>
                    <div className="relative">
                      <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                      <Input
                        type="url"
                        placeholder="https://yourwebsite.com"
                        value={formData.destinationUrl}
                        onChange={(e) => updateField('destinationUrl', e.target.value)}
                        className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm">Call to Action</Label>
                    {isSnapchat ? (
                      <Select value={formData.snapCta || 'MORE'} onValueChange={(v) => updateField('snapCta', v)}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="snapchat-cta-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-white/10">
                          {(formData.objective === 'APP_PROMOTION' ? SNAPCHAT_APP_CTA_TYPES : SNAPCHAT_CTA_TYPES).map((cta) => (
                            <SelectItem key={cta.value} value={cta.value} className="text-white hover:bg-white/10">
                              {cta.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                    <Select value={formData.ctaType} onValueChange={(v) => updateField('ctaType', v)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-white/10">
                        {CTA_TYPES.map((cta) => (
                          <SelectItem key={cta.value} value={cta.value} className="text-white hover:bg-white/10">
                            {cta.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    )}
                  </div>

                  {isSnapchat && <SnapchatAdPreview formData={formData} />}
                  {(activePlatform === 'facebook' || activePlatform === 'instagram') && (
                    <MetaAdPreview
                      key={activePlatform}
                      platform={activePlatform}
                      formData={{ ...formData, creativeSource, existingPost: selectedExistingPost }}
                      ctaLabel={CTA_TYPES.find(c => c.value === formData.ctaType)?.label}
                    />
                  )}
                </div>
              )}

              {/* Settings Section */}
              {activeSection === 'settings' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center">
                      <Settings className="h-4 w-4 text-white" />
                    </div>
                    <h2 className="font-semibold text-white">Platform Settings</h2>
                  </div>

                  {/* Platform Toggles */}
                  <div className="space-y-3">
                    <Label className="text-white/80 text-sm font-medium">Enable Platforms</Label>
                    <p className="text-xs text-white/50 -mt-1">Choose which platforms to publish your campaigns to</p>
                    
                    <div className="space-y-2">
                      {AD_PLATFORMS.map((platform) => {
                        const isEnabled = platformSettings[platform.id]?.enabled;
                        const isConnected = (platform.id === 'facebook' || platform.id === 'instagram') 
                  ? isMetaConnected 
                  : (platform.id === 'google' || platform.id === 'youtube') 
                    ? googleAdsConnection?.connected 
                    : platform.id === 'snapchat'
                      ? snap.connection?.connected
                      : platformSettings[platform.id]?.connected;
                        
                        return (
                          <div 
                            key={platform.id}
                            className={`p-3 rounded-xl border transition-all ${
                              isEnabled 
                                ? `bg-gradient-to-r ${platform.bgColor} ${platform.borderColor}` 
                                : 'bg-white/5 border-white/10'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {/* Platform Icon */}
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: platform.id === 'facebook' ? '#1877F2' : platform.id === 'instagram' ? '#E4405F' : platform.id === 'twitter' ? '#000' : 'transparent' }}>
                                  {platform.icon === 'facebook' ? (
                                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                  ) : platform.icon === 'instagram' ? (
                                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                                    </svg>
                                  ) : platform.icon === 'twitter-x' ? (
                                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                  ) : (
                                    <img 
                                      src={platform.icon} 
                                      alt={platform.name}
                                      className="w-10 h-10 rounded-lg"
                                    />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-white font-medium">{platform.name}</span>
                                    {isConnected && (
                                      <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 text-xs">
                                        Connected
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-white/50">{platform.description}</span>
                                </div>
                              </div>
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={() => togglePlatformEnabled(platform.id)}
                                className="data-[state=checked]:bg-blue-600"
                              />
                            </div>
                            
                            {/* Account Connection */}
                            {isEnabled && (
                              <div className="mt-3 pt-3 border-t border-white/10">
                                {isConnected ? (
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {(platform.id === 'facebook' || platform.id === 'instagram') && connectedAccount ? (
                                        <>
                                          <img 
                                            src={connectedAccount.profilePicture}
                                            alt="Profile"
                                            className="w-6 h-6 rounded-full"
                                          />
                                          <span className="text-sm text-white/70">{connectedAccount.name}</span>
                                        </>
                                      ) : (platform.id === 'google' || platform.id === 'youtube') && googleAdsConnection?.connected ? (
                                        <>
                                          <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                                            <span className="text-[10px] font-bold text-white">G</span>
                                          </div>
                                          <span className="text-sm text-white/70">{googleAdsConnection.user_email || 'Connected'}</span>
                                        </>
                                      ) : (
                                        <span className="text-sm text-white/70">Account linked</span>
                                      )}
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        if (platform.id === 'facebook' || platform.id === 'instagram') {
                                          handleDisconnectMeta();
                                        } else if (platform.id === 'google' || platform.id === 'youtube') {
                                          handleDisconnectGoogle();
                                        }
                                      }}
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                                    >
                                      <Unlink className="h-3 w-3 mr-1" />
                                      Disconnect
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => connectPlatformAccount(platform.id)}
                                    className="w-full border-white/20 text-white/70 hover:bg-white/10 hover:text-white text-xs"
                                  >
                                    <LinkIcon className="h-3 w-3 mr-2" />
                                    Connect {platform.name} Account
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sync Settings */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 mt-6">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-400 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold text-blue-300">Cross-Platform Sync</h3>
                        <p className="text-xs text-blue-200/60 mt-1">
                          Campaign data you enter will automatically sync across all enabled platforms. 
                          Each platform may have slight variations in how the ad appears based on their specifications.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* API Status */}
                  <div className="space-y-2 mt-4">
                    <Label className="text-white/80 text-sm font-medium">API Integration Status</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {AD_PLATFORMS.map((platform) => (
                        <div 
                          key={platform.id}
                          className="p-2 rounded-lg bg-white/5 border border-white/10 flex items-center gap-2"
                        >
                          <div className={`w-2 h-2 rounded-full ${
                            (platform.id === 'facebook' || platform.id === 'instagram') ? 'bg-green-400' : 'bg-yellow-400'
                          }`} />
                          <span className="text-xs text-white/60">{platform.name}</span>
                          <span className={`text-xs ml-auto ${
                            (platform.id === 'facebook' || platform.id === 'instagram') ? 'text-green-400' : 'text-yellow-400'
                          }`}>
                            {(platform.id === 'facebook' || platform.id === 'instagram') ? 'Live' : 'Coming Soon'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-white/10 p-4 bg-black/30">
              <div className="flex items-center justify-between gap-3">
                <Button variant="outline" onClick={() => navigate('/admin')} className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white">
                  Cancel
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={resetForm} className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white">
                    Reset
                  </Button>
                  <Button 
                    onClick={handleSubmit} 
                    disabled={isSubmitting || (
                      (activePlatform === 'facebook' || activePlatform === 'instagram') ? !connectedAccount?.accessToken :
                      (activePlatform === 'google' || activePlatform === 'youtube') ? !googleAdsConnection?.connected :
                      isSnapchat ? !(snap.connection?.connected && snap.selectedAccount) :
                      true
                    )}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/30 min-w-[220px]"
                    data-testid="adglobe-submit-btn"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{submissionProgress || 'Submitting...'}</span>
                      </div>
                    ) : (activePlatform === 'facebook' || activePlatform === 'instagram') && !connectedAccount?.accessToken ? (
                      <>
                        <Plug className="h-4 w-4 mr-2" />
                        Connect Meta First
                      </>
                    ) : (activePlatform === 'google' || activePlatform === 'youtube') && !googleAdsConnection?.connected ? (
                      <>
                        <Plug className="h-4 w-4 mr-2" />
                        Connect Google First
                      </>
                    ) : isSnapchat && !snap.connection?.connected ? (
                      <>
                        <Plug className="h-4 w-4 mr-2" />
                        Connect Snapchat First
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit to {getPlatformAdsManagerName(activePlatform).replace(' Manager', '')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {/* Connection status hint */}
              {(activePlatform === 'facebook' || activePlatform === 'instagram') && !connectedAccount?.accessToken && (
                <p className="text-center text-white/40 text-xs mt-2">
                  Connect your Meta account in Settings to submit ads
                </p>
              )}
              {(activePlatform === 'google' || activePlatform === 'youtube') && !googleAdsConnection?.connected && (
                <p className="text-center text-white/40 text-xs mt-2">
                  Connect your Google Ads account to submit ads
                </p>
              )}
              {isSnapchat && !snap.connection?.connected && (
                <p className="text-center text-white/40 text-xs mt-2" data-testid="snapchat-connect-hint">
                  Connect your Snapchat Ads account to submit ads
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Snapchat Submission Result Modal */}
      {snapSubmitResult && (
        <SnapchatSuccessModal
          result={snapSubmitResult}
          accountId={snap.selectedAccount?.id}
          isActivating={isActivatingSnap}
          onClose={() => setSnapSubmitResult(null)}
          onActivate={handleActivateSnapCampaign}
          onCreateAnother={() => { setSnapSubmitResult(null); resetForm(); }}
        />
      )}

      {/* Meta Login Modal */}
      {showMetaLoginModal && !isMetaConnected && (activePlatform === 'facebook' || activePlatform === 'instagram') && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-white/10 relative">
            {/* Close Button */}
            <button
              onClick={() => setShowMetaLoginModal(false)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all"
            >
              <X className="h-4 w-4" />
            </button>
            
            {/* Header */}
            <div className="relative p-6 pb-4">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-transparent to-purple-600/20" />
              <div className="relative flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">Connect to Meta</h2>
                <p className="text-white/60 text-sm mt-2">
                  Sign in to access your ad accounts, pages, and campaign data
                </p>
              </div>
            </div>

            {/* Login Options */}
            <div className="p-6 pt-2 space-y-3">
              {/* Facebook Login Button */}
              <button
                onClick={() => handleMetaLogin('facebook')}
                disabled={isConnecting}
                className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
              >
                {isConnecting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                )}
                {isConnecting ? 'Connecting...' : 'Continue with Facebook'}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 py-2">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-white/40 text-xs uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Meta Business Suite Button */}
              <button
                onClick={() => handleMetaLogin('business_suite')}
                disabled={isConnecting}
                className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/20"
              >
                {isConnecting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Building2 className="h-5 w-5" />
                )}
                {isConnecting ? 'Connecting...' : 'Meta Business Suite'}
              </button>

              {/* Ads Manager Button */}
              <button
                onClick={() => handleMetaLogin('ads_manager')}
                disabled={isConnecting}
                className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isConnecting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <BarChart3 className="h-5 w-5" />
                )}
                {isConnecting ? 'Connecting...' : 'Meta Ads Manager'}
              </button>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-start gap-3">
                  <Info className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/60 leading-relaxed">
                      By connecting, you authorize AdGlobe to access your Meta ad accounts, pages, and campaign data. 
                      You can disconnect anytime from settings.
                    </p>
                  </div>
                </div>
              </div>

              {/* Permissions Badge */}
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                <span className="px-2 py-1 rounded-full bg-blue-500/10 text-blue-300 text-xs border border-blue-500/20">
                  Pages Access
                </span>
                <span className="px-2 py-1 rounded-full bg-green-500/10 text-green-300 text-xs border border-green-500/20">
                  Ads Management
                </span>
                <span className="px-2 py-1 rounded-full bg-purple-500/10 text-purple-300 text-xs border border-purple-500/20">
                  Business Data
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Business Portfolio Selector Modal (for business_management permission demo) */}
      {showBusinessSelector && metaReviewMode && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Building2 className="h-7 w-7" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Select Business Portfolio</h2>
                  <p className="text-blue-100 text-sm">Choose a business to manage ads</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Permission Badge */}
              <div className="p-3 rounded-lg bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span className="text-xs text-green-300">business_management permission granted</span>
                </div>
              </div>
              
              {/* Data Source Indicator */}
              {businesses.length > 0 && businesses !== simulatedBusinesses ? (
                <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-xs text-blue-300">Live data from Meta Business API</span>
                  </div>
                </div>
              ) : (
                <div className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <span className="text-xs text-yellow-300">Demo data (real businesses not available)</span>
                  </div>
                </div>
              )}
              
              <p className="text-sm text-white/60">
                Select a Business Portfolio to access its Ad Accounts and manage campaigns:
              </p>
              
              {/* Loading State */}
              {isLoadingBusinesses ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                  <span className="ml-3 text-white/60">Fetching your Business Portfolios...</span>
                </div>
              ) : (
                /* Business List */
                <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                  {(businesses.length > 0 ? businesses : simulatedBusinesses).map((business) => (
                    <div 
                      key={business.id}
                      onClick={async () => {
                        setSelectedBusiness(business);
                        // Fetch ad accounts for this business if not already fetched
                        if (!business.ad_accounts && !businessAdAccounts[business.id] && connectedAccount?.accessToken) {
                          await fetchBusinessAdAccounts(business.id, connectedAccount.accessToken);
                        }
                      }}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        selectedBusiness?.id === business.id 
                          ? 'bg-blue-500/20 border-blue-500/50' 
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                            {business.profile_picture ? (
                              <img src={business.profile_picture} alt={business.name} className="w-10 h-10 rounded-lg" />
                            ) : (
                              <Building2 className="h-5 w-5 text-white" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-white">{business.name}</h3>
                            <p className="text-xs text-white/50">ID: {business.business_id || business.id}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            business.verification_status === 'verified' || business.verification_status === 'VERIFIED'
                              ? 'bg-green-500/20 text-green-300' 
                              : 'bg-yellow-500/20 text-yellow-300'
                          }`}>
                            {business.verification_status === 'verified' || business.verification_status === 'VERIFIED' ? '✓ Verified' : 'Pending'}
                          </span>
                          <p className="text-xs text-white/40 mt-1">{business.ad_account_count || business.ad_accounts?.length || 0} Ad Account(s)</p>
                        </div>
                      </div>
                      
                      {/* Show Ad Accounts preview when selected */}
                      {selectedBusiness?.id === business.id && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-xs text-white/50 mb-2">Available Ad Accounts:</p>
                          {isLoadingBusinessAdAccounts ? (
                            <div className="flex items-center gap-2 p-2">
                              <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                              <span className="text-xs text-white/50">Loading ad accounts...</span>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {(businessAdAccounts[business.id] || business.ad_accounts || []).map((account) => (
                                <div key={account.id} className="p-2 rounded-lg bg-white/5 flex items-center justify-between">
                                  <div>
                                    <p className="text-sm text-white">{account.name}</p>
                                    <p className="text-xs text-white/40">ID: {account.id}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-white/60">{account.currency}</p>
                                    <span className={`text-xs ${account.status === 'ACTIVE' ? 'text-green-400' : 'text-yellow-400'}`}>
                                      {account.status}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {(businessAdAccounts[business.id] || business.ad_accounts || []).length === 0 && (
                                <p className="text-xs text-white/40 p-2">No ad accounts found</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {businesses.length === 0 && simulatedBusinesses.length === 0 && (
                    <div className="text-center py-8 text-white/50">
                      <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>No Business Portfolios found</p>
                      <p className="text-xs mt-1">Make sure you have access to a Meta Business Portfolio</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Continue Button */}
              <Button
                onClick={async () => {
                  if (selectedBusiness) {
                    // Get ad accounts from fetched data or existing data
                    const accounts = businessAdAccounts[selectedBusiness.id] || selectedBusiness.ad_accounts || [];
                    
                    // Set ad accounts from selected business
                    setAdAccounts(accounts.map(acc => ({
                      id: acc.id,
                      name: acc.name,
                      currency: acc.currency,
                      status: acc.status,
                      timezone: acc.timezone,
                      amount_spent: acc.amount_spent,
                      balance: acc.balance,
                    })));
                    
                    // Fetch pages if we have an access token
                    if (connectedAccount?.accessToken) {
                      try {
                        const pagesResponse = await fetch(`https://graph.facebook.com/me/accounts?fields=id,name,category,followers_count,picture,access_token&access_token=${connectedAccount.accessToken}`);
                        const pagesData = await pagesResponse.json();
                        if (pagesData.data && pagesData.data.length > 0) {
                          setFacebookPages(pagesData.data.map(p => ({
                            id: p.id,
                            name: p.name,
                            category: p.category,
                            followers: p.followers_count || 0,
                            picture: p.picture?.data?.url || '',
                            access_token: p.access_token // Store page access token for fetching posts
                          })));
                        }
                      } catch (e) {
                        console.log('Could not fetch pages:', e);
                      }
                    }
                    
                    setShowBusinessSelector(false);
                    toast.success(`Connected to ${selectedBusiness.name}`, {
                      description: `${accounts.length} Ad Account(s) loaded from Business Portfolio`
                    });
                  }
                }}
                disabled={!selectedBusiness || isLoadingBusinessAdAccounts}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              >
                {isLoadingBusinessAdAccounts ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Loading...
                  </>
                ) : (
                  `Continue with ${selectedBusiness?.name || 'Selected Business'}`
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Google Ads Create Client Account Modal */}
      {showCreateAccountModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div 
            className="bg-slate-800 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-white/10"
            data-testid="create-client-account-modal"
          >
            {/* Header */}
            <div className="p-5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">Create Client Account</h2>
                    <p className="text-white/80 text-sm">Under your Manager account</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowCreateAccountModal(false);
                    setNewClientAccountName('');
                  }}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-5 space-y-4">
              <div className="space-y-2">
                <Label className="text-white/80 text-sm font-medium">Account Name *</Label>
                <Input
                  value={newClientAccountName}
                  onChange={(e) => setNewClientAccountName(e.target.value)}
                  placeholder="e.g., My Business Ads"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30"
                  data-testid="new-client-account-name-input"
                />
                <p className="text-xs text-white/40">
                  This name will help you identify this account in Google Ads
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs">Currency</Label>
                  <div className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-white/70 text-sm">
                    NGN (Nigerian Naira)
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs">Time Zone</Label>
                  <div className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-white/70 text-sm">
                    Africa/Lagos
                  </div>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-300">
                    A client account will be created under your Manager (MCC) account. 
                    You'll be able to run ad campaigns in this account.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-5 border-t border-white/10 flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateAccountModal(false);
                  setNewClientAccountName('');
                }}
                className="flex-1 border-white/20 text-white/70 hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={createGoogleAdsClientAccount}
                disabled={isCreatingClientAccount || !newClientAccountName.trim()}
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                data-testid="submit-create-client-account-btn"
              >
                {isCreatingClientAccount ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Building2 className="h-4 w-4 mr-2" />
                    Create Account
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Google Ads Link Existing Account Modal */}
      {showLinkAccountModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div 
            className="bg-slate-800 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-white/10"
            data-testid="link-account-modal"
          >
            {/* Header */}
            <div className="p-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <LinkIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">Link Existing Account</h2>
                    <p className="text-white/80 text-sm">Connect an account with spending history</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowLinkAccountModal(false);
                    setLinkAccountId('');
                  }}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-5 space-y-4">
              <div className="space-y-2">
                <Label className="text-white/80 text-sm font-medium">Google Ads Customer ID *</Label>
                <Input
                  value={linkAccountId}
                  onChange={(e) => {
                    // Format as XXX-XXX-XXXX while typing
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    if (val.length <= 3) {
                      setLinkAccountId(val);
                    } else if (val.length <= 6) {
                      setLinkAccountId(`${val.slice(0, 3)}-${val.slice(3)}`);
                    } else {
                      setLinkAccountId(`${val.slice(0, 3)}-${val.slice(3, 6)}-${val.slice(6)}`);
                    }
                  }}
                  placeholder="123-456-7890"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30 font-mono"
                  data-testid="link-account-id-input"
                />
                <p className="text-xs text-white/40">
                  Find this in Google Ads under Settings → Account Information
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-green-300 space-y-1">
                    <p className="font-medium">How it works:</p>
                    <ol className="list-decimal list-inside space-y-0.5 text-green-300/80">
                      <li>Enter the Customer ID of the account you want to link</li>
                      <li>We send a link invitation to that account</li>
                      <li>The account owner accepts in Google Ads Settings</li>
                      <li>The account appears in your list ready for campaigns</li>
                    </ol>
                  </div>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-yellow-300">
                    <span className="font-medium">Note:</span> The account must have $1,000+ spending history 
                    and not be managed by another MCC.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-5 border-t border-white/10 flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowLinkAccountModal(false);
                  setLinkAccountId('');
                }}
                className="flex-1 border-white/20 text-white/70 hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={linkExistingGoogleAdsAccount}
                disabled={isLinkingAccount || linkAccountId.replace(/\D/g, '').length !== 10}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                data-testid="submit-link-account-btn"
              >
                {isLinkingAccount ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Details Modal */}
      {showCampaignDetailsModal && selectedCampaignDetails && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10">
            {/* Modal Header */}
            <div className="p-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <BarChart3 className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">{selectedCampaignDetails.name}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        selectedCampaignDetails.status === 'ACTIVE' 
                          ? 'bg-green-500/30 text-green-200' 
                          : 'bg-yellow-500/30 text-yellow-200'
                      }`}>
                        {selectedCampaignDetails.status}
                      </span>
                      <span className="text-white/60 text-sm">{selectedCampaignDetails.objective}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowCampaignDetailsModal(false);
                    setSelectedCampaignDetails(null);
                  }}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-5 overflow-y-auto max-h-[60vh] space-y-5">
              {/* Budget & Spend Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                  <div className="text-green-400 text-xs mb-1 flex items-center gap-1">
                    <DollarSign className="h-3 w-3" /> Daily Budget
                  </div>
                  <div className="text-xl font-bold text-white">
                    ₦{(selectedCampaignDetails.daily_budget || 0).toLocaleString()}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
                  <div className="text-blue-400 text-xs mb-1 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Total Spent
                  </div>
                  <div className="text-xl font-bold text-white">
                    ₦{(selectedCampaignDetails.spent || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                  <div className="text-purple-400 text-xs mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Created
                  </div>
                  <div className="text-sm font-medium text-white">
                    {selectedCampaignDetails.created_at || 'N/A'}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                  <div className="text-cyan-400 text-xs mb-1 flex items-center gap-1">
                    <Target className="h-3 w-3" /> Lifetime Budget
                  </div>
                  <div className="text-xl font-bold text-white">
                    {selectedCampaignDetails.lifetime_budget ? `₦${selectedCampaignDetails.lifetime_budget.toLocaleString()}` : 'Not set'}
                  </div>
                </div>
              </div>
              
              {/* Performance Metrics */}
              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-blue-400" />
                  Performance Metrics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-white/50 text-xs mb-1 flex items-center gap-1">
                      <Eye className="h-3 w-3" /> Impressions
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {(selectedCampaignDetails.impressions || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-white/50 text-xs mb-1 flex items-center gap-1">
                      <Users className="h-3 w-3" /> Reach
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {(selectedCampaignDetails.reach || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-white/50 text-xs mb-1 flex items-center gap-1">
                      <MousePointerClick className="h-3 w-3" /> Clicks
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {(selectedCampaignDetails.clicks || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-white/50 text-xs mb-1 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" /> CTR
                    </div>
                    <div className="text-2xl font-bold text-cyan-400">
                      {(selectedCampaignDetails.ctr || 0).toFixed(2)}%
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Engagement Metrics */}
              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Heart className="h-4 w-4 text-pink-400" />
                  Engagement Metrics
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/20">
                    <div className="text-pink-300/70 text-xs mb-1 flex items-center gap-1">
                      <Heart className="h-3 w-3" /> Likes
                    </div>
                    <div className="text-2xl font-bold text-pink-300">
                      {(selectedCampaignDetails.likes || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
                    <div className="text-blue-300/70 text-xs mb-1 flex items-center gap-1">
                      <MessageCircle className="h-3 w-3" /> Comments
                    </div>
                    <div className="text-2xl font-bold text-blue-300">
                      {(selectedCampaignDetails.comments || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                    <div className="text-green-300/70 text-xs mb-1 flex items-center gap-1">
                      <Share2 className="h-3 w-3" /> Shares
                    </div>
                    <div className="text-2xl font-bold text-green-300">
                      {(selectedCampaignDetails.shares || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Campaign ID */}
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-sm">Campaign ID</span>
                  <span className="text-white font-mono text-sm">{selectedCampaignDetails.id}</span>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-5 border-t border-white/10 flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCampaignDetailsModal(false);
                  setSelectedCampaignDetails(null);
                }}
                className="border-white/20 text-white/70 hover:bg-white/5"
              >
                Close
              </Button>
              <div className="flex gap-2">
                {connectedAccount?.accessToken && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingCampaignId(selectedCampaignDetails.id);
                        setEditingCampaignData({
                          name: selectedCampaignDetails.name,
                          daily_budget: selectedCampaignDetails.daily_budget,
                          status: selectedCampaignDetails.status
                        });
                        setShowCampaignDetailsModal(false);
                      }}
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Campaign
                    </Button>
                    <Button
                      onClick={async () => {
                        const newStatus = selectedCampaignDetails.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
                        const success = await updateCampaignStatus(
                          selectedCampaignDetails.id, 
                          newStatus, 
                          connectedAccount.accessToken
                        );
                        if (success) {
                          setSelectedCampaignDetails(prev => ({ ...prev, status: newStatus }));
                          setCampaigns(prev => prev.map(c => 
                            c.id === selectedCampaignDetails.id 
                              ? { ...c, status: newStatus }
                              : c
                          ));
                        }
                      }}
                      className={selectedCampaignDetails.status === 'ACTIVE' 
                        ? 'bg-yellow-600 hover:bg-yellow-700' 
                        : 'bg-green-600 hover:bg-green-700'
                      }
                    >
                      {selectedCampaignDetails.status === 'ACTIVE' ? (
                        <>
                          <Pause className="h-4 w-4 mr-2" />
                          Pause Campaign
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Activate Campaign
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <CampaignCreatedModal
          data={submittedData}
          formData={formData}
          onViewCampaign={() => {
            const newId = submittedData?.meta_results?.campaign_id || submittedData?.campaign?.id;
            setShowSuccessModal(false);
            setActiveSection('campaigns');
            setCampaignsImported(true);
            setHighlightCampaignId(newId);
            setTimeout(() => document.querySelector(`[data-testid="campaign-card-${newId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
          }}
        />
      )}

      {/* Engagement Metrics Modal */}
      {engagementModalPost && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setEngagementModalPost(null)}
        >
          <div 
            className="bg-slate-900 border border-white/20 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Post Engagement</h3>
              <button 
                onClick={() => setEngagementModalPost(null)}
                className="p-1 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Post Preview */}
            <div className="flex gap-3 mb-5 p-3 rounded-xl bg-white/5 border border-white/10">
              {engagementModalPost.thumbnail ? (
                <img 
                  src={engagementModalPost.thumbnail} 
                  alt="Post"
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-6 w-6 text-blue-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white/80 line-clamp-2">{engagementModalPost.message || '(No text content)'}</p>
                <p className="text-xs text-white/40 mt-1">
                  {new Date(engagementModalPost.created_time).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            {/* Engagement Metrics */}
            <p className="text-xs text-white/40 mb-2">Click a metric to see who engaged</p>
            <div className="space-y-3">
              {/* Likes/Reactions */}
              <button
                type="button"
                data-testid="engagement-likes-btn"
                onClick={() => setEngagementDetailKind('reactions')}
                className="w-full flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/30 hover:border-pink-400/60 hover:from-pink-500/20 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <Heart className="h-5 w-5 text-pink-400" />
                  </div>
                  <span className="text-white/80 font-medium">Likes</span>
                </div>
                <span className="text-2xl font-bold text-pink-400">{(engagementModalPost.likes || 0).toLocaleString()}</span>
              </button>
              
              {/* Comments */}
              <button
                type="button"
                data-testid="engagement-comments-btn"
                onClick={() => setEngagementDetailKind('comments')}
                className="w-full flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/30 hover:border-blue-400/60 hover:from-blue-500/20 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-blue-400" />
                  </div>
                  <span className="text-white/80 font-medium">Comments</span>
                </div>
                <span className="text-2xl font-bold text-blue-400">{(engagementModalPost.comments || 0).toLocaleString()}</span>
              </button>
              
              {/* Shares */}
              <button
                type="button"
                data-testid="engagement-shares-btn"
                onClick={() => setEngagementDetailKind('shares')}
                className="w-full flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 hover:border-green-400/60 hover:from-green-500/20 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Share2 className="h-5 w-5 text-green-400" />
                  </div>
                  <span className="text-white/80 font-medium">Shares</span>
                </div>
                <span className="text-2xl font-bold text-green-400">{(engagementModalPost.shares || 0).toLocaleString()}</span>
              </button>
            </div>
            
            {/* Total Engagement */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-white/60">Total Engagement</span>
                <span className="text-xl font-bold text-white">
                  {((engagementModalPost.likes || 0) + (engagementModalPost.comments || 0) + (engagementModalPost.shares || 0)).toLocaleString()}
                </span>
              </div>
            </div>
            
            {/* View on Facebook link */}
            {engagementModalPost.permalink_url && (
              <a 
                href={engagementModalPost.permalink_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
              >
                <Facebook className="h-4 w-4" />
                View on Facebook
              </a>
            )}
          </div>
        </div>
      )}

      {engagementModalPost && engagementDetailKind && (() => {
        const detailPage = facebookPages.find(p => p.id === formData.selectedPageId);
        return (
          <PostEngagementDetailModal
            post={engagementModalPost}
            page={detailPage}
            kind={engagementDetailKind}
            accessToken={detailPage?.access_token || connectedAccount?.accessToken}
            apiUrl={API_URL}
            onClose={() => setEngagementDetailKind(null)}
          />
        );
      })()}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 5px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
          background-clip: padding-box;
        }
      `}</style>
    </div>
  );
}
